import 'events'; // Needed for xmlbuilder2 to load correctly via Vite. See: https://github.com/oozcitak/xmlbuilder2/issues/216
import { create } from 'xmlbuilder2';
import type { TRITRIDataType } from '../../types/form-r.ts';
import type { ISO8601DateOnly } from '../../../cross-app-shared/types/common.ts';

export function formRDecimalToString(number: number): string {
  if (number < 1) {
    // According to Brandon Curtis on 2025-06-06, we need to return 2 significant figures, which is defined as the first
    // non-zero digit, followed by another digit, capped at 7 digits after the decimal place:
    const str = number.toFixed(7);
    let result = '0.';
    let sawNonZeroDigit = false;
    for (let i = 2; i < str.length; i++) {
      const digit = str[i];
      result += digit;
      if (sawNonZeroDigit) {
        break;
      } else if (digit !== '0') {
        sawNonZeroDigit = true;
      }
    }

    return result;
  } else if (number < 10) {
    return number.toFixed(1);
  } else {
    return number.toFixed(0);
  }
}

/**
 * Section 8 totals are expected to be direct sums of all the inputs that went into them, with the trailing zeroes
 * chopped off. Most of the inputs into the total number have already gone through formRDecimalToString.
 *
 * Unless you're dealing directly with Section 8 totals, you likely want formRDecimalToString instead of this function.
 */
export function formRTotalQuantityToString(number: number): string {
  return number.toFixed(7).replace(/0+$/, '').replace(/\.$/, '');
}

// This function is automatically generated by tri-xml-schema-process.ts and should not be edited directly.
export function triToXml(input: TRITRIDataType, forPreview: boolean): string {
  let builder = create({ version: '1.0', encoding: 'UTF-8' });
  if (forPreview) {
    builder.ins('xml-stylesheet', 'type="text/xsl" href="/assets/TRI_Stylesheet_Viewer_6_2.xsl"');
  }
  builder = builder.ele('TRI:TRI');
  if (forPreview) {
    builder.att('xmlns:TRI', 'http://www.exchangenetwork.net/schema/TRI/6.2');
    builder.att('xmlns:sc', 'urn:us:net:exchangenetwork:sc:1:0');
    builder.att('xmlns:xsi', 'http://www.w3.org/2001/XMLSchema-instance');
    builder.att('xsi:schemaLocation', 'http://www.exchangenetwork.net/schema/TRI/6.3 TRI_TRI_v6.3.xsd');
  } else {
    builder.att('xmlns:TRI', 'http://www.exchangenetwork.net/schema/TRI/6');
    builder.att('xmlns:sc', 'urn:us:net:exchangenetwork:sc:1:0');
  }
  {
    const TRITRI = input;
    for (const TRITRIItem of TRITRI.Submission) {
      builder = builder.ele('TRI:Submission');
      {
        const TRISubmission = TRITRIItem;
        if (TRISubmission.TRISubmissionIdentifier !== null) {
          builder = builder.ele('TRI:TRISubmissionIdentifier');
          {
            const TRITRISubmissionIdentifier = TRISubmission.TRISubmissionIdentifier;
            builder.txt(TRITRISubmissionIdentifier.value);
            if (TRITRISubmissionIdentifier.attrs.SubmissionIdentifierContext !== undefined) {
              builder.att('SubmissionIdentifierContext', TRITRISubmissionIdentifier.attrs.SubmissionIdentifierContext);
            }
          }
          builder = builder.up();
        }
        if (TRISubmission.Facility !== null) {
          builder = builder.ele('TRI:Facility');
          {
            const TRIFacility = TRISubmission.Facility;
            for (const TRIFacilityItem of TRIFacility.FacilityIdentifier) {
              builder = builder.ele('TRI:FacilityIdentifier');
              {
                const TRIFacilityIdentifier = TRIFacilityItem;
                builder.txt(TRIFacilityIdentifier.value);
                if (TRIFacilityIdentifier.attrs.FacilitySiteIdentifierContext !== undefined) {
                  builder.att(
                    'FacilitySiteIdentifierContext',
                    TRIFacilityIdentifier.attrs.FacilitySiteIdentifierContext,
                  );
                }
              }
              builder = builder.up();
            }
            if (TRIFacility.FacilityAccessDetails !== null) {
              builder = builder.ele('TRI:FacilityAccessDetails');
              {
                const TRIFacilityAccessDetails = TRIFacility.FacilityAccessDetails;
                if (TRIFacilityAccessDetails.choice === 'FacilityAccessCode') {
                  {
                    const chosen0 = TRIFacilityAccessDetails.value;
                    builder = builder.ele('TRI:FacilityAccessCode');
                    {
                      const TRIFacilityAccessCode = chosen0;
                      builder.txt(TRIFacilityAccessCode);
                    }
                    builder = builder.up();
                  }
                }
                if (TRIFacilityAccessDetails.choice === 'PriorYearTechnicalContactDetails') {
                  {
                    const chosen1 = TRIFacilityAccessDetails.value;
                    builder = builder.ele('TRI:PriorYearTechnicalContactDetails');
                    {
                      const TRIPriorYearTechnicalContactDetails = chosen1;
                      builder = builder.ele('TRI:PriorYearTechnicalContactNameText');
                      {
                        const TRIPriorYearTechnicalContactNameText =
                          TRIPriorYearTechnicalContactDetails.PriorYearTechnicalContactNameText;
                        builder.txt(TRIPriorYearTechnicalContactNameText);
                      }
                      builder = builder.up();
                      builder = builder.ele('TRI:PriorYearTechnicalContactTelephoneNumberText');
                      {
                        const TRIPriorYearTechnicalContactTelephoneNumberText =
                          TRIPriorYearTechnicalContactDetails.PriorYearTechnicalContactTelephoneNumberText;
                        builder.txt(TRIPriorYearTechnicalContactTelephoneNumberText);
                      }
                      builder = builder.up();
                    }
                    builder = builder.up();
                  }
                }
              }
              builder = builder.up();
            }
            if (TRIFacility.FacilitySiteName !== null) {
              builder = builder.ele('sc:FacilitySiteName');
              {
                const SCFacilitySiteName = TRIFacility.FacilitySiteName;
                builder.txt(SCFacilitySiteName);
              }
              builder = builder.up();
            }
            if (TRIFacility.LocationAddress !== null) {
              builder = builder.ele('sc:LocationAddress');
              {
                const SCLocationAddress = TRIFacility.LocationAddress;
                if (SCLocationAddress.LocationAddressText !== null) {
                  builder = builder.ele('sc:LocationAddressText');
                  {
                    const SCLocationAddressText = SCLocationAddress.LocationAddressText;
                    builder.txt(SCLocationAddressText);
                  }
                  builder = builder.up();
                }
                if (SCLocationAddress.SupplementalLocationText !== null) {
                  builder = builder.ele('sc:SupplementalLocationText');
                  {
                    const SCSupplementalLocationText = SCLocationAddress.SupplementalLocationText;
                    builder.txt(SCSupplementalLocationText);
                  }
                  builder = builder.up();
                }
                if (SCLocationAddress.LocalityName !== null) {
                  builder = builder.ele('sc:LocalityName');
                  {
                    const SCLocalityName = SCLocationAddress.LocalityName;
                    builder.txt(SCLocalityName);
                  }
                  builder = builder.up();
                }
                if (SCLocationAddress.StateIdentity !== null) {
                  builder = builder.ele('sc:StateIdentity');
                  {
                    const SCStateIdentity = SCLocationAddress.StateIdentity;
                    if (SCStateIdentity.StateCode !== null) {
                      builder = builder.ele('sc:StateCode');
                      {
                        const SCStateCode = SCStateIdentity.StateCode;
                        builder.txt(SCStateCode);
                      }
                      builder = builder.up();
                    }
                    if (SCStateIdentity.StateCodeListIdentifier !== null) {
                      builder = builder.ele('sc:StateCodeListIdentifier');
                      {
                        const SCStateCodeListIdentifier = SCStateIdentity.StateCodeListIdentifier;
                        builder.txt(SCStateCodeListIdentifier.value);
                        if (SCStateCodeListIdentifier.attrs.CodeListVersionIdentifier !== undefined) {
                          builder.att(
                            'CodeListVersionIdentifier',
                            SCStateCodeListIdentifier.attrs.CodeListVersionIdentifier,
                          );
                        }
                        if (SCStateCodeListIdentifier.attrs.CodeListVersionAgencyIdentifier !== undefined) {
                          builder.att(
                            'CodeListVersionAgencyIdentifier',
                            SCStateCodeListIdentifier.attrs.CodeListVersionAgencyIdentifier,
                          );
                        }
                      }
                      builder = builder.up();
                    }
                    if (SCStateIdentity.StateName !== null) {
                      builder = builder.ele('sc:StateName');
                      {
                        const SCStateName = SCStateIdentity.StateName;
                        builder.txt(SCStateName);
                      }
                      builder = builder.up();
                    }
                  }
                  builder = builder.up();
                }
                if (SCLocationAddress.AddressPostalCode !== null) {
                  builder = builder.ele('sc:AddressPostalCode');
                  {
                    const SCAddressPostalCode = SCLocationAddress.AddressPostalCode;
                    builder.txt(SCAddressPostalCode.value);
                    if (SCAddressPostalCode.attrs.AddressPostalCodeContext !== undefined) {
                      builder.att('AddressPostalCodeContext', SCAddressPostalCode.attrs.AddressPostalCodeContext);
                    }
                  }
                  builder = builder.up();
                }
                if (SCLocationAddress.CountryIdentity !== null) {
                  builder = builder.ele('sc:CountryIdentity');
                  {
                    const SCCountryIdentity = SCLocationAddress.CountryIdentity;
                    if (SCCountryIdentity.CountryCode !== null) {
                      builder = builder.ele('sc:CountryCode');
                      {
                        const SCCountryCode = SCCountryIdentity.CountryCode;
                        builder.txt(SCCountryCode);
                      }
                      builder = builder.up();
                    }
                    if (SCCountryIdentity.CountryCodeListIdentifier !== null) {
                      builder = builder.ele('sc:CountryCodeListIdentifier');
                      {
                        const SCCountryCodeListIdentifier = SCCountryIdentity.CountryCodeListIdentifier;
                        builder.txt(SCCountryCodeListIdentifier.value);
                        if (SCCountryCodeListIdentifier.attrs.CodeListVersionIdentifier !== undefined) {
                          builder.att(
                            'CodeListVersionIdentifier',
                            SCCountryCodeListIdentifier.attrs.CodeListVersionIdentifier,
                          );
                        }
                        if (SCCountryCodeListIdentifier.attrs.CodeListVersionAgencyIdentifier !== undefined) {
                          builder.att(
                            'CodeListVersionAgencyIdentifier',
                            SCCountryCodeListIdentifier.attrs.CodeListVersionAgencyIdentifier,
                          );
                        }
                      }
                      builder = builder.up();
                    }
                    if (SCCountryIdentity.CountryName !== null) {
                      builder = builder.ele('sc:CountryName');
                      {
                        const SCCountryName = SCCountryIdentity.CountryName;
                        builder.txt(SCCountryName);
                      }
                      builder = builder.up();
                    }
                  }
                  builder = builder.up();
                }
                if (SCLocationAddress.CountyIdentity !== null) {
                  builder = builder.ele('sc:CountyIdentity');
                  {
                    const SCCountyIdentity = SCLocationAddress.CountyIdentity;
                    if (SCCountyIdentity.CountyCode !== null) {
                      builder = builder.ele('sc:CountyCode');
                      {
                        const SCCountyCode = SCCountyIdentity.CountyCode;
                        builder.txt(SCCountyCode);
                      }
                      builder = builder.up();
                    }
                    if (SCCountyIdentity.CountyCodeListIdentifier !== null) {
                      builder = builder.ele('sc:CountyCodeListIdentifier');
                      {
                        const SCCountyCodeListIdentifier = SCCountyIdentity.CountyCodeListIdentifier;
                        builder.txt(SCCountyCodeListIdentifier.value);
                        if (SCCountyCodeListIdentifier.attrs.CodeListVersionIdentifier !== undefined) {
                          builder.att(
                            'CodeListVersionIdentifier',
                            SCCountyCodeListIdentifier.attrs.CodeListVersionIdentifier,
                          );
                        }
                        if (SCCountyCodeListIdentifier.attrs.CodeListVersionAgencyIdentifier !== undefined) {
                          builder.att(
                            'CodeListVersionAgencyIdentifier',
                            SCCountyCodeListIdentifier.attrs.CodeListVersionAgencyIdentifier,
                          );
                        }
                      }
                      builder = builder.up();
                    }
                    if (SCCountyIdentity.CountyName !== null) {
                      builder = builder.ele('sc:CountyName');
                      {
                        const SCCountyName = SCCountyIdentity.CountyName;
                        builder.txt(SCCountyName);
                      }
                      builder = builder.up();
                    }
                  }
                  builder = builder.up();
                }
                if (SCLocationAddress.TribalIdentity !== null) {
                  builder = builder.ele('sc:TribalIdentity');
                  {
                    const SCTribalIdentity = SCLocationAddress.TribalIdentity;
                    if (SCTribalIdentity.TribalCode !== null) {
                      builder = builder.ele('sc:TribalCode');
                      {
                        const SCTribalCode = SCTribalIdentity.TribalCode;
                        builder.txt(SCTribalCode);
                      }
                      builder = builder.up();
                    }
                    if (SCTribalIdentity.TribalCodeListIdentifier !== null) {
                      builder = builder.ele('sc:TribalCodeListIdentifier');
                      {
                        const SCTribalCodeListIdentifier = SCTribalIdentity.TribalCodeListIdentifier;
                        builder.txt(SCTribalCodeListIdentifier.value);
                        if (SCTribalCodeListIdentifier.attrs.CodeListVersionIdentifier !== undefined) {
                          builder.att(
                            'CodeListVersionIdentifier',
                            SCTribalCodeListIdentifier.attrs.CodeListVersionIdentifier,
                          );
                        }
                        if (SCTribalCodeListIdentifier.attrs.CodeListVersionAgencyIdentifier !== undefined) {
                          builder.att(
                            'CodeListVersionAgencyIdentifier',
                            SCTribalCodeListIdentifier.attrs.CodeListVersionAgencyIdentifier,
                          );
                        }
                      }
                      builder = builder.up();
                    }
                    if (SCTribalIdentity.TribalName !== null) {
                      builder = builder.ele('sc:TribalName');
                      {
                        const SCTribalName = SCTribalIdentity.TribalName;
                        builder.txt(SCTribalName);
                      }
                      builder = builder.up();
                    }
                  }
                  builder = builder.up();
                }
                if (SCLocationAddress.TribalLandName !== null) {
                  builder = builder.ele('sc:TribalLandName');
                  {
                    const SCTribalLandName = SCLocationAddress.TribalLandName;
                    builder.txt(SCTribalLandName);
                  }
                  builder = builder.up();
                }
                if (SCLocationAddress.TribalLandIndicator !== null) {
                  builder = builder.ele('sc:TribalLandIndicator');
                  {
                    const SCTribalLandIndicator = SCLocationAddress.TribalLandIndicator;
                    builder.txt(SCTribalLandIndicator);
                  }
                  builder = builder.up();
                }
                if (SCLocationAddress.LocationDescriptionText !== null) {
                  builder = builder.ele('sc:LocationDescriptionText');
                  {
                    const SCLocationDescriptionText = SCLocationAddress.LocationDescriptionText;
                    builder.txt(SCLocationDescriptionText);
                  }
                  builder = builder.up();
                }
              }
              builder = builder.up();
            }
            if (TRIFacility.MailingFacilitySiteName !== null) {
              builder = builder.ele('TRI:MailingFacilitySiteName');
              {
                const TRIMailingFacilitySiteName = TRIFacility.MailingFacilitySiteName;
                builder.txt(TRIMailingFacilitySiteName);
              }
              builder = builder.up();
            }
            if (TRIFacility.MailingAddress !== null) {
              builder = builder.ele('TRI:MailingAddress');
              {
                const TRIMailingAddress = TRIFacility.MailingAddress;
                if (TRIMailingAddress.MailingAddressText !== null) {
                  builder = builder.ele('sc:MailingAddressText');
                  {
                    const SCMailingAddressText = TRIMailingAddress.MailingAddressText;
                    builder.txt(SCMailingAddressText);
                  }
                  builder = builder.up();
                }
                if (TRIMailingAddress.SupplementalAddressText !== null) {
                  builder = builder.ele('sc:SupplementalAddressText');
                  {
                    const SCSupplementalAddressText = TRIMailingAddress.SupplementalAddressText;
                    builder.txt(SCSupplementalAddressText);
                  }
                  builder = builder.up();
                }
                if (TRIMailingAddress.MailingAddressCityName !== null) {
                  builder = builder.ele('sc:MailingAddressCityName');
                  {
                    const SCMailingAddressCityName = TRIMailingAddress.MailingAddressCityName;
                    builder.txt(SCMailingAddressCityName);
                  }
                  builder = builder.up();
                }
                if (TRIMailingAddress.StateIdentity !== null) {
                  builder = builder.ele('sc:StateIdentity');
                  {
                    const SCStateIdentity = TRIMailingAddress.StateIdentity;
                    if (SCStateIdentity.StateCode !== null) {
                      builder = builder.ele('sc:StateCode');
                      {
                        const SCStateCode = SCStateIdentity.StateCode;
                        builder.txt(SCStateCode);
                      }
                      builder = builder.up();
                    }
                    if (SCStateIdentity.StateCodeListIdentifier !== null) {
                      builder = builder.ele('sc:StateCodeListIdentifier');
                      {
                        const SCStateCodeListIdentifier = SCStateIdentity.StateCodeListIdentifier;
                        builder.txt(SCStateCodeListIdentifier.value);
                        if (SCStateCodeListIdentifier.attrs.CodeListVersionIdentifier !== undefined) {
                          builder.att(
                            'CodeListVersionIdentifier',
                            SCStateCodeListIdentifier.attrs.CodeListVersionIdentifier,
                          );
                        }
                        if (SCStateCodeListIdentifier.attrs.CodeListVersionAgencyIdentifier !== undefined) {
                          builder.att(
                            'CodeListVersionAgencyIdentifier',
                            SCStateCodeListIdentifier.attrs.CodeListVersionAgencyIdentifier,
                          );
                        }
                      }
                      builder = builder.up();
                    }
                    if (SCStateIdentity.StateName !== null) {
                      builder = builder.ele('sc:StateName');
                      {
                        const SCStateName = SCStateIdentity.StateName;
                        builder.txt(SCStateName);
                      }
                      builder = builder.up();
                    }
                  }
                  builder = builder.up();
                }
                if (TRIMailingAddress.AddressPostalCode !== null) {
                  builder = builder.ele('sc:AddressPostalCode');
                  {
                    const SCAddressPostalCode = TRIMailingAddress.AddressPostalCode;
                    builder.txt(SCAddressPostalCode.value);
                    if (SCAddressPostalCode.attrs.AddressPostalCodeContext !== undefined) {
                      builder.att('AddressPostalCodeContext', SCAddressPostalCode.attrs.AddressPostalCodeContext);
                    }
                  }
                  builder = builder.up();
                }
                if (TRIMailingAddress.CountryIdentity !== null) {
                  builder = builder.ele('sc:CountryIdentity');
                  {
                    const SCCountryIdentity = TRIMailingAddress.CountryIdentity;
                    if (SCCountryIdentity.CountryCode !== null) {
                      builder = builder.ele('sc:CountryCode');
                      {
                        const SCCountryCode = SCCountryIdentity.CountryCode;
                        builder.txt(SCCountryCode);
                      }
                      builder = builder.up();
                    }
                    if (SCCountryIdentity.CountryCodeListIdentifier !== null) {
                      builder = builder.ele('sc:CountryCodeListIdentifier');
                      {
                        const SCCountryCodeListIdentifier = SCCountryIdentity.CountryCodeListIdentifier;
                        builder.txt(SCCountryCodeListIdentifier.value);
                        if (SCCountryCodeListIdentifier.attrs.CodeListVersionIdentifier !== undefined) {
                          builder.att(
                            'CodeListVersionIdentifier',
                            SCCountryCodeListIdentifier.attrs.CodeListVersionIdentifier,
                          );
                        }
                        if (SCCountryCodeListIdentifier.attrs.CodeListVersionAgencyIdentifier !== undefined) {
                          builder.att(
                            'CodeListVersionAgencyIdentifier',
                            SCCountryCodeListIdentifier.attrs.CodeListVersionAgencyIdentifier,
                          );
                        }
                      }
                      builder = builder.up();
                    }
                    if (SCCountryIdentity.CountryName !== null) {
                      builder = builder.ele('sc:CountryName');
                      {
                        const SCCountryName = SCCountryIdentity.CountryName;
                        builder.txt(SCCountryName);
                      }
                      builder = builder.up();
                    }
                  }
                  builder = builder.up();
                }
                if (TRIMailingAddress.ProvinceNameText !== null) {
                  builder = builder.ele('TRI:ProvinceNameText');
                  {
                    const TRIProvinceNameText = TRIMailingAddress.ProvinceNameText;
                    builder.txt(TRIProvinceNameText);
                  }
                  builder = builder.up();
                }
              }
              builder = builder.up();
            }
            for (const TRIFacilityItem of TRIFacility.FacilitySIC) {
              builder = builder.ele('TRI:FacilitySIC');
              {
                const TRIFacilitySIC = TRIFacilityItem;
                builder = builder.ele('sc:SICCode');
                {
                  const SCSICCode = TRIFacilitySIC.SICCode;
                  builder.txt(SCSICCode);
                }
                builder = builder.up();
                builder = builder.ele('sc:SICPrimaryIndicator');
                {
                  const SCSICPrimaryIndicator = TRIFacilitySIC.SICPrimaryIndicator;
                  builder.txt(SCSICPrimaryIndicator);
                }
                builder = builder.up();
              }
              builder = builder.up();
            }
            for (const TRIFacilityItem of TRIFacility.FacilityNAICS) {
              builder = builder.ele('TRI:FacilityNAICS');
              {
                const TRIFacilityNAICS = TRIFacilityItem;
                builder = builder.ele('sc:NAICSCode');
                {
                  const SCNAICSCode = TRIFacilityNAICS.NAICSCode;
                  builder.txt(SCNAICSCode);
                }
                builder = builder.up();
                builder = builder.ele('sc:NAICSPrimaryIndicator');
                {
                  const SCNAICSPrimaryIndicator = TRIFacilityNAICS.NAICSPrimaryIndicator;
                  builder.txt(SCNAICSPrimaryIndicator);
                }
                builder = builder.up();
              }
              builder = builder.up();
            }
            if (TRIFacility.GeographicLocationDescription !== null) {
              builder = builder.ele('TRI:GeographicLocationDescription');
              {
                const TRIGeographicLocationDescription = TRIFacility.GeographicLocationDescription;
                if (TRIGeographicLocationDescription.LatitudeMeasure !== null) {
                  builder = builder.ele('sc:LatitudeMeasure');
                  {
                    const SCLatitudeMeasure = TRIGeographicLocationDescription.LatitudeMeasure;
                    builder.txt(SCLatitudeMeasure);
                  }
                  builder = builder.up();
                }
                if (TRIGeographicLocationDescription.LongitudeMeasure !== null) {
                  builder = builder.ele('sc:LongitudeMeasure');
                  {
                    const SCLongitudeMeasure = TRIGeographicLocationDescription.LongitudeMeasure;
                    builder.txt(SCLongitudeMeasure);
                  }
                  builder = builder.up();
                }
                if (TRIGeographicLocationDescription.SourceMapScaleNumber !== null) {
                  builder = builder.ele('sc:SourceMapScaleNumber');
                  {
                    const SCSourceMapScaleNumber = TRIGeographicLocationDescription.SourceMapScaleNumber;
                    builder.txt(String(SCSourceMapScaleNumber satisfies number));
                  }
                  builder = builder.up();
                }
                if (TRIGeographicLocationDescription.HorizontalAccuracyMeasure !== null) {
                  builder = builder.ele('sc:HorizontalAccuracyMeasure');
                  {
                    const SCHorizontalAccuracyMeasure = TRIGeographicLocationDescription.HorizontalAccuracyMeasure;
                    if (SCHorizontalAccuracyMeasure.MeasureValue !== null) {
                      builder = builder.ele('sc:MeasureValue');
                      {
                        const SCMeasureValue = SCHorizontalAccuracyMeasure.MeasureValue;
                        builder.txt(SCMeasureValue);
                      }
                      builder = builder.up();
                    }
                    if (SCHorizontalAccuracyMeasure.MeasureUnit !== null) {
                      builder = builder.ele('sc:MeasureUnit');
                      {
                        const SCMeasureUnit = SCHorizontalAccuracyMeasure.MeasureUnit;
                        if (SCMeasureUnit.MeasureUnitCode !== null) {
                          builder = builder.ele('sc:MeasureUnitCode');
                          {
                            const SCMeasureUnitCode = SCMeasureUnit.MeasureUnitCode;
                            builder.txt(SCMeasureUnitCode);
                          }
                          builder = builder.up();
                        }
                        if (SCMeasureUnit.MeasureUnitCodeListIdentifier !== null) {
                          builder = builder.ele('sc:MeasureUnitCodeListIdentifier');
                          {
                            const SCMeasureUnitCodeListIdentifier = SCMeasureUnit.MeasureUnitCodeListIdentifier;
                            builder.txt(SCMeasureUnitCodeListIdentifier.value);
                            if (SCMeasureUnitCodeListIdentifier.attrs.CodeListVersionIdentifier !== undefined) {
                              builder.att(
                                'CodeListVersionIdentifier',
                                SCMeasureUnitCodeListIdentifier.attrs.CodeListVersionIdentifier,
                              );
                            }
                            if (SCMeasureUnitCodeListIdentifier.attrs.CodeListVersionAgencyIdentifier !== undefined) {
                              builder.att(
                                'CodeListVersionAgencyIdentifier',
                                SCMeasureUnitCodeListIdentifier.attrs.CodeListVersionAgencyIdentifier,
                              );
                            }
                          }
                          builder = builder.up();
                        }
                        if (SCMeasureUnit.MeasureUnitName !== null) {
                          builder = builder.ele('sc:MeasureUnitName');
                          {
                            const SCMeasureUnitName = SCMeasureUnit.MeasureUnitName;
                            builder.txt(SCMeasureUnitName);
                          }
                          builder = builder.up();
                        }
                      }
                      builder = builder.up();
                    }
                    if (SCHorizontalAccuracyMeasure.MeasurePrecisionText !== null) {
                      builder = builder.ele('sc:MeasurePrecisionText');
                      {
                        const SCMeasurePrecisionText = SCHorizontalAccuracyMeasure.MeasurePrecisionText;
                        builder.txt(SCMeasurePrecisionText);
                      }
                      builder = builder.up();
                    }
                    if (SCHorizontalAccuracyMeasure.ResultQualifier !== null) {
                      builder = builder.ele('sc:ResultQualifier');
                      {
                        const SCResultQualifier = SCHorizontalAccuracyMeasure.ResultQualifier;
                        if (SCResultQualifier.ResultQualifierCode !== null) {
                          builder = builder.ele('sc:ResultQualifierCode');
                          {
                            const SCResultQualifierCode = SCResultQualifier.ResultQualifierCode;
                            builder.txt(SCResultQualifierCode);
                          }
                          builder = builder.up();
                        }
                        if (SCResultQualifier.ResultQualifierCodeListIdentifier !== null) {
                          builder = builder.ele('sc:ResultQualifierCodeListIdentifier');
                          {
                            const SCResultQualifierCodeListIdentifier =
                              SCResultQualifier.ResultQualifierCodeListIdentifier;
                            builder.txt(SCResultQualifierCodeListIdentifier.value);
                            if (SCResultQualifierCodeListIdentifier.attrs.CodeListVersionIdentifier !== undefined) {
                              builder.att(
                                'CodeListVersionIdentifier',
                                SCResultQualifierCodeListIdentifier.attrs.CodeListVersionIdentifier,
                              );
                            }
                            if (
                              SCResultQualifierCodeListIdentifier.attrs.CodeListVersionAgencyIdentifier !== undefined
                            ) {
                              builder.att(
                                'CodeListVersionAgencyIdentifier',
                                SCResultQualifierCodeListIdentifier.attrs.CodeListVersionAgencyIdentifier,
                              );
                            }
                          }
                          builder = builder.up();
                        }
                        if (SCResultQualifier.ResultQualifierName !== null) {
                          builder = builder.ele('sc:ResultQualifierName');
                          {
                            const SCResultQualifierName = SCResultQualifier.ResultQualifierName;
                            builder.txt(SCResultQualifierName);
                          }
                          builder = builder.up();
                        }
                      }
                      builder = builder.up();
                    }
                  }
                  builder = builder.up();
                }
                if (TRIGeographicLocationDescription.HorizontalCollectionMethod !== null) {
                  builder = builder.ele('sc:HorizontalCollectionMethod');
                  {
                    const SCHorizontalCollectionMethod = TRIGeographicLocationDescription.HorizontalCollectionMethod;
                    if (SCHorizontalCollectionMethod.MethodIdentifierCode !== null) {
                      builder = builder.ele('sc:MethodIdentifierCode');
                      {
                        const SCMethodIdentifierCode = SCHorizontalCollectionMethod.MethodIdentifierCode;
                        builder.txt(SCMethodIdentifierCode);
                      }
                      builder = builder.up();
                    }
                    if (SCHorizontalCollectionMethod.MethodIdentifierCodeListIdentifier !== null) {
                      builder = builder.ele('sc:MethodIdentifierCodeListIdentifier');
                      {
                        const SCMethodIdentifierCodeListIdentifier =
                          SCHorizontalCollectionMethod.MethodIdentifierCodeListIdentifier;
                        builder.txt(SCMethodIdentifierCodeListIdentifier.value);
                        if (SCMethodIdentifierCodeListIdentifier.attrs.CodeListVersionIdentifier !== undefined) {
                          builder.att(
                            'CodeListVersionIdentifier',
                            SCMethodIdentifierCodeListIdentifier.attrs.CodeListVersionIdentifier,
                          );
                        }
                        if (SCMethodIdentifierCodeListIdentifier.attrs.CodeListVersionAgencyIdentifier !== undefined) {
                          builder.att(
                            'CodeListVersionAgencyIdentifier',
                            SCMethodIdentifierCodeListIdentifier.attrs.CodeListVersionAgencyIdentifier,
                          );
                        }
                      }
                      builder = builder.up();
                    }
                    if (SCHorizontalCollectionMethod.MethodName !== null) {
                      builder = builder.ele('sc:MethodName');
                      {
                        const SCMethodName = SCHorizontalCollectionMethod.MethodName;
                        builder.txt(SCMethodName);
                      }
                      builder = builder.up();
                    }
                    if (SCHorizontalCollectionMethod.MethodDescriptionText !== null) {
                      builder = builder.ele('sc:MethodDescriptionText');
                      {
                        const SCMethodDescriptionText = SCHorizontalCollectionMethod.MethodDescriptionText;
                        builder.txt(SCMethodDescriptionText);
                      }
                      builder = builder.up();
                    }
                    if (SCHorizontalCollectionMethod.MethodDeviationsText !== null) {
                      builder = builder.ele('sc:MethodDeviationsText');
                      {
                        const SCMethodDeviationsText = SCHorizontalCollectionMethod.MethodDeviationsText;
                        builder.txt(SCMethodDeviationsText);
                      }
                      builder = builder.up();
                    }
                  }
                  builder = builder.up();
                }
                if (TRIGeographicLocationDescription.GeographicReferencePoint !== null) {
                  builder = builder.ele('sc:GeographicReferencePoint');
                  {
                    const SCGeographicReferencePoint = TRIGeographicLocationDescription.GeographicReferencePoint;
                    if (SCGeographicReferencePoint.GeographicReferencePointCode !== null) {
                      builder = builder.ele('sc:GeographicReferencePointCode');
                      {
                        const SCGeographicReferencePointCode = SCGeographicReferencePoint.GeographicReferencePointCode;
                        builder.txt(SCGeographicReferencePointCode);
                      }
                      builder = builder.up();
                    }
                    if (SCGeographicReferencePoint.ReferencePointCodeListIdentifier !== null) {
                      builder = builder.ele('sc:ReferencePointCodeListIdentifier');
                      {
                        const SCReferencePointCodeListIdentifier =
                          SCGeographicReferencePoint.ReferencePointCodeListIdentifier;
                        builder.txt(SCReferencePointCodeListIdentifier.value);
                        if (SCReferencePointCodeListIdentifier.attrs.CodeListVersionIdentifier !== undefined) {
                          builder.att(
                            'CodeListVersionIdentifier',
                            SCReferencePointCodeListIdentifier.attrs.CodeListVersionIdentifier,
                          );
                        }
                        if (SCReferencePointCodeListIdentifier.attrs.CodeListVersionAgencyIdentifier !== undefined) {
                          builder.att(
                            'CodeListVersionAgencyIdentifier',
                            SCReferencePointCodeListIdentifier.attrs.CodeListVersionAgencyIdentifier,
                          );
                        }
                      }
                      builder = builder.up();
                    }
                    if (SCGeographicReferencePoint.GeographicReferencePointName !== null) {
                      builder = builder.ele('sc:GeographicReferencePointName');
                      {
                        const SCGeographicReferencePointName = SCGeographicReferencePoint.GeographicReferencePointName;
                        builder.txt(SCGeographicReferencePointName);
                      }
                      builder = builder.up();
                    }
                  }
                  builder = builder.up();
                }
                if (TRIGeographicLocationDescription.HorizontalReferenceDatum !== null) {
                  builder = builder.ele('sc:HorizontalReferenceDatum');
                  {
                    const SCHorizontalReferenceDatum = TRIGeographicLocationDescription.HorizontalReferenceDatum;
                    if (SCHorizontalReferenceDatum.GeographicReferenceDatumCode !== null) {
                      builder = builder.ele('sc:GeographicReferenceDatumCode');
                      {
                        const SCGeographicReferenceDatumCode = SCHorizontalReferenceDatum.GeographicReferenceDatumCode;
                        builder.txt(SCGeographicReferenceDatumCode);
                      }
                      builder = builder.up();
                    }
                    if (SCHorizontalReferenceDatum.GeographicReferenceDatumCodeListIdentifier !== null) {
                      builder = builder.ele('sc:GeographicReferenceDatumCodeListIdentifier');
                      {
                        const SCGeographicReferenceDatumCodeListIdentifier =
                          SCHorizontalReferenceDatum.GeographicReferenceDatumCodeListIdentifier;
                        builder.txt(SCGeographicReferenceDatumCodeListIdentifier.value);
                        if (
                          SCGeographicReferenceDatumCodeListIdentifier.attrs.CodeListVersionIdentifier !== undefined
                        ) {
                          builder.att(
                            'CodeListVersionIdentifier',
                            SCGeographicReferenceDatumCodeListIdentifier.attrs.CodeListVersionIdentifier,
                          );
                        }
                        if (
                          SCGeographicReferenceDatumCodeListIdentifier.attrs.CodeListVersionAgencyIdentifier !==
                          undefined
                        ) {
                          builder.att(
                            'CodeListVersionAgencyIdentifier',
                            SCGeographicReferenceDatumCodeListIdentifier.attrs.CodeListVersionAgencyIdentifier,
                          );
                        }
                      }
                      builder = builder.up();
                    }
                    if (SCHorizontalReferenceDatum.GeographicReferenceDatumName !== null) {
                      builder = builder.ele('sc:GeographicReferenceDatumName');
                      {
                        const SCGeographicReferenceDatumName = SCHorizontalReferenceDatum.GeographicReferenceDatumName;
                        builder.txt(SCGeographicReferenceDatumName);
                      }
                      builder = builder.up();
                    }
                  }
                  builder = builder.up();
                }
                if (TRIGeographicLocationDescription.DataCollectionDate !== null) {
                  builder = builder.ele('sc:DataCollectionDate');
                  {
                    const SCDataCollectionDate = TRIGeographicLocationDescription.DataCollectionDate;
                    builder.txt(String(SCDataCollectionDate satisfies ISO8601DateOnly));
                  }
                  builder = builder.up();
                }
                if (TRIGeographicLocationDescription.LocationCommentsText !== null) {
                  builder = builder.ele('sc:LocationCommentsText');
                  {
                    const SCLocationCommentsText = TRIGeographicLocationDescription.LocationCommentsText;
                    builder.txt(SCLocationCommentsText);
                  }
                  builder = builder.up();
                }
                if (TRIGeographicLocationDescription.VerticalMeasure !== null) {
                  builder = builder.ele('sc:VerticalMeasure');
                  {
                    const SCVerticalMeasure = TRIGeographicLocationDescription.VerticalMeasure;
                    if (SCVerticalMeasure.MeasureValue !== null) {
                      builder = builder.ele('sc:MeasureValue');
                      {
                        const SCMeasureValue = SCVerticalMeasure.MeasureValue;
                        builder.txt(SCMeasureValue);
                      }
                      builder = builder.up();
                    }
                    if (SCVerticalMeasure.MeasureUnit !== null) {
                      builder = builder.ele('sc:MeasureUnit');
                      {
                        const SCMeasureUnit = SCVerticalMeasure.MeasureUnit;
                        if (SCMeasureUnit.MeasureUnitCode !== null) {
                          builder = builder.ele('sc:MeasureUnitCode');
                          {
                            const SCMeasureUnitCode = SCMeasureUnit.MeasureUnitCode;
                            builder.txt(SCMeasureUnitCode);
                          }
                          builder = builder.up();
                        }
                        if (SCMeasureUnit.MeasureUnitCodeListIdentifier !== null) {
                          builder = builder.ele('sc:MeasureUnitCodeListIdentifier');
                          {
                            const SCMeasureUnitCodeListIdentifier = SCMeasureUnit.MeasureUnitCodeListIdentifier;
                            builder.txt(SCMeasureUnitCodeListIdentifier.value);
                            if (SCMeasureUnitCodeListIdentifier.attrs.CodeListVersionIdentifier !== undefined) {
                              builder.att(
                                'CodeListVersionIdentifier',
                                SCMeasureUnitCodeListIdentifier.attrs.CodeListVersionIdentifier,
                              );
                            }
                            if (SCMeasureUnitCodeListIdentifier.attrs.CodeListVersionAgencyIdentifier !== undefined) {
                              builder.att(
                                'CodeListVersionAgencyIdentifier',
                                SCMeasureUnitCodeListIdentifier.attrs.CodeListVersionAgencyIdentifier,
                              );
                            }
                          }
                          builder = builder.up();
                        }
                        if (SCMeasureUnit.MeasureUnitName !== null) {
                          builder = builder.ele('sc:MeasureUnitName');
                          {
                            const SCMeasureUnitName = SCMeasureUnit.MeasureUnitName;
                            builder.txt(SCMeasureUnitName);
                          }
                          builder = builder.up();
                        }
                      }
                      builder = builder.up();
                    }
                    if (SCVerticalMeasure.MeasurePrecisionText !== null) {
                      builder = builder.ele('sc:MeasurePrecisionText');
                      {
                        const SCMeasurePrecisionText = SCVerticalMeasure.MeasurePrecisionText;
                        builder.txt(SCMeasurePrecisionText);
                      }
                      builder = builder.up();
                    }
                    if (SCVerticalMeasure.ResultQualifier !== null) {
                      builder = builder.ele('sc:ResultQualifier');
                      {
                        const SCResultQualifier = SCVerticalMeasure.ResultQualifier;
                        if (SCResultQualifier.ResultQualifierCode !== null) {
                          builder = builder.ele('sc:ResultQualifierCode');
                          {
                            const SCResultQualifierCode = SCResultQualifier.ResultQualifierCode;
                            builder.txt(SCResultQualifierCode);
                          }
                          builder = builder.up();
                        }
                        if (SCResultQualifier.ResultQualifierCodeListIdentifier !== null) {
                          builder = builder.ele('sc:ResultQualifierCodeListIdentifier');
                          {
                            const SCResultQualifierCodeListIdentifier =
                              SCResultQualifier.ResultQualifierCodeListIdentifier;
                            builder.txt(SCResultQualifierCodeListIdentifier.value);
                            if (SCResultQualifierCodeListIdentifier.attrs.CodeListVersionIdentifier !== undefined) {
                              builder.att(
                                'CodeListVersionIdentifier',
                                SCResultQualifierCodeListIdentifier.attrs.CodeListVersionIdentifier,
                              );
                            }
                            if (
                              SCResultQualifierCodeListIdentifier.attrs.CodeListVersionAgencyIdentifier !== undefined
                            ) {
                              builder.att(
                                'CodeListVersionAgencyIdentifier',
                                SCResultQualifierCodeListIdentifier.attrs.CodeListVersionAgencyIdentifier,
                              );
                            }
                          }
                          builder = builder.up();
                        }
                        if (SCResultQualifier.ResultQualifierName !== null) {
                          builder = builder.ele('sc:ResultQualifierName');
                          {
                            const SCResultQualifierName = SCResultQualifier.ResultQualifierName;
                            builder.txt(SCResultQualifierName);
                          }
                          builder = builder.up();
                        }
                      }
                      builder = builder.up();
                    }
                  }
                  builder = builder.up();
                }
                if (TRIGeographicLocationDescription.VerticalCollectionMethod !== null) {
                  builder = builder.ele('sc:VerticalCollectionMethod');
                  {
                    const SCVerticalCollectionMethod = TRIGeographicLocationDescription.VerticalCollectionMethod;
                    if (SCVerticalCollectionMethod.MethodIdentifierCode !== null) {
                      builder = builder.ele('sc:MethodIdentifierCode');
                      {
                        const SCMethodIdentifierCode = SCVerticalCollectionMethod.MethodIdentifierCode;
                        builder.txt(SCMethodIdentifierCode);
                      }
                      builder = builder.up();
                    }
                    if (SCVerticalCollectionMethod.MethodIdentifierCodeListIdentifier !== null) {
                      builder = builder.ele('sc:MethodIdentifierCodeListIdentifier');
                      {
                        const SCMethodIdentifierCodeListIdentifier =
                          SCVerticalCollectionMethod.MethodIdentifierCodeListIdentifier;
                        builder.txt(SCMethodIdentifierCodeListIdentifier.value);
                        if (SCMethodIdentifierCodeListIdentifier.attrs.CodeListVersionIdentifier !== undefined) {
                          builder.att(
                            'CodeListVersionIdentifier',
                            SCMethodIdentifierCodeListIdentifier.attrs.CodeListVersionIdentifier,
                          );
                        }
                        if (SCMethodIdentifierCodeListIdentifier.attrs.CodeListVersionAgencyIdentifier !== undefined) {
                          builder.att(
                            'CodeListVersionAgencyIdentifier',
                            SCMethodIdentifierCodeListIdentifier.attrs.CodeListVersionAgencyIdentifier,
                          );
                        }
                      }
                      builder = builder.up();
                    }
                    if (SCVerticalCollectionMethod.MethodName !== null) {
                      builder = builder.ele('sc:MethodName');
                      {
                        const SCMethodName = SCVerticalCollectionMethod.MethodName;
                        builder.txt(SCMethodName);
                      }
                      builder = builder.up();
                    }
                    if (SCVerticalCollectionMethod.MethodDescriptionText !== null) {
                      builder = builder.ele('sc:MethodDescriptionText');
                      {
                        const SCMethodDescriptionText = SCVerticalCollectionMethod.MethodDescriptionText;
                        builder.txt(SCMethodDescriptionText);
                      }
                      builder = builder.up();
                    }
                    if (SCVerticalCollectionMethod.MethodDeviationsText !== null) {
                      builder = builder.ele('sc:MethodDeviationsText');
                      {
                        const SCMethodDeviationsText = SCVerticalCollectionMethod.MethodDeviationsText;
                        builder.txt(SCMethodDeviationsText);
                      }
                      builder = builder.up();
                    }
                  }
                  builder = builder.up();
                }
                if (TRIGeographicLocationDescription.VerticalReferenceDatum !== null) {
                  builder = builder.ele('sc:VerticalReferenceDatum');
                  {
                    const SCVerticalReferenceDatum = TRIGeographicLocationDescription.VerticalReferenceDatum;
                    if (SCVerticalReferenceDatum.GeographicReferenceDatumCode !== null) {
                      builder = builder.ele('sc:GeographicReferenceDatumCode');
                      {
                        const SCGeographicReferenceDatumCode = SCVerticalReferenceDatum.GeographicReferenceDatumCode;
                        builder.txt(SCGeographicReferenceDatumCode);
                      }
                      builder = builder.up();
                    }
                    if (SCVerticalReferenceDatum.GeographicReferenceDatumCodeListIdentifier !== null) {
                      builder = builder.ele('sc:GeographicReferenceDatumCodeListIdentifier');
                      {
                        const SCGeographicReferenceDatumCodeListIdentifier =
                          SCVerticalReferenceDatum.GeographicReferenceDatumCodeListIdentifier;
                        builder.txt(SCGeographicReferenceDatumCodeListIdentifier.value);
                        if (
                          SCGeographicReferenceDatumCodeListIdentifier.attrs.CodeListVersionIdentifier !== undefined
                        ) {
                          builder.att(
                            'CodeListVersionIdentifier',
                            SCGeographicReferenceDatumCodeListIdentifier.attrs.CodeListVersionIdentifier,
                          );
                        }
                        if (
                          SCGeographicReferenceDatumCodeListIdentifier.attrs.CodeListVersionAgencyIdentifier !==
                          undefined
                        ) {
                          builder.att(
                            'CodeListVersionAgencyIdentifier',
                            SCGeographicReferenceDatumCodeListIdentifier.attrs.CodeListVersionAgencyIdentifier,
                          );
                        }
                      }
                      builder = builder.up();
                    }
                    if (SCVerticalReferenceDatum.GeographicReferenceDatumName !== null) {
                      builder = builder.ele('sc:GeographicReferenceDatumName');
                      {
                        const SCGeographicReferenceDatumName = SCVerticalReferenceDatum.GeographicReferenceDatumName;
                        builder.txt(SCGeographicReferenceDatumName);
                      }
                      builder = builder.up();
                    }
                  }
                  builder = builder.up();
                }
                if (TRIGeographicLocationDescription.VerificationMethod !== null) {
                  builder = builder.ele('sc:VerificationMethod');
                  {
                    const SCVerificationMethod = TRIGeographicLocationDescription.VerificationMethod;
                    if (SCVerificationMethod.MethodIdentifierCode !== null) {
                      builder = builder.ele('sc:MethodIdentifierCode');
                      {
                        const SCMethodIdentifierCode = SCVerificationMethod.MethodIdentifierCode;
                        builder.txt(SCMethodIdentifierCode);
                      }
                      builder = builder.up();
                    }
                    if (SCVerificationMethod.MethodIdentifierCodeListIdentifier !== null) {
                      builder = builder.ele('sc:MethodIdentifierCodeListIdentifier');
                      {
                        const SCMethodIdentifierCodeListIdentifier =
                          SCVerificationMethod.MethodIdentifierCodeListIdentifier;
                        builder.txt(SCMethodIdentifierCodeListIdentifier.value);
                        if (SCMethodIdentifierCodeListIdentifier.attrs.CodeListVersionIdentifier !== undefined) {
                          builder.att(
                            'CodeListVersionIdentifier',
                            SCMethodIdentifierCodeListIdentifier.attrs.CodeListVersionIdentifier,
                          );
                        }
                        if (SCMethodIdentifierCodeListIdentifier.attrs.CodeListVersionAgencyIdentifier !== undefined) {
                          builder.att(
                            'CodeListVersionAgencyIdentifier',
                            SCMethodIdentifierCodeListIdentifier.attrs.CodeListVersionAgencyIdentifier,
                          );
                        }
                      }
                      builder = builder.up();
                    }
                    if (SCVerificationMethod.MethodName !== null) {
                      builder = builder.ele('sc:MethodName');
                      {
                        const SCMethodName = SCVerificationMethod.MethodName;
                        builder.txt(SCMethodName);
                      }
                      builder = builder.up();
                    }
                    if (SCVerificationMethod.MethodDescriptionText !== null) {
                      builder = builder.ele('sc:MethodDescriptionText');
                      {
                        const SCMethodDescriptionText = SCVerificationMethod.MethodDescriptionText;
                        builder.txt(SCMethodDescriptionText);
                      }
                      builder = builder.up();
                    }
                    if (SCVerificationMethod.MethodDeviationsText !== null) {
                      builder = builder.ele('sc:MethodDeviationsText');
                      {
                        const SCMethodDeviationsText = SCVerificationMethod.MethodDeviationsText;
                        builder.txt(SCMethodDeviationsText);
                      }
                      builder = builder.up();
                    }
                  }
                  builder = builder.up();
                }
                if (TRIGeographicLocationDescription.CoordinateDataSource !== null) {
                  builder = builder.ele('sc:CoordinateDataSource');
                  {
                    const SCCoordinateDataSource = TRIGeographicLocationDescription.CoordinateDataSource;
                    if (SCCoordinateDataSource.CoordinateDataSourceCode !== null) {
                      builder = builder.ele('sc:CoordinateDataSourceCode');
                      {
                        const SCCoordinateDataSourceCode = SCCoordinateDataSource.CoordinateDataSourceCode;
                        builder.txt(SCCoordinateDataSourceCode);
                      }
                      builder = builder.up();
                    }
                    if (SCCoordinateDataSource.CoordinateDataSourceCodeListIdentifier !== null) {
                      builder = builder.ele('sc:CoordinateDataSourceCodeListIdentifier');
                      {
                        const SCCoordinateDataSourceCodeListIdentifier =
                          SCCoordinateDataSource.CoordinateDataSourceCodeListIdentifier;
                        builder.txt(SCCoordinateDataSourceCodeListIdentifier.value);
                        if (SCCoordinateDataSourceCodeListIdentifier.attrs.CodeListVersionIdentifier !== undefined) {
                          builder.att(
                            'CodeListVersionIdentifier',
                            SCCoordinateDataSourceCodeListIdentifier.attrs.CodeListVersionIdentifier,
                          );
                        }
                        if (
                          SCCoordinateDataSourceCodeListIdentifier.attrs.CodeListVersionAgencyIdentifier !== undefined
                        ) {
                          builder.att(
                            'CodeListVersionAgencyIdentifier',
                            SCCoordinateDataSourceCodeListIdentifier.attrs.CodeListVersionAgencyIdentifier,
                          );
                        }
                      }
                      builder = builder.up();
                    }
                    if (SCCoordinateDataSource.CoordinateDataSourceName !== null) {
                      builder = builder.ele('sc:CoordinateDataSourceName');
                      {
                        const SCCoordinateDataSourceName = SCCoordinateDataSource.CoordinateDataSourceName;
                        builder.txt(SCCoordinateDataSourceName);
                      }
                      builder = builder.up();
                    }
                  }
                  builder = builder.up();
                }
                if (TRIGeographicLocationDescription.GeometricType !== null) {
                  builder = builder.ele('sc:GeometricType');
                  {
                    const SCGeometricType = TRIGeographicLocationDescription.GeometricType;
                    if (SCGeometricType.GeometricTypeCode !== null) {
                      builder = builder.ele('sc:GeometricTypeCode');
                      {
                        const SCGeometricTypeCode = SCGeometricType.GeometricTypeCode;
                        builder.txt(SCGeometricTypeCode);
                      }
                      builder = builder.up();
                    }
                    if (SCGeometricType.GeometricTypeCodeListIdentifier !== null) {
                      builder = builder.ele('sc:GeometricTypeCodeListIdentifier');
                      {
                        const SCGeometricTypeCodeListIdentifier = SCGeometricType.GeometricTypeCodeListIdentifier;
                        builder.txt(SCGeometricTypeCodeListIdentifier.value);
                        if (SCGeometricTypeCodeListIdentifier.attrs.CodeListVersionIdentifier !== undefined) {
                          builder.att(
                            'CodeListVersionIdentifier',
                            SCGeometricTypeCodeListIdentifier.attrs.CodeListVersionIdentifier,
                          );
                        }
                        if (SCGeometricTypeCodeListIdentifier.attrs.CodeListVersionAgencyIdentifier !== undefined) {
                          builder.att(
                            'CodeListVersionAgencyIdentifier',
                            SCGeometricTypeCodeListIdentifier.attrs.CodeListVersionAgencyIdentifier,
                          );
                        }
                      }
                      builder = builder.up();
                    }
                    if (SCGeometricType.GeometricTypeName !== null) {
                      builder = builder.ele('sc:GeometricTypeName');
                      {
                        const SCGeometricTypeName = SCGeometricType.GeometricTypeName;
                        builder.txt(SCGeometricTypeName);
                      }
                      builder = builder.up();
                    }
                  }
                  builder = builder.up();
                }
                if (TRIGeographicLocationDescription.LatitudeDegreeMeasure !== null) {
                  builder = builder.ele('TRI:LatitudeDegreeMeasure');
                  {
                    const TRILatitudeDegreeMeasure = TRIGeographicLocationDescription.LatitudeDegreeMeasure;
                    builder.txt(String(TRILatitudeDegreeMeasure satisfies number));
                  }
                  builder = builder.up();
                }
                if (TRIGeographicLocationDescription.LatitudeMinuteMeasure !== null) {
                  builder = builder.ele('TRI:LatitudeMinuteMeasure');
                  {
                    const TRILatitudeMinuteMeasure = TRIGeographicLocationDescription.LatitudeMinuteMeasure;
                    builder.txt(String(TRILatitudeMinuteMeasure satisfies number));
                  }
                  builder = builder.up();
                }
                if (TRIGeographicLocationDescription.LatitudeSecondMeasure !== null) {
                  builder = builder.ele('TRI:LatitudeSecondMeasure');
                  {
                    const TRILatitudeSecondMeasure = TRIGeographicLocationDescription.LatitudeSecondMeasure;
                    builder.txt(String(TRILatitudeSecondMeasure satisfies number));
                  }
                  builder = builder.up();
                }
                if (TRIGeographicLocationDescription.LongitudeDegreeMeasure !== null) {
                  builder = builder.ele('TRI:LongitudeDegreeMeasure');
                  {
                    const TRILongitudeDegreeMeasure = TRIGeographicLocationDescription.LongitudeDegreeMeasure;
                    builder.txt(String(TRILongitudeDegreeMeasure satisfies number));
                  }
                  builder = builder.up();
                }
                if (TRIGeographicLocationDescription.LongitudeMinuteMeasure !== null) {
                  builder = builder.ele('TRI:LongitudeMinuteMeasure');
                  {
                    const TRILongitudeMinuteMeasure = TRIGeographicLocationDescription.LongitudeMinuteMeasure;
                    builder.txt(String(TRILongitudeMinuteMeasure satisfies number));
                  }
                  builder = builder.up();
                }
                if (TRIGeographicLocationDescription.LongitudeSecondMeasure !== null) {
                  builder = builder.ele('TRI:LongitudeSecondMeasure');
                  {
                    const TRILongitudeSecondMeasure = TRIGeographicLocationDescription.LongitudeSecondMeasure;
                    builder.txt(String(TRILongitudeSecondMeasure satisfies number));
                  }
                  builder = builder.up();
                }
              }
              builder = builder.up();
            }
            if (TRIFacility.ParentCompanyNameNAIndicator !== null) {
              builder = builder.ele('TRI:ParentCompanyNameNAIndicator');
              {
                const TRIParentCompanyNameNAIndicator = TRIFacility.ParentCompanyNameNAIndicator;
                builder.txt(String(TRIParentCompanyNameNAIndicator satisfies boolean));
              }
              builder = builder.up();
            }
            if (TRIFacility.ParentCompanyNameText !== null) {
              builder = builder.ele('TRI:ParentCompanyNameText');
              {
                const TRIParentCompanyNameText = TRIFacility.ParentCompanyNameText;
                builder.txt(TRIParentCompanyNameText);
              }
              builder = builder.up();
            }
            if (TRIFacility.ParentCompanyNameNotStandard !== null) {
              builder = builder.ele('TRI:ParentCompanyNameNotStandard');
              {
                const TRIParentCompanyNameNotStandard = TRIFacility.ParentCompanyNameNotStandard;
                builder.txt(String(TRIParentCompanyNameNotStandard satisfies boolean));
              }
              builder = builder.up();
            }
            if (TRIFacility.ParentDunBradstreetCode !== null) {
              builder = builder.ele('TRI:ParentDunBradstreetCode');
              {
                const TRIParentDunBradstreetCode = TRIFacility.ParentDunBradstreetCode;
                builder.txt(TRIParentDunBradstreetCode);
              }
              builder = builder.up();
            }
            if (TRIFacility.ForeignParentCompanyNameNAIndicator !== null) {
              builder = builder.ele('TRI:ForeignParentCompanyNameNAIndicator');
              {
                const TRIForeignParentCompanyNameNAIndicator = TRIFacility.ForeignParentCompanyNameNAIndicator;
                builder.txt(String(TRIForeignParentCompanyNameNAIndicator satisfies boolean));
              }
              builder = builder.up();
            }
            if (TRIFacility.ForeignParentCompanyNameText !== null) {
              builder = builder.ele('TRI:ForeignParentCompanyNameText');
              {
                const TRIForeignParentCompanyNameText = TRIFacility.ForeignParentCompanyNameText;
                builder.txt(TRIForeignParentCompanyNameText);
              }
              builder = builder.up();
            }
            if (TRIFacility.ForeignParentCompanyNameNotStandard !== null) {
              builder = builder.ele('TRI:ForeignParentCompanyNameNotStandard');
              {
                const TRIForeignParentCompanyNameNotStandard = TRIFacility.ForeignParentCompanyNameNotStandard;
                builder.txt(String(TRIForeignParentCompanyNameNotStandard satisfies boolean));
              }
              builder = builder.up();
            }
            if (TRIFacility.ForeignParentDunBradstreetCode !== null) {
              builder = builder.ele('TRI:ForeignParentDunBradstreetCode');
              {
                const TRIForeignParentDunBradstreetCode = TRIFacility.ForeignParentDunBradstreetCode;
                builder.txt(TRIForeignParentDunBradstreetCode);
              }
              builder = builder.up();
            }
            for (const TRIFacilityItem of TRIFacility.FacilityDunBradstreetCode) {
              builder = builder.ele('TRI:FacilityDunBradstreetCode');
              {
                const TRIFacilityDunBradstreetCode = TRIFacilityItem;
                builder.txt(TRIFacilityDunBradstreetCode);
              }
              builder = builder.up();
            }
            for (const TRIFacilityItem of TRIFacility.RCRAIdentificationNumber) {
              builder = builder.ele('TRI:RCRAIdentificationNumber');
              {
                const TRIRCRAIdentificationNumber = TRIFacilityItem;
                builder.txt(TRIRCRAIdentificationNumber);
              }
              builder = builder.up();
            }
            for (const TRIFacilityItem of TRIFacility.NPDESIdentificationNumber) {
              builder = builder.ele('TRI:NPDESIdentificationNumber');
              {
                const TRINPDESIdentificationNumber = TRIFacilityItem;
                builder.txt(TRINPDESIdentificationNumber);
              }
              builder = builder.up();
            }
            for (const TRIFacilityItem of TRIFacility.UICIdentificationNumber) {
              builder = builder.ele('TRI:UICIdentificationNumber');
              {
                const TRIUICIdentificationNumber = TRIFacilityItem;
                builder.txt(TRIUICIdentificationNumber);
              }
              builder = builder.up();
            }
          }
          builder = builder.up();
        }
        for (const TRISubmissionItem of TRISubmission.Report) {
          builder = builder.ele('TRI:Report');
          {
            const TRIReport = TRISubmissionItem;
            if (TRIReport.ReportMetaData !== null) {
              builder = builder.ele('TRI:ReportMetaData');
              {
                const TRIReportMetaData = TRIReport.ReportMetaData;
                if (TRIReportMetaData.ReportPostmarkDate !== null) {
                  builder = builder.ele('TRI:ReportPostmarkDate');
                  {
                    const TRIReportPostmarkDate = TRIReportMetaData.ReportPostmarkDate;
                    builder.txt(String(TRIReportPostmarkDate satisfies ISO8601DateOnly));
                  }
                  builder = builder.up();
                }
                if (TRIReportMetaData.ReportReceivedDate !== null) {
                  builder = builder.ele('TRI:ReportReceivedDate');
                  {
                    const TRIReportReceivedDate = TRIReportMetaData.ReportReceivedDate;
                    builder.txt(String(TRIReportReceivedDate satisfies ISO8601DateOnly));
                  }
                  builder = builder.up();
                }
                if (TRIReportMetaData.ReportOriginalPostmarkDate !== null) {
                  builder = builder.ele('TRI:ReportOriginalPostmarkDate');
                  {
                    const TRIReportOriginalPostmarkDate = TRIReportMetaData.ReportOriginalPostmarkDate;
                    builder.txt(String(TRIReportOriginalPostmarkDate satisfies ISO8601DateOnly));
                  }
                  builder = builder.up();
                }
                if (TRIReportMetaData.ReportOriginalReceivedDate !== null) {
                  builder = builder.ele('TRI:ReportOriginalReceivedDate');
                  {
                    const TRIReportOriginalReceivedDate = TRIReportMetaData.ReportOriginalReceivedDate;
                    builder.txt(String(TRIReportOriginalReceivedDate satisfies ISO8601DateOnly));
                  }
                  builder = builder.up();
                }
                if (TRIReportMetaData.ReportSubmissionMethodCode !== null) {
                  builder = builder.ele('TRI:ReportSubmissionMethodCode');
                  {
                    const TRIReportSubmissionMethodCode = TRIReportMetaData.ReportSubmissionMethodCode;
                    builder.txt(TRIReportSubmissionMethodCode);
                  }
                  builder = builder.up();
                }
                if (TRIReportMetaData.EPAPassedValidationIndicator !== null) {
                  builder = builder.ele('TRI:EPAPassedValidationIndicator');
                  {
                    const TRIEPAPassedValidationIndicator = TRIReportMetaData.EPAPassedValidationIndicator;
                    builder.txt(String(TRIEPAPassedValidationIndicator satisfies boolean));
                  }
                  builder = builder.up();
                }
                if (TRIReportMetaData.EPAProcessingStatusCode !== null) {
                  builder = builder.ele('TRI:EPAProcessingStatusCode');
                  {
                    const TRIEPAProcessingStatusCode = TRIReportMetaData.EPAProcessingStatusCode;
                    builder.txt(TRIEPAProcessingStatusCode);
                  }
                  builder = builder.up();
                }
                if (TRIReportMetaData.UnalteredReportIndicator !== null) {
                  builder = builder.ele('TRI:UnalteredReportIndicator');
                  {
                    const TRIUnalteredReportIndicator = TRIReportMetaData.UnalteredReportIndicator;
                    builder.txt(String(TRIUnalteredReportIndicator satisfies boolean));
                  }
                  builder = builder.up();
                }
                if (TRIReportMetaData.FormPreparationMethod !== null) {
                  builder = builder.ele('TRI:FormPreparationMethod');
                  {
                    const TRIFormPreparationMethod = TRIReportMetaData.FormPreparationMethod;
                    builder.txt(TRIFormPreparationMethod);
                  }
                  builder = builder.up();
                }
                for (const TRIReportMetaDataItem of TRIReportMetaData.ReportValidation) {
                  builder = builder.ele('TRI:ReportValidation');
                  {
                    const TRIReportValidation = TRIReportMetaDataItem;
                    if (TRIReportValidation.ValidationEntityNameText !== null) {
                      builder = builder.ele('TRI:ValidationEntityNameText');
                      {
                        const TRIValidationEntityNameText = TRIReportValidation.ValidationEntityNameText;
                        builder.txt(TRIValidationEntityNameText);
                      }
                      builder = builder.up();
                    }
                    if (TRIReportValidation.ValidationMessageCode !== null) {
                      builder = builder.ele('TRI:ValidationMessageCode');
                      {
                        const TRIValidationMessageCode = TRIReportValidation.ValidationMessageCode;
                        builder.txt(TRIValidationMessageCode);
                      }
                      builder = builder.up();
                    }
                    if (TRIReportValidation.ValidationMessageText !== null) {
                      builder = builder.ele('TRI:ValidationMessageText');
                      {
                        const TRIValidationMessageText = TRIReportValidation.ValidationMessageText;
                        builder.txt(TRIValidationMessageText);
                      }
                      builder = builder.up();
                    }
                    if (TRIReportValidation.EPAErrorSeverityCode !== null) {
                      builder = builder.ele('TRI:EPAErrorSeverityCode');
                      {
                        const TRIEPAErrorSeverityCode = TRIReportValidation.EPAErrorSeverityCode;
                        builder.txt(TRIEPAErrorSeverityCode);
                      }
                      builder = builder.up();
                    }
                  }
                  builder = builder.up();
                }
              }
              builder = builder.up();
            }
            for (const TRIReportItem of TRIReport.ReportIdentifier) {
              builder = builder.ele('sc:ReportIdentifier');
              {
                const SCReportIdentifier = TRIReportItem;
                builder.txt(SCReportIdentifier.value);
                if (SCReportIdentifier.attrs.ReportIdentifierContext !== undefined) {
                  builder.att('ReportIdentifierContext', SCReportIdentifier.attrs.ReportIdentifierContext);
                }
              }
              builder = builder.up();
            }
            for (const TRIReportItem of TRIReport.ReplacedReportIdentifier) {
              builder = builder.ele('sc:ReplacedReportIdentifier');
              {
                const SCReplacedReportIdentifier = TRIReportItem;
                builder.txt(SCReplacedReportIdentifier);
              }
              builder = builder.up();
            }
            if (TRIReport.ReportType !== null) {
              builder = builder.ele('TRI:ReportType');
              {
                const TRIReportType = TRIReport.ReportType;
                if (TRIReportType.ReportTypeCode !== null) {
                  builder = builder.ele('TRI:ReportTypeCode');
                  {
                    const TRIReportTypeCode = TRIReportType.ReportTypeCode;
                    builder.txt(TRIReportTypeCode);
                  }
                  builder = builder.up();
                }
                if (TRIReportType.ReportTypeCodeListIdentifier !== null) {
                  builder = builder.ele('sc:ReportTypeCodeListIdentifier');
                  {
                    const SCReportTypeCodeListIdentifier = TRIReportType.ReportTypeCodeListIdentifier;
                    builder.txt(SCReportTypeCodeListIdentifier.value);
                    if (SCReportTypeCodeListIdentifier.attrs.CodeListVersionIdentifier !== undefined) {
                      builder.att(
                        'CodeListVersionIdentifier',
                        SCReportTypeCodeListIdentifier.attrs.CodeListVersionIdentifier,
                      );
                    }
                    if (SCReportTypeCodeListIdentifier.attrs.CodeListVersionAgencyIdentifier !== undefined) {
                      builder.att(
                        'CodeListVersionAgencyIdentifier',
                        SCReportTypeCodeListIdentifier.attrs.CodeListVersionAgencyIdentifier,
                      );
                    }
                  }
                  builder = builder.up();
                }
                if (TRIReportType.ReportTypeName !== null) {
                  builder = builder.ele('sc:ReportTypeName');
                  {
                    const SCReportTypeName = TRIReportType.ReportTypeName;
                    builder.txt(SCReportTypeName);
                  }
                  builder = builder.up();
                }
              }
              builder = builder.up();
            }
            if (TRIReport.SubmissionReportingYear !== null) {
              builder = builder.ele('TRI:SubmissionReportingYear');
              {
                const TRISubmissionReportingYear = TRIReport.SubmissionReportingYear;
                builder.txt(TRISubmissionReportingYear);
              }
              builder = builder.up();
            }
            if (TRIReport.ReportDueDate !== null) {
              builder = builder.ele('sc:ReportDueDate');
              {
                const SCReportDueDate = TRIReport.ReportDueDate;
                builder.txt(String(SCReportDueDate satisfies ISO8601DateOnly));
              }
              builder = builder.up();
            }
            if (TRIReport.RevisionIndicator !== null) {
              builder = builder.ele('sc:RevisionIndicator');
              {
                const SCRevisionIndicator = TRIReport.RevisionIndicator;
                builder.txt(SCRevisionIndicator);
              }
              builder = builder.up();
            }
            if (TRIReport.ChemicalTradeSecretIndicator !== null) {
              builder = builder.ele('TRI:ChemicalTradeSecretIndicator');
              {
                const TRIChemicalTradeSecretIndicator = TRIReport.ChemicalTradeSecretIndicator;
                builder.txt(String(TRIChemicalTradeSecretIndicator satisfies boolean));
              }
              builder = builder.up();
            }
            if (TRIReport.SubmissionSanitizedIndicator !== null) {
              builder = builder.ele('TRI:SubmissionSanitizedIndicator');
              {
                const TRISubmissionSanitizedIndicator = TRIReport.SubmissionSanitizedIndicator;
                builder.txt(String(TRISubmissionSanitizedIndicator satisfies boolean));
              }
              builder = builder.up();
            }
            if (TRIReport.CertifierName !== null) {
              builder = builder.ele('TRI:CertifierName');
              {
                const TRICertifierName = TRIReport.CertifierName;
                builder.txt(TRICertifierName);
              }
              builder = builder.up();
            }
            if (TRIReport.CertifierTitleText !== null) {
              builder = builder.ele('TRI:CertifierTitleText');
              {
                const TRICertifierTitleText = TRIReport.CertifierTitleText;
                builder.txt(TRICertifierTitleText);
              }
              builder = builder.up();
            }
            if (TRIReport.CertificationSignedDate !== null) {
              builder = builder.ele('TRI:CertificationSignedDate');
              {
                const TRICertificationSignedDate = TRIReport.CertificationSignedDate;
                builder.txt(String(TRICertificationSignedDate satisfies ISO8601DateOnly));
              }
              builder = builder.up();
            }
            if (TRIReport.SubmissionEntireFacilityIndicator !== null) {
              builder = builder.ele('TRI:SubmissionEntireFacilityIndicator');
              {
                const TRISubmissionEntireFacilityIndicator = TRIReport.SubmissionEntireFacilityIndicator;
                builder.txt(String(TRISubmissionEntireFacilityIndicator satisfies boolean));
              }
              builder = builder.up();
            }
            if (TRIReport.SubmissionPartialFacilityIndicator !== null) {
              builder = builder.ele('TRI:SubmissionPartialFacilityIndicator');
              {
                const TRISubmissionPartialFacilityIndicator = TRIReport.SubmissionPartialFacilityIndicator;
                builder.txt(String(TRISubmissionPartialFacilityIndicator satisfies boolean));
              }
              builder = builder.up();
            }
            if (TRIReport.SubmissionFederalFacilityIndicator !== null) {
              builder = builder.ele('TRI:SubmissionFederalFacilityIndicator');
              {
                const TRISubmissionFederalFacilityIndicator = TRIReport.SubmissionFederalFacilityIndicator;
                builder.txt(TRISubmissionFederalFacilityIndicator);
              }
              builder = builder.up();
            }
            if (TRIReport.SubmissionGOCOFacilityIndicator !== null) {
              builder = builder.ele('TRI:SubmissionGOCOFacilityIndicator');
              {
                const TRISubmissionGOCOFacilityIndicator = TRIReport.SubmissionGOCOFacilityIndicator;
                builder.txt(String(TRISubmissionGOCOFacilityIndicator satisfies boolean));
              }
              builder = builder.up();
            }
            if (TRIReport.TechnicalContactNameText !== null) {
              builder = builder.ele('TRI:TechnicalContactNameText');
              {
                const TRITechnicalContactNameText = TRIReport.TechnicalContactNameText;
                if (TRITechnicalContactNameText.IndividualIdentifier !== null) {
                  builder = builder.ele('sc:IndividualIdentifier');
                  {
                    const SCIndividualIdentifier = TRITechnicalContactNameText.IndividualIdentifier;
                    builder.txt(SCIndividualIdentifier.value);
                    if (SCIndividualIdentifier.attrs.IndividualIdentifierContext !== undefined) {
                      builder.att(
                        'IndividualIdentifierContext',
                        SCIndividualIdentifier.attrs.IndividualIdentifierContext,
                      );
                    }
                  }
                  builder = builder.up();
                }
                if (TRITechnicalContactNameText.IndividualTitleText !== null) {
                  builder = builder.ele('sc:IndividualTitleText');
                  {
                    const SCIndividualTitleText = TRITechnicalContactNameText.IndividualTitleText;
                    builder.txt(SCIndividualTitleText);
                  }
                  builder = builder.up();
                }
                if (TRITechnicalContactNameText.NamePrefixText !== null) {
                  builder = builder.ele('sc:NamePrefixText');
                  {
                    const SCNamePrefixText = TRITechnicalContactNameText.NamePrefixText;
                    builder.txt(SCNamePrefixText);
                  }
                  builder = builder.up();
                }
                {
                  const IndividualName = TRITechnicalContactNameText.IndividualName;
                  if (IndividualName !== null) {
                    if (IndividualName.choice === 'IndividualFullName') {
                      {
                        const chosen2 = IndividualName.value;
                        if (chosen2 !== null) {
                          builder = builder.ele('sc:IndividualFullName');
                          {
                            const SCIndividualFullName = chosen2;
                            builder.txt(SCIndividualFullName);
                          }
                          builder = builder.up();
                        }
                      }
                    }
                    if (IndividualName.choice === 1) {
                      {
                        const chosen3 = IndividualName.value;
                        if (chosen3.FirstName !== null) {
                          builder = builder.ele('sc:FirstName');
                          {
                            const SCFirstName = chosen3.FirstName;
                            builder.txt(SCFirstName);
                          }
                          builder = builder.up();
                        }
                        if (chosen3.MiddleName !== null) {
                          builder = builder.ele('sc:MiddleName');
                          {
                            const SCMiddleName = chosen3.MiddleName;
                            builder.txt(SCMiddleName);
                          }
                          builder = builder.up();
                        }
                        if (chosen3.LastName !== null) {
                          builder = builder.ele('sc:LastName');
                          {
                            const SCLastName = chosen3.LastName;
                            builder.txt(SCLastName);
                          }
                          builder = builder.up();
                        }
                      }
                    }
                  }
                }
                if (TRITechnicalContactNameText.NameSuffixText !== null) {
                  builder = builder.ele('sc:NameSuffixText');
                  {
                    const SCNameSuffixText = TRITechnicalContactNameText.NameSuffixText;
                    builder.txt(SCNameSuffixText);
                  }
                  builder = builder.up();
                }
              }
              builder = builder.up();
            }
            if (TRIReport.TechnicalContactPhoneText !== null) {
              builder = builder.ele('TRI:TechnicalContactPhoneText');
              {
                const TRITechnicalContactPhoneText = TRIReport.TechnicalContactPhoneText;
                builder.txt(TRITechnicalContactPhoneText);
              }
              builder = builder.up();
            }
            if (TRIReport.TechnicalContactPhoneExtText !== null) {
              builder = builder.ele('TRI:TechnicalContactPhoneExtText');
              {
                const TRITechnicalContactPhoneExtText = TRIReport.TechnicalContactPhoneExtText;
                builder.txt(TRITechnicalContactPhoneExtText);
              }
              builder = builder.up();
            }
            if (TRIReport.TechnicalContactEmailAddressText !== null) {
              builder = builder.ele('TRI:TechnicalContactEmailAddressText');
              {
                const TRITechnicalContactEmailAddressText = TRIReport.TechnicalContactEmailAddressText;
                builder.txt(TRITechnicalContactEmailAddressText);
              }
              builder = builder.up();
            }
            if (TRIReport.PublicContactNameText !== null) {
              builder = builder.ele('TRI:PublicContactNameText');
              {
                const TRIPublicContactNameText = TRIReport.PublicContactNameText;
                if (TRIPublicContactNameText.IndividualIdentifier !== null) {
                  builder = builder.ele('sc:IndividualIdentifier');
                  {
                    const SCIndividualIdentifier = TRIPublicContactNameText.IndividualIdentifier;
                    builder.txt(SCIndividualIdentifier.value);
                    if (SCIndividualIdentifier.attrs.IndividualIdentifierContext !== undefined) {
                      builder.att(
                        'IndividualIdentifierContext',
                        SCIndividualIdentifier.attrs.IndividualIdentifierContext,
                      );
                    }
                  }
                  builder = builder.up();
                }
                if (TRIPublicContactNameText.IndividualTitleText !== null) {
                  builder = builder.ele('sc:IndividualTitleText');
                  {
                    const SCIndividualTitleText = TRIPublicContactNameText.IndividualTitleText;
                    builder.txt(SCIndividualTitleText);
                  }
                  builder = builder.up();
                }
                if (TRIPublicContactNameText.NamePrefixText !== null) {
                  builder = builder.ele('sc:NamePrefixText');
                  {
                    const SCNamePrefixText = TRIPublicContactNameText.NamePrefixText;
                    builder.txt(SCNamePrefixText);
                  }
                  builder = builder.up();
                }
                {
                  const IndividualName = TRIPublicContactNameText.IndividualName;
                  if (IndividualName !== null) {
                    if (IndividualName.choice === 'IndividualFullName') {
                      {
                        const chosen4 = IndividualName.value;
                        if (chosen4 !== null) {
                          builder = builder.ele('sc:IndividualFullName');
                          {
                            const SCIndividualFullName = chosen4;
                            builder.txt(SCIndividualFullName);
                          }
                          builder = builder.up();
                        }
                      }
                    }
                    if (IndividualName.choice === 1) {
                      {
                        const chosen5 = IndividualName.value;
                        if (chosen5.FirstName !== null) {
                          builder = builder.ele('sc:FirstName');
                          {
                            const SCFirstName = chosen5.FirstName;
                            builder.txt(SCFirstName);
                          }
                          builder = builder.up();
                        }
                        if (chosen5.MiddleName !== null) {
                          builder = builder.ele('sc:MiddleName');
                          {
                            const SCMiddleName = chosen5.MiddleName;
                            builder.txt(SCMiddleName);
                          }
                          builder = builder.up();
                        }
                        if (chosen5.LastName !== null) {
                          builder = builder.ele('sc:LastName');
                          {
                            const SCLastName = chosen5.LastName;
                            builder.txt(SCLastName);
                          }
                          builder = builder.up();
                        }
                      }
                    }
                  }
                }
                if (TRIPublicContactNameText.NameSuffixText !== null) {
                  builder = builder.ele('sc:NameSuffixText');
                  {
                    const SCNameSuffixText = TRIPublicContactNameText.NameSuffixText;
                    builder.txt(SCNameSuffixText);
                  }
                  builder = builder.up();
                }
              }
              builder = builder.up();
            }
            if (TRIReport.PublicContactPhoneText !== null) {
              builder = builder.ele('TRI:PublicContactPhoneText');
              {
                const TRIPublicContactPhoneText = TRIReport.PublicContactPhoneText;
                builder.txt(TRIPublicContactPhoneText);
              }
              builder = builder.up();
            }
            if (TRIReport.PublicContactPhoneExtText !== null) {
              builder = builder.ele('TRI:PublicContactPhoneExtText');
              {
                const TRIPublicContactPhoneExtText = TRIReport.PublicContactPhoneExtText;
                builder.txt(TRIPublicContactPhoneExtText);
              }
              builder = builder.up();
            }
            if (TRIReport.PublicContactEmailAddressText !== null) {
              builder = builder.ele('TRI:PublicContactEmailAddressText');
              {
                const TRIPublicContactEmailAddressText = TRIReport.PublicContactEmailAddressText;
                builder.txt(TRIPublicContactEmailAddressText);
              }
              builder = builder.up();
            }
            for (const TRIReportItem of TRIReport.ChemicalReportRevisionCode) {
              builder = builder.ele('TRI:ChemicalReportRevisionCode');
              {
                const TRIChemicalReportRevisionCode = TRIReportItem;
                builder.txt(TRIChemicalReportRevisionCode);
              }
              builder = builder.up();
            }
            for (const TRIReportItem of TRIReport.ChemicalReportWithdrawalCode) {
              builder = builder.ele('TRI:ChemicalReportWithdrawalCode');
              {
                const TRIChemicalReportWithdrawalCode = TRIReportItem;
                builder.txt(TRIChemicalReportWithdrawalCode);
              }
              builder = builder.up();
            }
            for (const TRIReportItem of TRIReport.ChemicalIdentification) {
              builder = builder.ele('TRI:ChemicalIdentification');
              {
                const TRIChemicalIdentification = TRIReportItem;
                if (TRIChemicalIdentification.CASNumber !== null) {
                  builder = builder.ele('sc:CASNumber');
                  {
                    const SCCASNumber = TRIChemicalIdentification.CASNumber;
                    builder.txt(SCCASNumber);
                  }
                  builder = builder.up();
                }
                if (TRIChemicalIdentification.ChemicalNameText !== null) {
                  builder = builder.ele('TRI:ChemicalNameText');
                  {
                    const TRIChemicalNameText = TRIChemicalIdentification.ChemicalNameText;
                    builder.txt(TRIChemicalNameText);
                  }
                  builder = builder.up();
                }
                if (TRIChemicalIdentification.ChemicalMixtureNameText !== null) {
                  builder = builder.ele('TRI:ChemicalMixtureNameText');
                  {
                    const TRIChemicalMixtureNameText = TRIChemicalIdentification.ChemicalMixtureNameText;
                    builder.txt(TRIChemicalMixtureNameText);
                  }
                  builder = builder.up();
                }
                if (TRIChemicalIdentification.MetalCompoundReportIncludeElementalMetalIndicator !== null) {
                  builder = builder.ele('TRI:MetalCompoundReportIncludeElementalMetalIndicator');
                  {
                    const TRIMetalCompoundReportIncludeElementalMetalIndicator =
                      TRIChemicalIdentification.MetalCompoundReportIncludeElementalMetalIndicator;
                    builder.txt(String(TRIMetalCompoundReportIncludeElementalMetalIndicator satisfies boolean));
                  }
                  builder = builder.up();
                }
                if (TRIChemicalIdentification.LeadExceedsThresholdIndicator !== null) {
                  builder = builder.ele('TRI:LeadExceedsThresholdIndicator');
                  {
                    const TRILeadExceedsThresholdIndicator = TRIChemicalIdentification.LeadExceedsThresholdIndicator;
                    builder.txt(String(TRILeadExceedsThresholdIndicator satisfies boolean));
                  }
                  builder = builder.up();
                }
                if (TRIChemicalIdentification.EPAChemicalIdentifier !== null) {
                  builder = builder.ele('sc:EPAChemicalIdentifier');
                  {
                    const SCEPAChemicalIdentifier = TRIChemicalIdentification.EPAChemicalIdentifier;
                    builder.txt(SCEPAChemicalIdentifier);
                  }
                  builder = builder.up();
                }
                if (TRIChemicalIdentification.EPAChemicalRegistryName !== null) {
                  builder = builder.ele('sc:EPAChemicalRegistryName');
                  {
                    const SCEPAChemicalRegistryName = TRIChemicalIdentification.EPAChemicalRegistryName;
                    builder.txt(SCEPAChemicalRegistryName);
                  }
                  builder = builder.up();
                }
                if (TRIChemicalIdentification.EPAChemicalRegistryNameContext !== null) {
                  builder = builder.ele('sc:EPAChemicalRegistryNameContext');
                  {
                    const SCEPAChemicalRegistryNameContext = TRIChemicalIdentification.EPAChemicalRegistryNameContext;
                    builder.txt(SCEPAChemicalRegistryNameContext);
                  }
                  builder = builder.up();
                }
                if (TRIChemicalIdentification.DioxinDistribution1Percent !== null) {
                  builder = builder.ele('TRI:DioxinDistribution1Percent');
                  {
                    const TRIDioxinDistribution1Percent = TRIChemicalIdentification.DioxinDistribution1Percent;
                    builder.txt(formRDecimalToString(TRIDioxinDistribution1Percent));
                  }
                  builder = builder.up();
                }
                if (TRIChemicalIdentification.DioxinDistribution2Percent !== null) {
                  builder = builder.ele('TRI:DioxinDistribution2Percent');
                  {
                    const TRIDioxinDistribution2Percent = TRIChemicalIdentification.DioxinDistribution2Percent;
                    builder.txt(formRDecimalToString(TRIDioxinDistribution2Percent));
                  }
                  builder = builder.up();
                }
                if (TRIChemicalIdentification.DioxinDistribution3Percent !== null) {
                  builder = builder.ele('TRI:DioxinDistribution3Percent');
                  {
                    const TRIDioxinDistribution3Percent = TRIChemicalIdentification.DioxinDistribution3Percent;
                    builder.txt(formRDecimalToString(TRIDioxinDistribution3Percent));
                  }
                  builder = builder.up();
                }
                if (TRIChemicalIdentification.DioxinDistribution4Percent !== null) {
                  builder = builder.ele('TRI:DioxinDistribution4Percent');
                  {
                    const TRIDioxinDistribution4Percent = TRIChemicalIdentification.DioxinDistribution4Percent;
                    builder.txt(formRDecimalToString(TRIDioxinDistribution4Percent));
                  }
                  builder = builder.up();
                }
                if (TRIChemicalIdentification.DioxinDistribution5Percent !== null) {
                  builder = builder.ele('TRI:DioxinDistribution5Percent');
                  {
                    const TRIDioxinDistribution5Percent = TRIChemicalIdentification.DioxinDistribution5Percent;
                    builder.txt(formRDecimalToString(TRIDioxinDistribution5Percent));
                  }
                  builder = builder.up();
                }
                if (TRIChemicalIdentification.DioxinDistribution6Percent !== null) {
                  builder = builder.ele('TRI:DioxinDistribution6Percent');
                  {
                    const TRIDioxinDistribution6Percent = TRIChemicalIdentification.DioxinDistribution6Percent;
                    builder.txt(formRDecimalToString(TRIDioxinDistribution6Percent));
                  }
                  builder = builder.up();
                }
                if (TRIChemicalIdentification.DioxinDistribution7Percent !== null) {
                  builder = builder.ele('TRI:DioxinDistribution7Percent');
                  {
                    const TRIDioxinDistribution7Percent = TRIChemicalIdentification.DioxinDistribution7Percent;
                    builder.txt(formRDecimalToString(TRIDioxinDistribution7Percent));
                  }
                  builder = builder.up();
                }
                if (TRIChemicalIdentification.DioxinDistribution8Percent !== null) {
                  builder = builder.ele('TRI:DioxinDistribution8Percent');
                  {
                    const TRIDioxinDistribution8Percent = TRIChemicalIdentification.DioxinDistribution8Percent;
                    builder.txt(formRDecimalToString(TRIDioxinDistribution8Percent));
                  }
                  builder = builder.up();
                }
                if (TRIChemicalIdentification.DioxinDistribution9Percent !== null) {
                  builder = builder.ele('TRI:DioxinDistribution9Percent');
                  {
                    const TRIDioxinDistribution9Percent = TRIChemicalIdentification.DioxinDistribution9Percent;
                    builder.txt(formRDecimalToString(TRIDioxinDistribution9Percent));
                  }
                  builder = builder.up();
                }
                if (TRIChemicalIdentification.DioxinDistribution10Percent !== null) {
                  builder = builder.ele('TRI:DioxinDistribution10Percent');
                  {
                    const TRIDioxinDistribution10Percent = TRIChemicalIdentification.DioxinDistribution10Percent;
                    builder.txt(formRDecimalToString(TRIDioxinDistribution10Percent));
                  }
                  builder = builder.up();
                }
                if (TRIChemicalIdentification.DioxinDistribution11Percent !== null) {
                  builder = builder.ele('TRI:DioxinDistribution11Percent');
                  {
                    const TRIDioxinDistribution11Percent = TRIChemicalIdentification.DioxinDistribution11Percent;
                    builder.txt(formRDecimalToString(TRIDioxinDistribution11Percent));
                  }
                  builder = builder.up();
                }
                if (TRIChemicalIdentification.DioxinDistribution12Percent !== null) {
                  builder = builder.ele('TRI:DioxinDistribution12Percent');
                  {
                    const TRIDioxinDistribution12Percent = TRIChemicalIdentification.DioxinDistribution12Percent;
                    builder.txt(formRDecimalToString(TRIDioxinDistribution12Percent));
                  }
                  builder = builder.up();
                }
                if (TRIChemicalIdentification.DioxinDistribution13Percent !== null) {
                  builder = builder.ele('TRI:DioxinDistribution13Percent');
                  {
                    const TRIDioxinDistribution13Percent = TRIChemicalIdentification.DioxinDistribution13Percent;
                    builder.txt(formRDecimalToString(TRIDioxinDistribution13Percent));
                  }
                  builder = builder.up();
                }
                if (TRIChemicalIdentification.DioxinDistribution14Percent !== null) {
                  builder = builder.ele('TRI:DioxinDistribution14Percent');
                  {
                    const TRIDioxinDistribution14Percent = TRIChemicalIdentification.DioxinDistribution14Percent;
                    builder.txt(formRDecimalToString(TRIDioxinDistribution14Percent));
                  }
                  builder = builder.up();
                }
                if (TRIChemicalIdentification.DioxinDistribution15Percent !== null) {
                  builder = builder.ele('TRI:DioxinDistribution15Percent');
                  {
                    const TRIDioxinDistribution15Percent = TRIChemicalIdentification.DioxinDistribution15Percent;
                    builder.txt(formRDecimalToString(TRIDioxinDistribution15Percent));
                  }
                  builder = builder.up();
                }
                if (TRIChemicalIdentification.DioxinDistribution16Percent !== null) {
                  builder = builder.ele('TRI:DioxinDistribution16Percent');
                  {
                    const TRIDioxinDistribution16Percent = TRIChemicalIdentification.DioxinDistribution16Percent;
                    builder.txt(formRDecimalToString(TRIDioxinDistribution16Percent));
                  }
                  builder = builder.up();
                }
                if (TRIChemicalIdentification.DioxinDistribution17Percent !== null) {
                  builder = builder.ele('TRI:DioxinDistribution17Percent');
                  {
                    const TRIDioxinDistribution17Percent = TRIChemicalIdentification.DioxinDistribution17Percent;
                    builder.txt(formRDecimalToString(TRIDioxinDistribution17Percent));
                  }
                  builder = builder.up();
                }
                if (TRIChemicalIdentification.DioxinDistributionNAIndicator !== null) {
                  builder = builder.ele('TRI:DioxinDistributionNAIndicator');
                  {
                    const TRIDioxinDistributionNAIndicator = TRIChemicalIdentification.DioxinDistributionNAIndicator;
                    builder.txt(String(TRIDioxinDistributionNAIndicator satisfies boolean));
                  }
                  builder = builder.up();
                }
              }
              builder = builder.up();
            }
            if (TRIReport.ChemicalActivitiesAndUses !== null) {
              builder = builder.ele('TRI:ChemicalActivitiesAndUses');
              {
                const TRIChemicalActivitiesAndUses = TRIReport.ChemicalActivitiesAndUses;
                if (TRIChemicalActivitiesAndUses.ChemicalAncillaryUsageIndicator !== null) {
                  builder = builder.ele('TRI:ChemicalAncillaryUsageIndicator');
                  {
                    const TRIChemicalAncillaryUsageIndicator =
                      TRIChemicalActivitiesAndUses.ChemicalAncillaryUsageIndicator;
                    builder.txt(String(TRIChemicalAncillaryUsageIndicator satisfies boolean));
                  }
                  builder = builder.up();
                }
                if (TRIChemicalActivitiesAndUses.ChemicalArticleComponentIndicator !== null) {
                  builder = builder.ele('TRI:ChemicalArticleComponentIndicator');
                  {
                    const TRIChemicalArticleComponentIndicator =
                      TRIChemicalActivitiesAndUses.ChemicalArticleComponentIndicator;
                    builder.txt(String(TRIChemicalArticleComponentIndicator satisfies boolean));
                  }
                  builder = builder.up();
                }
                if (TRIChemicalActivitiesAndUses.ChemicalByproductIndicator !== null) {
                  builder = builder.ele('TRI:ChemicalByproductIndicator');
                  {
                    const TRIChemicalByproductIndicator = TRIChemicalActivitiesAndUses.ChemicalByproductIndicator;
                    builder.txt(String(TRIChemicalByproductIndicator satisfies boolean));
                  }
                  builder = builder.up();
                }
                if (TRIChemicalActivitiesAndUses.ChemicalFormulationComponentIndicator !== null) {
                  builder = builder.ele('TRI:ChemicalFormulationComponentIndicator');
                  {
                    const TRIChemicalFormulationComponentIndicator =
                      TRIChemicalActivitiesAndUses.ChemicalFormulationComponentIndicator;
                    builder.txt(String(TRIChemicalFormulationComponentIndicator satisfies boolean));
                  }
                  builder = builder.up();
                }
                if (TRIChemicalActivitiesAndUses.ChemicalImportedIndicator !== null) {
                  builder = builder.ele('TRI:ChemicalImportedIndicator');
                  {
                    const TRIChemicalImportedIndicator = TRIChemicalActivitiesAndUses.ChemicalImportedIndicator;
                    builder.txt(String(TRIChemicalImportedIndicator satisfies boolean));
                  }
                  builder = builder.up();
                }
                if (TRIChemicalActivitiesAndUses.ChemicalManufactureAidIndicator !== null) {
                  builder = builder.ele('TRI:ChemicalManufactureAidIndicator');
                  {
                    const TRIChemicalManufactureAidIndicator =
                      TRIChemicalActivitiesAndUses.ChemicalManufactureAidIndicator;
                    builder.txt(String(TRIChemicalManufactureAidIndicator satisfies boolean));
                  }
                  builder = builder.up();
                }
                if (TRIChemicalActivitiesAndUses.ChemicalManufactureImpurityIndicator !== null) {
                  builder = builder.ele('TRI:ChemicalManufactureImpurityIndicator');
                  {
                    const TRIChemicalManufactureImpurityIndicator =
                      TRIChemicalActivitiesAndUses.ChemicalManufactureImpurityIndicator;
                    builder.txt(String(TRIChemicalManufactureImpurityIndicator satisfies boolean));
                  }
                  builder = builder.up();
                }
                if (TRIChemicalActivitiesAndUses.ChemicalProcessImpurityIndicator !== null) {
                  builder = builder.ele('TRI:ChemicalProcessImpurityIndicator');
                  {
                    const TRIChemicalProcessImpurityIndicator =
                      TRIChemicalActivitiesAndUses.ChemicalProcessImpurityIndicator;
                    builder.txt(String(TRIChemicalProcessImpurityIndicator satisfies boolean));
                  }
                  builder = builder.up();
                }
                if (TRIChemicalActivitiesAndUses.ChemicalProcessingAidIndicator !== null) {
                  builder = builder.ele('TRI:ChemicalProcessingAidIndicator');
                  {
                    const TRIChemicalProcessingAidIndicator =
                      TRIChemicalActivitiesAndUses.ChemicalProcessingAidIndicator;
                    builder.txt(String(TRIChemicalProcessingAidIndicator satisfies boolean));
                  }
                  builder = builder.up();
                }
                if (TRIChemicalActivitiesAndUses.ChemicalProducedIndicator !== null) {
                  builder = builder.ele('TRI:ChemicalProducedIndicator');
                  {
                    const TRIChemicalProducedIndicator = TRIChemicalActivitiesAndUses.ChemicalProducedIndicator;
                    builder.txt(String(TRIChemicalProducedIndicator satisfies boolean));
                  }
                  builder = builder.up();
                }
                if (TRIChemicalActivitiesAndUses.ChemicalReactantIndicator !== null) {
                  builder = builder.ele('TRI:ChemicalReactantIndicator');
                  {
                    const TRIChemicalReactantIndicator = TRIChemicalActivitiesAndUses.ChemicalReactantIndicator;
                    builder.txt(String(TRIChemicalReactantIndicator satisfies boolean));
                  }
                  builder = builder.up();
                }
                if (TRIChemicalActivitiesAndUses.ChemicalRepackagingIndicator !== null) {
                  builder = builder.ele('TRI:ChemicalRepackagingIndicator');
                  {
                    const TRIChemicalRepackagingIndicator = TRIChemicalActivitiesAndUses.ChemicalRepackagingIndicator;
                    builder.txt(String(TRIChemicalRepackagingIndicator satisfies boolean));
                  }
                  builder = builder.up();
                }
                if (TRIChemicalActivitiesAndUses.ChemicalSalesDistributionIndicator !== null) {
                  builder = builder.ele('TRI:ChemicalSalesDistributionIndicator');
                  {
                    const TRIChemicalSalesDistributionIndicator =
                      TRIChemicalActivitiesAndUses.ChemicalSalesDistributionIndicator;
                    builder.txt(String(TRIChemicalSalesDistributionIndicator satisfies boolean));
                  }
                  builder = builder.up();
                }
                if (TRIChemicalActivitiesAndUses.ChemicalUsedProcessedIndicator !== null) {
                  builder = builder.ele('TRI:ChemicalUsedProcessedIndicator');
                  {
                    const TRIChemicalUsedProcessedIndicator =
                      TRIChemicalActivitiesAndUses.ChemicalUsedProcessedIndicator;
                    builder.txt(String(TRIChemicalUsedProcessedIndicator satisfies boolean));
                  }
                  builder = builder.up();
                }
                if (TRIChemicalActivitiesAndUses.ChemicalProcessRecyclingIndicator !== null) {
                  builder = builder.ele('TRI:ChemicalProcessRecyclingIndicator');
                  {
                    const TRIChemicalProcessRecyclingIndicator =
                      TRIChemicalActivitiesAndUses.ChemicalProcessRecyclingIndicator;
                    builder.txt(String(TRIChemicalProcessRecyclingIndicator satisfies boolean));
                  }
                  builder = builder.up();
                }
                for (const TRIChemicalActivitiesAndUsesItem of TRIChemicalActivitiesAndUses.ChemicalAncillaryUsageSubcategory) {
                  builder = builder.ele('TRI:ChemicalAncillaryUsageSubcategory');
                  {
                    const TRIChemicalAncillaryUsageSubcategory = TRIChemicalActivitiesAndUsesItem;
                    builder.txt(TRIChemicalAncillaryUsageSubcategory);
                  }
                  builder = builder.up();
                }
                for (const TRIChemicalActivitiesAndUsesItem of TRIChemicalActivitiesAndUses.ChemicalFormulationComponentSubcategory) {
                  builder = builder.ele('TRI:ChemicalFormulationComponentSubcategory');
                  {
                    const TRIChemicalFormulationComponentSubcategory = TRIChemicalActivitiesAndUsesItem;
                    builder.txt(TRIChemicalFormulationComponentSubcategory);
                  }
                  builder = builder.up();
                }
                for (const TRIChemicalActivitiesAndUsesItem of TRIChemicalActivitiesAndUses.ChemicalManufactureAidSubcategory) {
                  builder = builder.ele('TRI:ChemicalManufactureAidSubcategory');
                  {
                    const TRIChemicalManufactureAidSubcategory = TRIChemicalActivitiesAndUsesItem;
                    builder.txt(TRIChemicalManufactureAidSubcategory);
                  }
                  builder = builder.up();
                }
                for (const TRIChemicalActivitiesAndUsesItem of TRIChemicalActivitiesAndUses.ChemicalProcessingAidSubcategory) {
                  builder = builder.ele('TRI:ChemicalProcessingAidSubcategory');
                  {
                    const TRIChemicalProcessingAidSubcategory = TRIChemicalActivitiesAndUsesItem;
                    builder.txt(TRIChemicalProcessingAidSubcategory);
                  }
                  builder = builder.up();
                }
                for (const TRIChemicalActivitiesAndUsesItem of TRIChemicalActivitiesAndUses.ChemicalReactantSubcategory) {
                  builder = builder.ele('TRI:ChemicalReactantSubcategory');
                  {
                    const TRIChemicalReactantSubcategory = TRIChemicalActivitiesAndUsesItem;
                    builder.txt(TRIChemicalReactantSubcategory);
                  }
                  builder = builder.up();
                }
              }
              builder = builder.up();
            }
            if (TRIReport.MaximumChemicalAmountCode !== null) {
              builder = builder.ele('TRI:MaximumChemicalAmountCode');
              {
                const TRIMaximumChemicalAmountCode = TRIReport.MaximumChemicalAmountCode;
                builder.txt(TRIMaximumChemicalAmountCode);
              }
              builder = builder.up();
            }
            for (const TRIReportItem of TRIReport.OnsiteReleaseQuantity) {
              builder = builder.ele('TRI:OnsiteReleaseQuantity');
              {
                const TRIOnsiteReleaseQuantity = TRIReportItem;
                builder = builder.ele('TRI:EnvironmentalMediumCode');
                {
                  const TRIEnvironmentalMediumCode = TRIOnsiteReleaseQuantity.EnvironmentalMediumCode;
                  builder.txt(TRIEnvironmentalMediumCode);
                }
                builder = builder.up();
                if (TRIOnsiteReleaseQuantity.OnsiteWasteQuantity !== null) {
                  builder = builder.ele('TRI:OnsiteWasteQuantity');
                  {
                    const TRIOnsiteWasteQuantity = TRIOnsiteReleaseQuantity.OnsiteWasteQuantity;
                    {
                      const Quantity = TRIOnsiteWasteQuantity.Quantity;
                      if (Quantity.choice === 0) {
                        {
                          const chosen6 = Quantity.value;
                          builder = builder.ele('TRI:WasteQuantityMeasure');
                          {
                            const TRIWasteQuantityMeasure = chosen6.WasteQuantityMeasure;
                            builder.txt(formRDecimalToString(TRIWasteQuantityMeasure));
                          }
                          builder = builder.up();
                          if (chosen6.WasteQuantityCatastrophicMeasure !== null) {
                            builder = builder.ele('TRI:WasteQuantityCatastrophicMeasure');
                            {
                              const TRIWasteQuantityCatastrophicMeasure = chosen6.WasteQuantityCatastrophicMeasure;
                              builder.txt(formRDecimalToString(TRIWasteQuantityCatastrophicMeasure));
                            }
                            builder = builder.up();
                          }
                        }
                      }
                      if (Quantity.choice === 1) {
                        {
                          const chosen7 = Quantity.value;
                          builder = builder.ele('TRI:WasteQuantityRangeCode');
                          {
                            const TRIWasteQuantityRangeCode = chosen7.WasteQuantityRangeCode;
                            builder.txt(TRIWasteQuantityRangeCode);
                          }
                          builder = builder.up();
                          if (chosen7.WasteQuantityRangeNumericBasisValue !== null) {
                            builder = builder.ele('TRI:WasteQuantityRangeNumericBasisValue');
                            {
                              const TRIWasteQuantityRangeNumericBasisValue =
                                chosen7.WasteQuantityRangeNumericBasisValue;
                              builder.txt(formRDecimalToString(TRIWasteQuantityRangeNumericBasisValue));
                            }
                            builder = builder.up();
                          }
                          if (chosen7.WasteQuantityCatastrophicMeasure !== null) {
                            builder = builder.ele('TRI:WasteQuantityCatastrophicMeasure');
                            {
                              const TRIWasteQuantityCatastrophicMeasure = chosen7.WasteQuantityCatastrophicMeasure;
                              builder.txt(formRDecimalToString(TRIWasteQuantityCatastrophicMeasure));
                            }
                            builder = builder.up();
                          }
                        }
                      }
                      if (Quantity.choice === 'NA') {
                        {
                          const chosen8 = Quantity.value;
                          if (chosen8 !== null) {
                            builder = builder.ele('TRI:WasteQuantityNAIndicator');
                            {
                              const TRIWasteQuantityNAIndicator = chosen8;
                              builder.txt(String(TRIWasteQuantityNAIndicator satisfies boolean));
                            }
                            builder = builder.up();
                          }
                        }
                      }
                    }
                    {
                      const QuantityBasis = TRIOnsiteWasteQuantity.QuantityBasis;
                      if (QuantityBasis.choice === 'QuantityBasisEstimationCode') {
                        {
                          const chosen9 = QuantityBasis.value;
                          if (chosen9 !== null) {
                            builder = builder.ele('TRI:QuantityBasisEstimationCode');
                            {
                              const TRIQuantityBasisEstimationCode = chosen9;
                              builder.txt(TRIQuantityBasisEstimationCode);
                            }
                            builder = builder.up();
                          }
                        }
                      }
                      if (QuantityBasis.choice === 'NA') {
                        {
                          const chosen10 = QuantityBasis.value;
                          if (chosen10 !== null) {
                            builder = builder.ele('TRI:QuantityBasisEstimationNAIndicator');
                            {
                              const TRIQuantityBasisEstimationNAIndicator = chosen10;
                              builder.txt(String(TRIQuantityBasisEstimationNAIndicator satisfies boolean));
                            }
                            builder = builder.up();
                          }
                        }
                      }
                    }
                    if (TRIOnsiteWasteQuantity.ToxicEquivalencyIdentification !== null) {
                      builder = builder.ele('TRI:ToxicEquivalencyIdentification');
                      {
                        const TRIToxicEquivalencyIdentification = TRIOnsiteWasteQuantity.ToxicEquivalencyIdentification;
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency1Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency1Value');
                          {
                            const TRIToxicEquivalency1Value = TRIToxicEquivalencyIdentification.ToxicEquivalency1Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency1Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency2Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency2Value');
                          {
                            const TRIToxicEquivalency2Value = TRIToxicEquivalencyIdentification.ToxicEquivalency2Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency2Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency3Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency3Value');
                          {
                            const TRIToxicEquivalency3Value = TRIToxicEquivalencyIdentification.ToxicEquivalency3Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency3Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency4Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency4Value');
                          {
                            const TRIToxicEquivalency4Value = TRIToxicEquivalencyIdentification.ToxicEquivalency4Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency4Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency5Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency5Value');
                          {
                            const TRIToxicEquivalency5Value = TRIToxicEquivalencyIdentification.ToxicEquivalency5Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency5Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency6Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency6Value');
                          {
                            const TRIToxicEquivalency6Value = TRIToxicEquivalencyIdentification.ToxicEquivalency6Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency6Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency7Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency7Value');
                          {
                            const TRIToxicEquivalency7Value = TRIToxicEquivalencyIdentification.ToxicEquivalency7Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency7Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency8Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency8Value');
                          {
                            const TRIToxicEquivalency8Value = TRIToxicEquivalencyIdentification.ToxicEquivalency8Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency8Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency9Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency9Value');
                          {
                            const TRIToxicEquivalency9Value = TRIToxicEquivalencyIdentification.ToxicEquivalency9Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency9Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency10Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency10Value');
                          {
                            const TRIToxicEquivalency10Value =
                              TRIToxicEquivalencyIdentification.ToxicEquivalency10Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency10Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency11Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency11Value');
                          {
                            const TRIToxicEquivalency11Value =
                              TRIToxicEquivalencyIdentification.ToxicEquivalency11Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency11Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency12Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency12Value');
                          {
                            const TRIToxicEquivalency12Value =
                              TRIToxicEquivalencyIdentification.ToxicEquivalency12Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency12Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency13Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency13Value');
                          {
                            const TRIToxicEquivalency13Value =
                              TRIToxicEquivalencyIdentification.ToxicEquivalency13Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency13Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency14Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency14Value');
                          {
                            const TRIToxicEquivalency14Value =
                              TRIToxicEquivalencyIdentification.ToxicEquivalency14Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency14Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency15Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency15Value');
                          {
                            const TRIToxicEquivalency15Value =
                              TRIToxicEquivalencyIdentification.ToxicEquivalency15Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency15Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency16Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency16Value');
                          {
                            const TRIToxicEquivalency16Value =
                              TRIToxicEquivalencyIdentification.ToxicEquivalency16Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency16Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency17Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency17Value');
                          {
                            const TRIToxicEquivalency17Value =
                              TRIToxicEquivalencyIdentification.ToxicEquivalency17Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency17Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalencyNAIndicator !== null) {
                          builder = builder.ele('TRI:ToxicEquivalencyNAIndicator');
                          {
                            const TRIToxicEquivalencyNAIndicator =
                              TRIToxicEquivalencyIdentification.ToxicEquivalencyNAIndicator;
                            builder.txt(String(TRIToxicEquivalencyNAIndicator satisfies boolean));
                          }
                          builder = builder.up();
                        }
                      }
                      builder = builder.up();
                    }
                    if (TRIOnsiteWasteQuantity.NonProductionWasteQuantityMeasure !== null) {
                      builder = builder.ele('TRI:NonProductionWasteQuantityMeasure');
                      {
                        const TRINonProductionWasteQuantityMeasure =
                          TRIOnsiteWasteQuantity.NonProductionWasteQuantityMeasure;
                        builder.txt(formRDecimalToString(TRINonProductionWasteQuantityMeasure));
                      }
                      builder = builder.up();
                    }
                  }
                  builder = builder.up();
                }
                if (TRIOnsiteReleaseQuantity.WaterStream !== null) {
                  builder = builder.ele('TRI:WaterStream');
                  {
                    const TRIWaterStream = TRIOnsiteReleaseQuantity.WaterStream;
                    if (TRIWaterStream.WaterSequenceNumber !== null) {
                      builder = builder.ele('TRI:WaterSequenceNumber');
                      {
                        const TRIWaterSequenceNumber = TRIWaterStream.WaterSequenceNumber;
                        builder.txt(String(TRIWaterSequenceNumber satisfies number));
                      }
                      builder = builder.up();
                    }
                    if (TRIWaterStream.StreamName !== null) {
                      builder = builder.ele('TRI:StreamName');
                      {
                        const TRIStreamName = TRIWaterStream.StreamName;
                        builder.txt(TRIStreamName);
                      }
                      builder = builder.up();
                    }
                    if (TRIWaterStream.StreamReachCode !== null) {
                      builder = builder.ele('TRI:StreamReachCode');
                      {
                        const TRIStreamReachCode = TRIWaterStream.StreamReachCode;
                        builder.txt(TRIStreamReachCode);
                      }
                      builder = builder.up();
                    }
                    {
                      const StormWaterPercent = TRIWaterStream.StormWaterPercent;
                      if (StormWaterPercent !== null) {
                        if (StormWaterPercent.choice === 'ReleaseStormWaterPercent') {
                          {
                            const chosen11 = StormWaterPercent.value;
                            if (chosen11 !== null) {
                              builder = builder.ele('TRI:ReleaseStormWaterPercent');
                              {
                                const TRIReleaseStormWaterPercent = chosen11;
                                builder.txt(formRDecimalToString(TRIReleaseStormWaterPercent));
                              }
                              builder = builder.up();
                            }
                          }
                        }
                        if (StormWaterPercent.choice === 'NA') {
                          {
                            const chosen12 = StormWaterPercent.value;
                            if (chosen12 !== null) {
                              builder = builder.ele('TRI:ReleaseStormWaterNAIndicator');
                              {
                                const TRIReleaseStormWaterNAIndicator = chosen12;
                                builder.txt(String(TRIReleaseStormWaterNAIndicator satisfies boolean));
                              }
                              builder = builder.up();
                            }
                          }
                        }
                      }
                    }
                  }
                  builder = builder.up();
                }
              }
              builder = builder.up();
            }
            if (TRIReport.WasteRockManagedPileIndicator !== null) {
              builder = builder.ele('TRI:WasteRockManagedPileIndicator');
              {
                const TRIWasteRockManagedPileIndicator = TRIReport.WasteRockManagedPileIndicator;
                builder.txt(String(TRIWasteRockManagedPileIndicator satisfies boolean));
              }
              builder = builder.up();
            }
            if (TRIReport.WasteRockQuantity !== null) {
              builder = builder.ele('TRI:WasteRockQuantity');
              {
                const TRIWasteRockQuantity = TRIReport.WasteRockQuantity;
                builder.txt(formRDecimalToString(TRIWasteRockQuantity));
              }
              builder = builder.up();
            }
            for (const TRIReportItem of TRIReport.POTWWasteQuantity) {
              builder = builder.ele('TRI:POTWWasteQuantity');
              {
                const TRIPOTWWasteQuantity = TRIReportItem;
                if (TRIPOTWWasteQuantity.POTWSequenceNumber !== null) {
                  builder = builder.ele('TRI:POTWSequenceNumber');
                  {
                    const TRIPOTWSequenceNumber = TRIPOTWWasteQuantity.POTWSequenceNumber;
                    builder.txt(String(TRIPOTWSequenceNumber satisfies number));
                  }
                  builder = builder.up();
                }
                {
                  const WasteQuantity = TRIPOTWWasteQuantity.WasteQuantity;
                  if (WasteQuantity.choice === 0) {
                    {
                      const chosen13 = WasteQuantity.value;
                      builder = builder.ele('TRI:WasteQuantityMeasure');
                      {
                        const TRIWasteQuantityMeasure = chosen13.WasteQuantityMeasure;
                        builder.txt(formRDecimalToString(TRIWasteQuantityMeasure));
                      }
                      builder = builder.up();
                      if (chosen13.WasteQuantityCatastrophicMeasure !== null) {
                        builder = builder.ele('TRI:WasteQuantityCatastrophicMeasure');
                        {
                          const TRIWasteQuantityCatastrophicMeasure = chosen13.WasteQuantityCatastrophicMeasure;
                          builder.txt(formRDecimalToString(TRIWasteQuantityCatastrophicMeasure));
                        }
                        builder = builder.up();
                      }
                    }
                  }
                  if (WasteQuantity.choice === 1) {
                    {
                      const chosen14 = WasteQuantity.value;
                      builder = builder.ele('TRI:WasteQuantityRangeCode');
                      {
                        const TRIWasteQuantityRangeCode = chosen14.WasteQuantityRangeCode;
                        builder.txt(TRIWasteQuantityRangeCode);
                      }
                      builder = builder.up();
                      if (chosen14.WasteQuantityRangeNumericBasisValue !== null) {
                        builder = builder.ele('TRI:WasteQuantityRangeNumericBasisValue');
                        {
                          const TRIWasteQuantityRangeNumericBasisValue = chosen14.WasteQuantityRangeNumericBasisValue;
                          builder.txt(formRDecimalToString(TRIWasteQuantityRangeNumericBasisValue));
                        }
                        builder = builder.up();
                      }
                      if (chosen14.WasteQuantityCatastrophicMeasure !== null) {
                        builder = builder.ele('TRI:WasteQuantityCatastrophicMeasure');
                        {
                          const TRIWasteQuantityCatastrophicMeasure = chosen14.WasteQuantityCatastrophicMeasure;
                          builder.txt(formRDecimalToString(TRIWasteQuantityCatastrophicMeasure));
                        }
                        builder = builder.up();
                      }
                    }
                  }
                  if (WasteQuantity.choice === 'NA') {
                    {
                      const chosen15 = WasteQuantity.value;
                      if (chosen15 !== null) {
                        builder = builder.ele('TRI:WasteQuantityNAIndicator');
                        {
                          const TRIWasteQuantityNAIndicator = chosen15;
                          builder.txt(String(TRIWasteQuantityNAIndicator satisfies boolean));
                        }
                        builder = builder.up();
                      }
                    }
                  }
                }
                {
                  const QuantityBasisEstimation = TRIPOTWWasteQuantity.QuantityBasisEstimation;
                  if (QuantityBasisEstimation.choice === 'QuantityBasisEstimationCode') {
                    {
                      const chosen16 = QuantityBasisEstimation.value;
                      if (chosen16 !== null) {
                        builder = builder.ele('TRI:QuantityBasisEstimationCode');
                        {
                          const TRIQuantityBasisEstimationCode = chosen16;
                          builder.txt(TRIQuantityBasisEstimationCode);
                        }
                        builder = builder.up();
                      }
                    }
                  }
                  if (QuantityBasisEstimation.choice === 'NA') {
                    {
                      const chosen17 = QuantityBasisEstimation.value;
                      if (chosen17 !== null) {
                        builder = builder.ele('TRI:QuantityBasisEstimationNAIndicator');
                        {
                          const TRIQuantityBasisEstimationNAIndicator = chosen17;
                          builder.txt(String(TRIQuantityBasisEstimationNAIndicator satisfies boolean));
                        }
                        builder = builder.up();
                      }
                    }
                  }
                }
                if (TRIPOTWWasteQuantity.ToxicEquivalencyIdentification !== null) {
                  builder = builder.ele('TRI:ToxicEquivalencyIdentification');
                  {
                    const TRIToxicEquivalencyIdentification = TRIPOTWWasteQuantity.ToxicEquivalencyIdentification;
                    if (TRIToxicEquivalencyIdentification.ToxicEquivalency1Value !== null) {
                      builder = builder.ele('TRI:ToxicEquivalency1Value');
                      {
                        const TRIToxicEquivalency1Value = TRIToxicEquivalencyIdentification.ToxicEquivalency1Value;
                        builder.txt(formRDecimalToString(TRIToxicEquivalency1Value));
                      }
                      builder = builder.up();
                    }
                    if (TRIToxicEquivalencyIdentification.ToxicEquivalency2Value !== null) {
                      builder = builder.ele('TRI:ToxicEquivalency2Value');
                      {
                        const TRIToxicEquivalency2Value = TRIToxicEquivalencyIdentification.ToxicEquivalency2Value;
                        builder.txt(formRDecimalToString(TRIToxicEquivalency2Value));
                      }
                      builder = builder.up();
                    }
                    if (TRIToxicEquivalencyIdentification.ToxicEquivalency3Value !== null) {
                      builder = builder.ele('TRI:ToxicEquivalency3Value');
                      {
                        const TRIToxicEquivalency3Value = TRIToxicEquivalencyIdentification.ToxicEquivalency3Value;
                        builder.txt(formRDecimalToString(TRIToxicEquivalency3Value));
                      }
                      builder = builder.up();
                    }
                    if (TRIToxicEquivalencyIdentification.ToxicEquivalency4Value !== null) {
                      builder = builder.ele('TRI:ToxicEquivalency4Value');
                      {
                        const TRIToxicEquivalency4Value = TRIToxicEquivalencyIdentification.ToxicEquivalency4Value;
                        builder.txt(formRDecimalToString(TRIToxicEquivalency4Value));
                      }
                      builder = builder.up();
                    }
                    if (TRIToxicEquivalencyIdentification.ToxicEquivalency5Value !== null) {
                      builder = builder.ele('TRI:ToxicEquivalency5Value');
                      {
                        const TRIToxicEquivalency5Value = TRIToxicEquivalencyIdentification.ToxicEquivalency5Value;
                        builder.txt(formRDecimalToString(TRIToxicEquivalency5Value));
                      }
                      builder = builder.up();
                    }
                    if (TRIToxicEquivalencyIdentification.ToxicEquivalency6Value !== null) {
                      builder = builder.ele('TRI:ToxicEquivalency6Value');
                      {
                        const TRIToxicEquivalency6Value = TRIToxicEquivalencyIdentification.ToxicEquivalency6Value;
                        builder.txt(formRDecimalToString(TRIToxicEquivalency6Value));
                      }
                      builder = builder.up();
                    }
                    if (TRIToxicEquivalencyIdentification.ToxicEquivalency7Value !== null) {
                      builder = builder.ele('TRI:ToxicEquivalency7Value');
                      {
                        const TRIToxicEquivalency7Value = TRIToxicEquivalencyIdentification.ToxicEquivalency7Value;
                        builder.txt(formRDecimalToString(TRIToxicEquivalency7Value));
                      }
                      builder = builder.up();
                    }
                    if (TRIToxicEquivalencyIdentification.ToxicEquivalency8Value !== null) {
                      builder = builder.ele('TRI:ToxicEquivalency8Value');
                      {
                        const TRIToxicEquivalency8Value = TRIToxicEquivalencyIdentification.ToxicEquivalency8Value;
                        builder.txt(formRDecimalToString(TRIToxicEquivalency8Value));
                      }
                      builder = builder.up();
                    }
                    if (TRIToxicEquivalencyIdentification.ToxicEquivalency9Value !== null) {
                      builder = builder.ele('TRI:ToxicEquivalency9Value');
                      {
                        const TRIToxicEquivalency9Value = TRIToxicEquivalencyIdentification.ToxicEquivalency9Value;
                        builder.txt(formRDecimalToString(TRIToxicEquivalency9Value));
                      }
                      builder = builder.up();
                    }
                    if (TRIToxicEquivalencyIdentification.ToxicEquivalency10Value !== null) {
                      builder = builder.ele('TRI:ToxicEquivalency10Value');
                      {
                        const TRIToxicEquivalency10Value = TRIToxicEquivalencyIdentification.ToxicEquivalency10Value;
                        builder.txt(formRDecimalToString(TRIToxicEquivalency10Value));
                      }
                      builder = builder.up();
                    }
                    if (TRIToxicEquivalencyIdentification.ToxicEquivalency11Value !== null) {
                      builder = builder.ele('TRI:ToxicEquivalency11Value');
                      {
                        const TRIToxicEquivalency11Value = TRIToxicEquivalencyIdentification.ToxicEquivalency11Value;
                        builder.txt(formRDecimalToString(TRIToxicEquivalency11Value));
                      }
                      builder = builder.up();
                    }
                    if (TRIToxicEquivalencyIdentification.ToxicEquivalency12Value !== null) {
                      builder = builder.ele('TRI:ToxicEquivalency12Value');
                      {
                        const TRIToxicEquivalency12Value = TRIToxicEquivalencyIdentification.ToxicEquivalency12Value;
                        builder.txt(formRDecimalToString(TRIToxicEquivalency12Value));
                      }
                      builder = builder.up();
                    }
                    if (TRIToxicEquivalencyIdentification.ToxicEquivalency13Value !== null) {
                      builder = builder.ele('TRI:ToxicEquivalency13Value');
                      {
                        const TRIToxicEquivalency13Value = TRIToxicEquivalencyIdentification.ToxicEquivalency13Value;
                        builder.txt(formRDecimalToString(TRIToxicEquivalency13Value));
                      }
                      builder = builder.up();
                    }
                    if (TRIToxicEquivalencyIdentification.ToxicEquivalency14Value !== null) {
                      builder = builder.ele('TRI:ToxicEquivalency14Value');
                      {
                        const TRIToxicEquivalency14Value = TRIToxicEquivalencyIdentification.ToxicEquivalency14Value;
                        builder.txt(formRDecimalToString(TRIToxicEquivalency14Value));
                      }
                      builder = builder.up();
                    }
                    if (TRIToxicEquivalencyIdentification.ToxicEquivalency15Value !== null) {
                      builder = builder.ele('TRI:ToxicEquivalency15Value');
                      {
                        const TRIToxicEquivalency15Value = TRIToxicEquivalencyIdentification.ToxicEquivalency15Value;
                        builder.txt(formRDecimalToString(TRIToxicEquivalency15Value));
                      }
                      builder = builder.up();
                    }
                    if (TRIToxicEquivalencyIdentification.ToxicEquivalency16Value !== null) {
                      builder = builder.ele('TRI:ToxicEquivalency16Value');
                      {
                        const TRIToxicEquivalency16Value = TRIToxicEquivalencyIdentification.ToxicEquivalency16Value;
                        builder.txt(formRDecimalToString(TRIToxicEquivalency16Value));
                      }
                      builder = builder.up();
                    }
                    if (TRIToxicEquivalencyIdentification.ToxicEquivalency17Value !== null) {
                      builder = builder.ele('TRI:ToxicEquivalency17Value');
                      {
                        const TRIToxicEquivalency17Value = TRIToxicEquivalencyIdentification.ToxicEquivalency17Value;
                        builder.txt(formRDecimalToString(TRIToxicEquivalency17Value));
                      }
                      builder = builder.up();
                    }
                    if (TRIToxicEquivalencyIdentification.ToxicEquivalencyNAIndicator !== null) {
                      builder = builder.ele('TRI:ToxicEquivalencyNAIndicator');
                      {
                        const TRIToxicEquivalencyNAIndicator =
                          TRIToxicEquivalencyIdentification.ToxicEquivalencyNAIndicator;
                        builder.txt(String(TRIToxicEquivalencyNAIndicator satisfies boolean));
                      }
                      builder = builder.up();
                    }
                  }
                  builder = builder.up();
                }
                if (TRIPOTWWasteQuantity.QuantityDisposedLandfillPercentValue !== null) {
                  builder = builder.ele('TRI:QuantityDisposedLandfillPercentValue');
                  {
                    const TRIQuantityDisposedLandfillPercentValue =
                      TRIPOTWWasteQuantity.QuantityDisposedLandfillPercentValue;
                    builder.txt(formRDecimalToString(TRIQuantityDisposedLandfillPercentValue));
                  }
                  builder = builder.up();
                }
                if (TRIPOTWWasteQuantity.QuantityDisposedOtherPercentValue !== null) {
                  builder = builder.ele('TRI:QuantityDisposedOtherPercentValue');
                  {
                    const TRIQuantityDisposedOtherPercentValue = TRIPOTWWasteQuantity.QuantityDisposedOtherPercentValue;
                    builder.txt(formRDecimalToString(TRIQuantityDisposedOtherPercentValue));
                  }
                  builder = builder.up();
                }
                if (TRIPOTWWasteQuantity.QuantityTreatedPercentValue !== null) {
                  builder = builder.ele('TRI:QuantityTreatedPercentValue');
                  {
                    const TRIQuantityTreatedPercentValue = TRIPOTWWasteQuantity.QuantityTreatedPercentValue;
                    builder.txt(formRDecimalToString(TRIQuantityTreatedPercentValue));
                  }
                  builder = builder.up();
                }
                if (TRIPOTWWasteQuantity.POTWTransferTypeCode !== null) {
                  builder = builder.ele('TRI:POTWTransferTypeCode');
                  {
                    const TRIPOTWTransferTypeCode = TRIPOTWWasteQuantity.POTWTransferTypeCode;
                    builder.txt(TRIPOTWTransferTypeCode);
                  }
                  builder = builder.up();
                }
                if (TRIPOTWWasteQuantity.POTWTransferSequenceNumber !== null) {
                  builder = builder.ele('TRI:POTWTransferSequenceNumber');
                  {
                    const TRIPOTWTransferSequenceNumber = TRIPOTWWasteQuantity.POTWTransferSequenceNumber;
                    builder.txt(String(TRIPOTWTransferSequenceNumber satisfies number));
                  }
                  builder = builder.up();
                }
                if (TRIPOTWWasteQuantity.NonProductionWasteQuantityMeasure !== null) {
                  builder = builder.ele('TRI:NonProductionWasteQuantityMeasure');
                  {
                    const TRINonProductionWasteQuantityMeasure = TRIPOTWWasteQuantity.NonProductionWasteQuantityMeasure;
                    builder.txt(formRDecimalToString(TRINonProductionWasteQuantityMeasure));
                  }
                  builder = builder.up();
                }
              }
              builder = builder.up();
            }
            for (const TRIReportItem of TRIReport.TransferLocation) {
              builder = builder.ele('TRI:TransferLocation');
              {
                const TRITransferLocation = TRIReportItem;
                if (TRITransferLocation.TransferLocationSequenceNumber !== null) {
                  builder = builder.ele('TRI:TransferLocationSequenceNumber');
                  {
                    const TRITransferLocationSequenceNumber = TRITransferLocation.TransferLocationSequenceNumber;
                    builder.txt(String(TRITransferLocationSequenceNumber satisfies number));
                  }
                  builder = builder.up();
                }
                if (TRITransferLocation.POTWIndicator !== null) {
                  builder = builder.ele('TRI:POTWIndicator');
                  {
                    const TRIPOTWIndicator = TRITransferLocation.POTWIndicator;
                    builder.txt(String(TRIPOTWIndicator satisfies boolean));
                  }
                  builder = builder.up();
                }
                if (TRITransferLocation.FacilitySiteName !== null) {
                  builder = builder.ele('sc:FacilitySiteName');
                  {
                    const SCFacilitySiteName = TRITransferLocation.FacilitySiteName;
                    builder.txt(SCFacilitySiteName);
                  }
                  builder = builder.up();
                }
                if (TRITransferLocation.LocationAddress !== null) {
                  builder = builder.ele('sc:LocationAddress');
                  {
                    const SCLocationAddress = TRITransferLocation.LocationAddress;
                    if (SCLocationAddress.LocationAddressText !== null) {
                      builder = builder.ele('sc:LocationAddressText');
                      {
                        const SCLocationAddressText = SCLocationAddress.LocationAddressText;
                        builder.txt(SCLocationAddressText);
                      }
                      builder = builder.up();
                    }
                    if (SCLocationAddress.SupplementalLocationText !== null) {
                      builder = builder.ele('sc:SupplementalLocationText');
                      {
                        const SCSupplementalLocationText = SCLocationAddress.SupplementalLocationText;
                        builder.txt(SCSupplementalLocationText);
                      }
                      builder = builder.up();
                    }
                    if (SCLocationAddress.LocalityName !== null) {
                      builder = builder.ele('sc:LocalityName');
                      {
                        const SCLocalityName = SCLocationAddress.LocalityName;
                        builder.txt(SCLocalityName);
                      }
                      builder = builder.up();
                    }
                    if (SCLocationAddress.StateIdentity !== null) {
                      builder = builder.ele('sc:StateIdentity');
                      {
                        const SCStateIdentity = SCLocationAddress.StateIdentity;
                        if (SCStateIdentity.StateCode !== null) {
                          builder = builder.ele('sc:StateCode');
                          {
                            const SCStateCode = SCStateIdentity.StateCode;
                            builder.txt(SCStateCode);
                          }
                          builder = builder.up();
                        }
                        if (SCStateIdentity.StateCodeListIdentifier !== null) {
                          builder = builder.ele('sc:StateCodeListIdentifier');
                          {
                            const SCStateCodeListIdentifier = SCStateIdentity.StateCodeListIdentifier;
                            builder.txt(SCStateCodeListIdentifier.value);
                            if (SCStateCodeListIdentifier.attrs.CodeListVersionIdentifier !== undefined) {
                              builder.att(
                                'CodeListVersionIdentifier',
                                SCStateCodeListIdentifier.attrs.CodeListVersionIdentifier,
                              );
                            }
                            if (SCStateCodeListIdentifier.attrs.CodeListVersionAgencyIdentifier !== undefined) {
                              builder.att(
                                'CodeListVersionAgencyIdentifier',
                                SCStateCodeListIdentifier.attrs.CodeListVersionAgencyIdentifier,
                              );
                            }
                          }
                          builder = builder.up();
                        }
                        if (SCStateIdentity.StateName !== null) {
                          builder = builder.ele('sc:StateName');
                          {
                            const SCStateName = SCStateIdentity.StateName;
                            builder.txt(SCStateName);
                          }
                          builder = builder.up();
                        }
                      }
                      builder = builder.up();
                    }
                    if (SCLocationAddress.AddressPostalCode !== null) {
                      builder = builder.ele('sc:AddressPostalCode');
                      {
                        const SCAddressPostalCode = SCLocationAddress.AddressPostalCode;
                        builder.txt(SCAddressPostalCode.value);
                        if (SCAddressPostalCode.attrs.AddressPostalCodeContext !== undefined) {
                          builder.att('AddressPostalCodeContext', SCAddressPostalCode.attrs.AddressPostalCodeContext);
                        }
                      }
                      builder = builder.up();
                    }
                    if (SCLocationAddress.CountryIdentity !== null) {
                      builder = builder.ele('sc:CountryIdentity');
                      {
                        const SCCountryIdentity = SCLocationAddress.CountryIdentity;
                        if (SCCountryIdentity.CountryCode !== null) {
                          builder = builder.ele('sc:CountryCode');
                          {
                            const SCCountryCode = SCCountryIdentity.CountryCode;
                            builder.txt(SCCountryCode);
                          }
                          builder = builder.up();
                        }
                        if (SCCountryIdentity.CountryCodeListIdentifier !== null) {
                          builder = builder.ele('sc:CountryCodeListIdentifier');
                          {
                            const SCCountryCodeListIdentifier = SCCountryIdentity.CountryCodeListIdentifier;
                            builder.txt(SCCountryCodeListIdentifier.value);
                            if (SCCountryCodeListIdentifier.attrs.CodeListVersionIdentifier !== undefined) {
                              builder.att(
                                'CodeListVersionIdentifier',
                                SCCountryCodeListIdentifier.attrs.CodeListVersionIdentifier,
                              );
                            }
                            if (SCCountryCodeListIdentifier.attrs.CodeListVersionAgencyIdentifier !== undefined) {
                              builder.att(
                                'CodeListVersionAgencyIdentifier',
                                SCCountryCodeListIdentifier.attrs.CodeListVersionAgencyIdentifier,
                              );
                            }
                          }
                          builder = builder.up();
                        }
                        if (SCCountryIdentity.CountryName !== null) {
                          builder = builder.ele('sc:CountryName');
                          {
                            const SCCountryName = SCCountryIdentity.CountryName;
                            builder.txt(SCCountryName);
                          }
                          builder = builder.up();
                        }
                      }
                      builder = builder.up();
                    }
                    if (SCLocationAddress.CountyIdentity !== null) {
                      builder = builder.ele('sc:CountyIdentity');
                      {
                        const SCCountyIdentity = SCLocationAddress.CountyIdentity;
                        if (SCCountyIdentity.CountyCode !== null) {
                          builder = builder.ele('sc:CountyCode');
                          {
                            const SCCountyCode = SCCountyIdentity.CountyCode;
                            builder.txt(SCCountyCode);
                          }
                          builder = builder.up();
                        }
                        if (SCCountyIdentity.CountyCodeListIdentifier !== null) {
                          builder = builder.ele('sc:CountyCodeListIdentifier');
                          {
                            const SCCountyCodeListIdentifier = SCCountyIdentity.CountyCodeListIdentifier;
                            builder.txt(SCCountyCodeListIdentifier.value);
                            if (SCCountyCodeListIdentifier.attrs.CodeListVersionIdentifier !== undefined) {
                              builder.att(
                                'CodeListVersionIdentifier',
                                SCCountyCodeListIdentifier.attrs.CodeListVersionIdentifier,
                              );
                            }
                            if (SCCountyCodeListIdentifier.attrs.CodeListVersionAgencyIdentifier !== undefined) {
                              builder.att(
                                'CodeListVersionAgencyIdentifier',
                                SCCountyCodeListIdentifier.attrs.CodeListVersionAgencyIdentifier,
                              );
                            }
                          }
                          builder = builder.up();
                        }
                        if (SCCountyIdentity.CountyName !== null) {
                          builder = builder.ele('sc:CountyName');
                          {
                            const SCCountyName = SCCountyIdentity.CountyName;
                            builder.txt(SCCountyName);
                          }
                          builder = builder.up();
                        }
                      }
                      builder = builder.up();
                    }
                    if (SCLocationAddress.TribalIdentity !== null) {
                      builder = builder.ele('sc:TribalIdentity');
                      {
                        const SCTribalIdentity = SCLocationAddress.TribalIdentity;
                        if (SCTribalIdentity.TribalCode !== null) {
                          builder = builder.ele('sc:TribalCode');
                          {
                            const SCTribalCode = SCTribalIdentity.TribalCode;
                            builder.txt(SCTribalCode);
                          }
                          builder = builder.up();
                        }
                        if (SCTribalIdentity.TribalCodeListIdentifier !== null) {
                          builder = builder.ele('sc:TribalCodeListIdentifier');
                          {
                            const SCTribalCodeListIdentifier = SCTribalIdentity.TribalCodeListIdentifier;
                            builder.txt(SCTribalCodeListIdentifier.value);
                            if (SCTribalCodeListIdentifier.attrs.CodeListVersionIdentifier !== undefined) {
                              builder.att(
                                'CodeListVersionIdentifier',
                                SCTribalCodeListIdentifier.attrs.CodeListVersionIdentifier,
                              );
                            }
                            if (SCTribalCodeListIdentifier.attrs.CodeListVersionAgencyIdentifier !== undefined) {
                              builder.att(
                                'CodeListVersionAgencyIdentifier',
                                SCTribalCodeListIdentifier.attrs.CodeListVersionAgencyIdentifier,
                              );
                            }
                          }
                          builder = builder.up();
                        }
                        if (SCTribalIdentity.TribalName !== null) {
                          builder = builder.ele('sc:TribalName');
                          {
                            const SCTribalName = SCTribalIdentity.TribalName;
                            builder.txt(SCTribalName);
                          }
                          builder = builder.up();
                        }
                      }
                      builder = builder.up();
                    }
                    if (SCLocationAddress.TribalLandName !== null) {
                      builder = builder.ele('sc:TribalLandName');
                      {
                        const SCTribalLandName = SCLocationAddress.TribalLandName;
                        builder.txt(SCTribalLandName);
                      }
                      builder = builder.up();
                    }
                    if (SCLocationAddress.TribalLandIndicator !== null) {
                      builder = builder.ele('sc:TribalLandIndicator');
                      {
                        const SCTribalLandIndicator = SCLocationAddress.TribalLandIndicator;
                        builder.txt(SCTribalLandIndicator);
                      }
                      builder = builder.up();
                    }
                    if (SCLocationAddress.LocationDescriptionText !== null) {
                      builder = builder.ele('sc:LocationDescriptionText');
                      {
                        const SCLocationDescriptionText = SCLocationAddress.LocationDescriptionText;
                        builder.txt(SCLocationDescriptionText);
                      }
                      builder = builder.up();
                    }
                  }
                  builder = builder.up();
                }
                if (TRITransferLocation.ControlledLocationIndicator !== null) {
                  builder = builder.ele('TRI:ControlledLocationIndicator');
                  {
                    const TRIControlledLocationIndicator = TRITransferLocation.ControlledLocationIndicator;
                    builder.txt(String(TRIControlledLocationIndicator satisfies boolean));
                  }
                  builder = builder.up();
                }
                if (TRITransferLocation.RCRAIdentificationNumber !== null) {
                  builder = builder.ele('TRI:RCRAIdentificationNumber');
                  {
                    const TRIRCRAIdentificationNumber = TRITransferLocation.RCRAIdentificationNumber;
                    builder.txt(TRIRCRAIdentificationNumber);
                  }
                  builder = builder.up();
                }
                if (TRITransferLocation.EPARegistryIdentification !== null) {
                  builder = builder.ele('TRI:EPARegistryIdentification');
                  {
                    const TRIEPARegistryIdentification = TRITransferLocation.EPARegistryIdentification;
                    builder.txt(TRIEPARegistryIdentification);
                  }
                  builder = builder.up();
                }
                for (const TRITransferLocationItem of TRITransferLocation.TransferQuantity) {
                  builder = builder.ele('TRI:TransferQuantity');
                  {
                    const TRITransferQuantity = TRITransferLocationItem;
                    if (TRITransferQuantity.TransferSequenceNumber !== null) {
                      builder = builder.ele('TRI:TransferSequenceNumber');
                      {
                        const TRITransferSequenceNumber = TRITransferQuantity.TransferSequenceNumber;
                        builder.txt(String(TRITransferSequenceNumber satisfies number));
                      }
                      builder = builder.up();
                    }
                    if (TRITransferQuantity.TransferWasteQuantity !== null) {
                      builder = builder.ele('TRI:TransferWasteQuantity');
                      {
                        const TRITransferWasteQuantity = TRITransferQuantity.TransferWasteQuantity;
                        {
                          const Quantity = TRITransferWasteQuantity.Quantity;
                          if (Quantity.choice === 0) {
                            {
                              const chosen18 = Quantity.value;
                              builder = builder.ele('TRI:WasteQuantityMeasure');
                              {
                                const TRIWasteQuantityMeasure = chosen18.WasteQuantityMeasure;
                                builder.txt(formRDecimalToString(TRIWasteQuantityMeasure));
                              }
                              builder = builder.up();
                              if (chosen18.WasteQuantityCatastrophicMeasure !== null) {
                                builder = builder.ele('TRI:WasteQuantityCatastrophicMeasure');
                                {
                                  const TRIWasteQuantityCatastrophicMeasure = chosen18.WasteQuantityCatastrophicMeasure;
                                  builder.txt(formRDecimalToString(TRIWasteQuantityCatastrophicMeasure));
                                }
                                builder = builder.up();
                              }
                            }
                          }
                          if (Quantity.choice === 1) {
                            {
                              const chosen19 = Quantity.value;
                              builder = builder.ele('TRI:WasteQuantityRangeCode');
                              {
                                const TRIWasteQuantityRangeCode = chosen19.WasteQuantityRangeCode;
                                builder.txt(TRIWasteQuantityRangeCode);
                              }
                              builder = builder.up();
                              if (chosen19.WasteQuantityRangeNumericBasisValue !== null) {
                                builder = builder.ele('TRI:WasteQuantityRangeNumericBasisValue');
                                {
                                  const TRIWasteQuantityRangeNumericBasisValue =
                                    chosen19.WasteQuantityRangeNumericBasisValue;
                                  builder.txt(formRDecimalToString(TRIWasteQuantityRangeNumericBasisValue));
                                }
                                builder = builder.up();
                              }
                              if (chosen19.WasteQuantityCatastrophicMeasure !== null) {
                                builder = builder.ele('TRI:WasteQuantityCatastrophicMeasure');
                                {
                                  const TRIWasteQuantityCatastrophicMeasure = chosen19.WasteQuantityCatastrophicMeasure;
                                  builder.txt(formRDecimalToString(TRIWasteQuantityCatastrophicMeasure));
                                }
                                builder = builder.up();
                              }
                            }
                          }
                          if (Quantity.choice === 'NA') {
                            {
                              const chosen20 = Quantity.value;
                              if (chosen20 !== null) {
                                builder = builder.ele('TRI:WasteQuantityNAIndicator');
                                {
                                  const TRIWasteQuantityNAIndicator = chosen20;
                                  builder.txt(String(TRIWasteQuantityNAIndicator satisfies boolean));
                                }
                                builder = builder.up();
                              }
                            }
                          }
                        }
                        {
                          const QuantityBasis = TRITransferWasteQuantity.QuantityBasis;
                          if (QuantityBasis.choice === 'QuantityBasisEstimationCode') {
                            {
                              const chosen21 = QuantityBasis.value;
                              if (chosen21 !== null) {
                                builder = builder.ele('TRI:QuantityBasisEstimationCode');
                                {
                                  const TRIQuantityBasisEstimationCode = chosen21;
                                  builder.txt(TRIQuantityBasisEstimationCode);
                                }
                                builder = builder.up();
                              }
                            }
                          }
                          if (QuantityBasis.choice === 'NA') {
                            {
                              const chosen22 = QuantityBasis.value;
                              if (chosen22 !== null) {
                                builder = builder.ele('TRI:QuantityBasisEstimationNAIndicator');
                                {
                                  const TRIQuantityBasisEstimationNAIndicator = chosen22;
                                  builder.txt(String(TRIQuantityBasisEstimationNAIndicator satisfies boolean));
                                }
                                builder = builder.up();
                              }
                            }
                          }
                        }
                        if (TRITransferWasteQuantity.ToxicEquivalencyIdentification !== null) {
                          builder = builder.ele('TRI:ToxicEquivalencyIdentification');
                          {
                            const TRIToxicEquivalencyIdentification =
                              TRITransferWasteQuantity.ToxicEquivalencyIdentification;
                            if (TRIToxicEquivalencyIdentification.ToxicEquivalency1Value !== null) {
                              builder = builder.ele('TRI:ToxicEquivalency1Value');
                              {
                                const TRIToxicEquivalency1Value =
                                  TRIToxicEquivalencyIdentification.ToxicEquivalency1Value;
                                builder.txt(formRDecimalToString(TRIToxicEquivalency1Value));
                              }
                              builder = builder.up();
                            }
                            if (TRIToxicEquivalencyIdentification.ToxicEquivalency2Value !== null) {
                              builder = builder.ele('TRI:ToxicEquivalency2Value');
                              {
                                const TRIToxicEquivalency2Value =
                                  TRIToxicEquivalencyIdentification.ToxicEquivalency2Value;
                                builder.txt(formRDecimalToString(TRIToxicEquivalency2Value));
                              }
                              builder = builder.up();
                            }
                            if (TRIToxicEquivalencyIdentification.ToxicEquivalency3Value !== null) {
                              builder = builder.ele('TRI:ToxicEquivalency3Value');
                              {
                                const TRIToxicEquivalency3Value =
                                  TRIToxicEquivalencyIdentification.ToxicEquivalency3Value;
                                builder.txt(formRDecimalToString(TRIToxicEquivalency3Value));
                              }
                              builder = builder.up();
                            }
                            if (TRIToxicEquivalencyIdentification.ToxicEquivalency4Value !== null) {
                              builder = builder.ele('TRI:ToxicEquivalency4Value');
                              {
                                const TRIToxicEquivalency4Value =
                                  TRIToxicEquivalencyIdentification.ToxicEquivalency4Value;
                                builder.txt(formRDecimalToString(TRIToxicEquivalency4Value));
                              }
                              builder = builder.up();
                            }
                            if (TRIToxicEquivalencyIdentification.ToxicEquivalency5Value !== null) {
                              builder = builder.ele('TRI:ToxicEquivalency5Value');
                              {
                                const TRIToxicEquivalency5Value =
                                  TRIToxicEquivalencyIdentification.ToxicEquivalency5Value;
                                builder.txt(formRDecimalToString(TRIToxicEquivalency5Value));
                              }
                              builder = builder.up();
                            }
                            if (TRIToxicEquivalencyIdentification.ToxicEquivalency6Value !== null) {
                              builder = builder.ele('TRI:ToxicEquivalency6Value');
                              {
                                const TRIToxicEquivalency6Value =
                                  TRIToxicEquivalencyIdentification.ToxicEquivalency6Value;
                                builder.txt(formRDecimalToString(TRIToxicEquivalency6Value));
                              }
                              builder = builder.up();
                            }
                            if (TRIToxicEquivalencyIdentification.ToxicEquivalency7Value !== null) {
                              builder = builder.ele('TRI:ToxicEquivalency7Value');
                              {
                                const TRIToxicEquivalency7Value =
                                  TRIToxicEquivalencyIdentification.ToxicEquivalency7Value;
                                builder.txt(formRDecimalToString(TRIToxicEquivalency7Value));
                              }
                              builder = builder.up();
                            }
                            if (TRIToxicEquivalencyIdentification.ToxicEquivalency8Value !== null) {
                              builder = builder.ele('TRI:ToxicEquivalency8Value');
                              {
                                const TRIToxicEquivalency8Value =
                                  TRIToxicEquivalencyIdentification.ToxicEquivalency8Value;
                                builder.txt(formRDecimalToString(TRIToxicEquivalency8Value));
                              }
                              builder = builder.up();
                            }
                            if (TRIToxicEquivalencyIdentification.ToxicEquivalency9Value !== null) {
                              builder = builder.ele('TRI:ToxicEquivalency9Value');
                              {
                                const TRIToxicEquivalency9Value =
                                  TRIToxicEquivalencyIdentification.ToxicEquivalency9Value;
                                builder.txt(formRDecimalToString(TRIToxicEquivalency9Value));
                              }
                              builder = builder.up();
                            }
                            if (TRIToxicEquivalencyIdentification.ToxicEquivalency10Value !== null) {
                              builder = builder.ele('TRI:ToxicEquivalency10Value');
                              {
                                const TRIToxicEquivalency10Value =
                                  TRIToxicEquivalencyIdentification.ToxicEquivalency10Value;
                                builder.txt(formRDecimalToString(TRIToxicEquivalency10Value));
                              }
                              builder = builder.up();
                            }
                            if (TRIToxicEquivalencyIdentification.ToxicEquivalency11Value !== null) {
                              builder = builder.ele('TRI:ToxicEquivalency11Value');
                              {
                                const TRIToxicEquivalency11Value =
                                  TRIToxicEquivalencyIdentification.ToxicEquivalency11Value;
                                builder.txt(formRDecimalToString(TRIToxicEquivalency11Value));
                              }
                              builder = builder.up();
                            }
                            if (TRIToxicEquivalencyIdentification.ToxicEquivalency12Value !== null) {
                              builder = builder.ele('TRI:ToxicEquivalency12Value');
                              {
                                const TRIToxicEquivalency12Value =
                                  TRIToxicEquivalencyIdentification.ToxicEquivalency12Value;
                                builder.txt(formRDecimalToString(TRIToxicEquivalency12Value));
                              }
                              builder = builder.up();
                            }
                            if (TRIToxicEquivalencyIdentification.ToxicEquivalency13Value !== null) {
                              builder = builder.ele('TRI:ToxicEquivalency13Value');
                              {
                                const TRIToxicEquivalency13Value =
                                  TRIToxicEquivalencyIdentification.ToxicEquivalency13Value;
                                builder.txt(formRDecimalToString(TRIToxicEquivalency13Value));
                              }
                              builder = builder.up();
                            }
                            if (TRIToxicEquivalencyIdentification.ToxicEquivalency14Value !== null) {
                              builder = builder.ele('TRI:ToxicEquivalency14Value');
                              {
                                const TRIToxicEquivalency14Value =
                                  TRIToxicEquivalencyIdentification.ToxicEquivalency14Value;
                                builder.txt(formRDecimalToString(TRIToxicEquivalency14Value));
                              }
                              builder = builder.up();
                            }
                            if (TRIToxicEquivalencyIdentification.ToxicEquivalency15Value !== null) {
                              builder = builder.ele('TRI:ToxicEquivalency15Value');
                              {
                                const TRIToxicEquivalency15Value =
                                  TRIToxicEquivalencyIdentification.ToxicEquivalency15Value;
                                builder.txt(formRDecimalToString(TRIToxicEquivalency15Value));
                              }
                              builder = builder.up();
                            }
                            if (TRIToxicEquivalencyIdentification.ToxicEquivalency16Value !== null) {
                              builder = builder.ele('TRI:ToxicEquivalency16Value');
                              {
                                const TRIToxicEquivalency16Value =
                                  TRIToxicEquivalencyIdentification.ToxicEquivalency16Value;
                                builder.txt(formRDecimalToString(TRIToxicEquivalency16Value));
                              }
                              builder = builder.up();
                            }
                            if (TRIToxicEquivalencyIdentification.ToxicEquivalency17Value !== null) {
                              builder = builder.ele('TRI:ToxicEquivalency17Value');
                              {
                                const TRIToxicEquivalency17Value =
                                  TRIToxicEquivalencyIdentification.ToxicEquivalency17Value;
                                builder.txt(formRDecimalToString(TRIToxicEquivalency17Value));
                              }
                              builder = builder.up();
                            }
                            if (TRIToxicEquivalencyIdentification.ToxicEquivalencyNAIndicator !== null) {
                              builder = builder.ele('TRI:ToxicEquivalencyNAIndicator');
                              {
                                const TRIToxicEquivalencyNAIndicator =
                                  TRIToxicEquivalencyIdentification.ToxicEquivalencyNAIndicator;
                                builder.txt(String(TRIToxicEquivalencyNAIndicator satisfies boolean));
                              }
                              builder = builder.up();
                            }
                          }
                          builder = builder.up();
                        }
                        if (TRITransferWasteQuantity.NonProductionWasteQuantityMeasure !== null) {
                          builder = builder.ele('TRI:NonProductionWasteQuantityMeasure');
                          {
                            const TRINonProductionWasteQuantityMeasure =
                              TRITransferWasteQuantity.NonProductionWasteQuantityMeasure;
                            builder.txt(formRDecimalToString(TRINonProductionWasteQuantityMeasure));
                          }
                          builder = builder.up();
                        }
                      }
                      builder = builder.up();
                    }
                    if (TRITransferQuantity.WasteManagementTypeCode !== null) {
                      builder = builder.ele('TRI:WasteManagementTypeCode');
                      {
                        const TRIWasteManagementTypeCode = TRITransferQuantity.WasteManagementTypeCode;
                        builder.txt(TRIWasteManagementTypeCode);
                      }
                      builder = builder.up();
                    }
                  }
                  builder = builder.up();
                }
              }
              builder = builder.up();
            }
            {
              const WasteTreatment = TRIReport.WasteTreatment;
              if (WasteTreatment !== null) {
                if (WasteTreatment.choice === 'NA') {
                  {
                    const chosen23 = WasteTreatment.value;
                    if (chosen23 !== null) {
                      builder = builder.ele('TRI:WasteTreatmentNAIndicator');
                      {
                        const TRIWasteTreatmentNAIndicator = chosen23;
                        builder.txt(String(TRIWasteTreatmentNAIndicator satisfies boolean));
                      }
                      builder = builder.up();
                    }
                  }
                }
                if (WasteTreatment.choice === 'WasteTreatmentDetails') {
                  {
                    const chosen24 = WasteTreatment.value;
                    for (const chosen24Item of chosen24) {
                      builder = builder.ele('TRI:WasteTreatmentDetails');
                      {
                        const TRIWasteTreatmentDetails = chosen24Item;
                        if (TRIWasteTreatmentDetails.WasteStreamSequenceNumber !== null) {
                          builder = builder.ele('TRI:WasteStreamSequenceNumber');
                          {
                            const TRIWasteStreamSequenceNumber = TRIWasteTreatmentDetails.WasteStreamSequenceNumber;
                            builder.txt(String(TRIWasteStreamSequenceNumber satisfies number));
                          }
                          builder = builder.up();
                        }
                        builder = builder.ele('TRI:WasteStreamTypeCode');
                        {
                          const TRIWasteStreamTypeCode = TRIWasteTreatmentDetails.WasteStreamTypeCode;
                          builder.txt(TRIWasteStreamTypeCode);
                        }
                        builder = builder.up();
                        for (const TRIWasteTreatmentDetailsItem of TRIWasteTreatmentDetails.WasteTreatmentMethod) {
                          builder = builder.ele('TRI:WasteTreatmentMethod');
                          {
                            const TRIWasteTreatmentMethod = TRIWasteTreatmentDetailsItem;
                            if (TRIWasteTreatmentMethod.WasteTreatmentSequenceNumber !== null) {
                              builder = builder.ele('TRI:WasteTreatmentSequenceNumber');
                              {
                                const TRIWasteTreatmentSequenceNumber =
                                  TRIWasteTreatmentMethod.WasteTreatmentSequenceNumber;
                                builder.txt(String(TRIWasteTreatmentSequenceNumber satisfies number));
                              }
                              builder = builder.up();
                            }
                            if (TRIWasteTreatmentMethod.WasteTreatmentMethodCode !== null) {
                              builder = builder.ele('TRI:WasteTreatmentMethodCode');
                              {
                                const TRIWasteTreatmentMethodCode = TRIWasteTreatmentMethod.WasteTreatmentMethodCode;
                                builder.txt(TRIWasteTreatmentMethodCode);
                              }
                              builder = builder.up();
                            }
                          }
                          builder = builder.up();
                        }
                        if (TRIWasteTreatmentDetails.InfluentConcentrationRangeCode !== null) {
                          builder = builder.ele('TRI:InfluentConcentrationRangeCode');
                          {
                            const TRIInfluentConcentrationRangeCode =
                              TRIWasteTreatmentDetails.InfluentConcentrationRangeCode;
                            builder.txt(TRIInfluentConcentrationRangeCode);
                          }
                          builder = builder.up();
                        }
                        {
                          const TreatmentEfficiency = TRIWasteTreatmentDetails.TreatmentEfficiency;
                          if (TreatmentEfficiency.choice === 'TreatmentEfficiencyEstimatePercent') {
                            {
                              const chosen25 = TreatmentEfficiency.value;
                              if (chosen25 !== null) {
                                builder = builder.ele('TRI:TreatmentEfficiencyEstimatePercent');
                                {
                                  const TRITreatmentEfficiencyEstimatePercent = chosen25;
                                  builder.txt(formRDecimalToString(TRITreatmentEfficiencyEstimatePercent));
                                }
                                builder = builder.up();
                              }
                            }
                          }
                          if (TreatmentEfficiency.choice === 'TreatmentEfficiencyRangeCode') {
                            {
                              const chosen26 = TreatmentEfficiency.value;
                              if (chosen26 !== null) {
                                builder = builder.ele('TRI:TreatmentEfficiencyRangeCode');
                                {
                                  const TRITreatmentEfficiencyRangeCode = chosen26;
                                  builder.txt(TRITreatmentEfficiencyRangeCode);
                                }
                                builder = builder.up();
                              }
                            }
                          }
                          if (TreatmentEfficiency.choice === 'NA') {
                            {
                              const chosen27 = TreatmentEfficiency.value;
                              if (chosen27 !== null) {
                                builder = builder.ele('TRI:TreatmentEfficiencyNAIndicator');
                                {
                                  const TRITreatmentEfficiencyNAIndicator = chosen27;
                                  builder.txt(String(TRITreatmentEfficiencyNAIndicator satisfies boolean));
                                }
                                builder = builder.up();
                              }
                            }
                          }
                        }
                        if (TRIWasteTreatmentDetails.OperatingDataIndicator !== null) {
                          builder = builder.ele('TRI:OperatingDataIndicator');
                          {
                            const TRIOperatingDataIndicator = TRIWasteTreatmentDetails.OperatingDataIndicator;
                            builder.txt(String(TRIOperatingDataIndicator satisfies boolean));
                          }
                          builder = builder.up();
                        }
                      }
                      builder = builder.up();
                    }
                  }
                }
              }
            }
            if (TRIReport.OnsiteRecoveryProcess !== null) {
              builder = builder.ele('TRI:OnsiteRecoveryProcess');
              {
                const TRIOnsiteRecoveryProcess = TRIReport.OnsiteRecoveryProcess;
                if (TRIOnsiteRecoveryProcess.choice === 'EnergyRecoveryMethodCode') {
                  {
                    const chosen28 = TRIOnsiteRecoveryProcess.value;
                    for (const chosen28Item of chosen28) {
                      builder = builder.ele('TRI:EnergyRecoveryMethodCode');
                      {
                        const TRIEnergyRecoveryMethodCode = chosen28Item;
                        builder.txt(TRIEnergyRecoveryMethodCode);
                      }
                      builder = builder.up();
                    }
                  }
                }
                if (TRIOnsiteRecoveryProcess.choice === 'NA') {
                  {
                    const chosen29 = TRIOnsiteRecoveryProcess.value;
                    if (chosen29 !== null) {
                      builder = builder.ele('TRI:EnergyRecoveryNAIndicator');
                      {
                        const TRIEnergyRecoveryNAIndicator = chosen29;
                        builder.txt(String(TRIEnergyRecoveryNAIndicator satisfies boolean));
                      }
                      builder = builder.up();
                    }
                  }
                }
              }
              builder = builder.up();
            }
            if (TRIReport.OnsiteRecyclingProcess !== null) {
              builder = builder.ele('TRI:OnsiteRecyclingProcess');
              {
                const TRIOnsiteRecyclingProcess = TRIReport.OnsiteRecyclingProcess;
                if (TRIOnsiteRecyclingProcess.choice === 'NA') {
                  {
                    const chosen30 = TRIOnsiteRecyclingProcess.value;
                    if (chosen30 !== null) {
                      builder = builder.ele('TRI:OnsiteRecyclingNAIndicator');
                      {
                        const TRIOnsiteRecyclingNAIndicator = chosen30;
                        builder.txt(String(TRIOnsiteRecyclingNAIndicator satisfies boolean));
                      }
                      builder = builder.up();
                    }
                  }
                }
                if (TRIOnsiteRecyclingProcess.choice === 'OnsiteRecyclingMethodCode') {
                  {
                    const chosen31 = TRIOnsiteRecyclingProcess.value;
                    for (const chosen31Item of chosen31) {
                      builder = builder.ele('TRI:OnsiteRecyclingMethodCode');
                      {
                        const TRIOnsiteRecyclingMethodCode = chosen31Item;
                        builder.txt(TRIOnsiteRecyclingMethodCode);
                      }
                      builder = builder.up();
                    }
                  }
                }
              }
              builder = builder.up();
            }
            if (TRIReport.SourceReductionQuantity !== null) {
              builder = builder.ele('TRI:SourceReductionQuantity');
              {
                const TRISourceReductionQuantity = TRIReport.SourceReductionQuantity;
                for (const TRISourceReductionQuantityItem of TRISourceReductionQuantity.OnsiteUICDisposalQuantity) {
                  builder = builder.ele('TRI:OnsiteUICDisposalQuantity');
                  {
                    const TRIOnsiteUICDisposalQuantity = TRISourceReductionQuantityItem;
                    if (TRIOnsiteUICDisposalQuantity.YearOffsetMeasure !== null) {
                      builder = builder.ele('TRI:YearOffsetMeasure');
                      {
                        const TRIYearOffsetMeasure = TRIOnsiteUICDisposalQuantity.YearOffsetMeasure;
                        builder.txt(String(TRIYearOffsetMeasure satisfies number));
                      }
                      builder = builder.up();
                    }
                    {
                      const Quantity = TRIOnsiteUICDisposalQuantity.Quantity;
                      if (Quantity.choice === 0) {
                        {
                          const chosen32 = Quantity.value;
                          builder = builder.ele('TRI:TotalQuantity');
                          {
                            const TRITotalQuantity = chosen32.TotalQuantity;
                            builder.txt(formRTotalQuantityToString(TRITotalQuantity));
                          }
                          builder = builder.up();
                          if (chosen32.CalculatorRoundingHintNumber !== null) {
                            builder = builder.ele('TRI:CalculatorRoundingHintNumber');
                            {
                              const TRICalculatorRoundingHintNumber = chosen32.CalculatorRoundingHintNumber;
                              builder.txt(String(TRICalculatorRoundingHintNumber satisfies number));
                            }
                            builder = builder.up();
                          }
                        }
                      }
                      if (Quantity.choice === 'NA') {
                        {
                          const chosen33 = Quantity.value;
                          if (chosen33 !== null) {
                            builder = builder.ele('TRI:TotalQuantityNAIndicator');
                            {
                              const TRITotalQuantityNAIndicator = chosen33;
                              builder.txt(String(TRITotalQuantityNAIndicator satisfies boolean));
                            }
                            builder = builder.up();
                          }
                        }
                      }
                    }
                    if (TRIOnsiteUICDisposalQuantity.ToxicEquivalencyIdentification !== null) {
                      builder = builder.ele('TRI:ToxicEquivalencyIdentification');
                      {
                        const TRIToxicEquivalencyIdentification =
                          TRIOnsiteUICDisposalQuantity.ToxicEquivalencyIdentification;
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency1Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency1Value');
                          {
                            const TRIToxicEquivalency1Value = TRIToxicEquivalencyIdentification.ToxicEquivalency1Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency1Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency2Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency2Value');
                          {
                            const TRIToxicEquivalency2Value = TRIToxicEquivalencyIdentification.ToxicEquivalency2Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency2Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency3Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency3Value');
                          {
                            const TRIToxicEquivalency3Value = TRIToxicEquivalencyIdentification.ToxicEquivalency3Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency3Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency4Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency4Value');
                          {
                            const TRIToxicEquivalency4Value = TRIToxicEquivalencyIdentification.ToxicEquivalency4Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency4Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency5Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency5Value');
                          {
                            const TRIToxicEquivalency5Value = TRIToxicEquivalencyIdentification.ToxicEquivalency5Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency5Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency6Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency6Value');
                          {
                            const TRIToxicEquivalency6Value = TRIToxicEquivalencyIdentification.ToxicEquivalency6Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency6Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency7Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency7Value');
                          {
                            const TRIToxicEquivalency7Value = TRIToxicEquivalencyIdentification.ToxicEquivalency7Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency7Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency8Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency8Value');
                          {
                            const TRIToxicEquivalency8Value = TRIToxicEquivalencyIdentification.ToxicEquivalency8Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency8Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency9Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency9Value');
                          {
                            const TRIToxicEquivalency9Value = TRIToxicEquivalencyIdentification.ToxicEquivalency9Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency9Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency10Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency10Value');
                          {
                            const TRIToxicEquivalency10Value =
                              TRIToxicEquivalencyIdentification.ToxicEquivalency10Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency10Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency11Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency11Value');
                          {
                            const TRIToxicEquivalency11Value =
                              TRIToxicEquivalencyIdentification.ToxicEquivalency11Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency11Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency12Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency12Value');
                          {
                            const TRIToxicEquivalency12Value =
                              TRIToxicEquivalencyIdentification.ToxicEquivalency12Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency12Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency13Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency13Value');
                          {
                            const TRIToxicEquivalency13Value =
                              TRIToxicEquivalencyIdentification.ToxicEquivalency13Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency13Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency14Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency14Value');
                          {
                            const TRIToxicEquivalency14Value =
                              TRIToxicEquivalencyIdentification.ToxicEquivalency14Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency14Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency15Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency15Value');
                          {
                            const TRIToxicEquivalency15Value =
                              TRIToxicEquivalencyIdentification.ToxicEquivalency15Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency15Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency16Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency16Value');
                          {
                            const TRIToxicEquivalency16Value =
                              TRIToxicEquivalencyIdentification.ToxicEquivalency16Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency16Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency17Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency17Value');
                          {
                            const TRIToxicEquivalency17Value =
                              TRIToxicEquivalencyIdentification.ToxicEquivalency17Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency17Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalencyNAIndicator !== null) {
                          builder = builder.ele('TRI:ToxicEquivalencyNAIndicator');
                          {
                            const TRIToxicEquivalencyNAIndicator =
                              TRIToxicEquivalencyIdentification.ToxicEquivalencyNAIndicator;
                            builder.txt(String(TRIToxicEquivalencyNAIndicator satisfies boolean));
                          }
                          builder = builder.up();
                        }
                      }
                      builder = builder.up();
                    }
                  }
                  builder = builder.up();
                }
                for (const TRISourceReductionQuantityItem of TRISourceReductionQuantity.OnsiteOtherDisposalQuantity) {
                  builder = builder.ele('TRI:OnsiteOtherDisposalQuantity');
                  {
                    const TRIOnsiteOtherDisposalQuantity = TRISourceReductionQuantityItem;
                    if (TRIOnsiteOtherDisposalQuantity.YearOffsetMeasure !== null) {
                      builder = builder.ele('TRI:YearOffsetMeasure');
                      {
                        const TRIYearOffsetMeasure = TRIOnsiteOtherDisposalQuantity.YearOffsetMeasure;
                        builder.txt(String(TRIYearOffsetMeasure satisfies number));
                      }
                      builder = builder.up();
                    }
                    {
                      const Quantity = TRIOnsiteOtherDisposalQuantity.Quantity;
                      if (Quantity.choice === 0) {
                        {
                          const chosen34 = Quantity.value;
                          builder = builder.ele('TRI:TotalQuantity');
                          {
                            const TRITotalQuantity = chosen34.TotalQuantity;
                            builder.txt(formRTotalQuantityToString(TRITotalQuantity));
                          }
                          builder = builder.up();
                          if (chosen34.CalculatorRoundingHintNumber !== null) {
                            builder = builder.ele('TRI:CalculatorRoundingHintNumber');
                            {
                              const TRICalculatorRoundingHintNumber = chosen34.CalculatorRoundingHintNumber;
                              builder.txt(String(TRICalculatorRoundingHintNumber satisfies number));
                            }
                            builder = builder.up();
                          }
                        }
                      }
                      if (Quantity.choice === 'NA') {
                        {
                          const chosen35 = Quantity.value;
                          if (chosen35 !== null) {
                            builder = builder.ele('TRI:TotalQuantityNAIndicator');
                            {
                              const TRITotalQuantityNAIndicator = chosen35;
                              builder.txt(String(TRITotalQuantityNAIndicator satisfies boolean));
                            }
                            builder = builder.up();
                          }
                        }
                      }
                    }
                    if (TRIOnsiteOtherDisposalQuantity.ToxicEquivalencyIdentification !== null) {
                      builder = builder.ele('TRI:ToxicEquivalencyIdentification');
                      {
                        const TRIToxicEquivalencyIdentification =
                          TRIOnsiteOtherDisposalQuantity.ToxicEquivalencyIdentification;
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency1Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency1Value');
                          {
                            const TRIToxicEquivalency1Value = TRIToxicEquivalencyIdentification.ToxicEquivalency1Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency1Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency2Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency2Value');
                          {
                            const TRIToxicEquivalency2Value = TRIToxicEquivalencyIdentification.ToxicEquivalency2Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency2Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency3Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency3Value');
                          {
                            const TRIToxicEquivalency3Value = TRIToxicEquivalencyIdentification.ToxicEquivalency3Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency3Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency4Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency4Value');
                          {
                            const TRIToxicEquivalency4Value = TRIToxicEquivalencyIdentification.ToxicEquivalency4Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency4Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency5Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency5Value');
                          {
                            const TRIToxicEquivalency5Value = TRIToxicEquivalencyIdentification.ToxicEquivalency5Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency5Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency6Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency6Value');
                          {
                            const TRIToxicEquivalency6Value = TRIToxicEquivalencyIdentification.ToxicEquivalency6Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency6Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency7Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency7Value');
                          {
                            const TRIToxicEquivalency7Value = TRIToxicEquivalencyIdentification.ToxicEquivalency7Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency7Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency8Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency8Value');
                          {
                            const TRIToxicEquivalency8Value = TRIToxicEquivalencyIdentification.ToxicEquivalency8Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency8Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency9Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency9Value');
                          {
                            const TRIToxicEquivalency9Value = TRIToxicEquivalencyIdentification.ToxicEquivalency9Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency9Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency10Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency10Value');
                          {
                            const TRIToxicEquivalency10Value =
                              TRIToxicEquivalencyIdentification.ToxicEquivalency10Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency10Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency11Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency11Value');
                          {
                            const TRIToxicEquivalency11Value =
                              TRIToxicEquivalencyIdentification.ToxicEquivalency11Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency11Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency12Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency12Value');
                          {
                            const TRIToxicEquivalency12Value =
                              TRIToxicEquivalencyIdentification.ToxicEquivalency12Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency12Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency13Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency13Value');
                          {
                            const TRIToxicEquivalency13Value =
                              TRIToxicEquivalencyIdentification.ToxicEquivalency13Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency13Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency14Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency14Value');
                          {
                            const TRIToxicEquivalency14Value =
                              TRIToxicEquivalencyIdentification.ToxicEquivalency14Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency14Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency15Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency15Value');
                          {
                            const TRIToxicEquivalency15Value =
                              TRIToxicEquivalencyIdentification.ToxicEquivalency15Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency15Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency16Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency16Value');
                          {
                            const TRIToxicEquivalency16Value =
                              TRIToxicEquivalencyIdentification.ToxicEquivalency16Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency16Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency17Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency17Value');
                          {
                            const TRIToxicEquivalency17Value =
                              TRIToxicEquivalencyIdentification.ToxicEquivalency17Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency17Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalencyNAIndicator !== null) {
                          builder = builder.ele('TRI:ToxicEquivalencyNAIndicator');
                          {
                            const TRIToxicEquivalencyNAIndicator =
                              TRIToxicEquivalencyIdentification.ToxicEquivalencyNAIndicator;
                            builder.txt(String(TRIToxicEquivalencyNAIndicator satisfies boolean));
                          }
                          builder = builder.up();
                        }
                      }
                      builder = builder.up();
                    }
                  }
                  builder = builder.up();
                }
                for (const TRISourceReductionQuantityItem of TRISourceReductionQuantity.OffsiteUICDisposalQuantity) {
                  builder = builder.ele('TRI:OffsiteUICDisposalQuantity');
                  {
                    const TRIOffsiteUICDisposalQuantity = TRISourceReductionQuantityItem;
                    if (TRIOffsiteUICDisposalQuantity.YearOffsetMeasure !== null) {
                      builder = builder.ele('TRI:YearOffsetMeasure');
                      {
                        const TRIYearOffsetMeasure = TRIOffsiteUICDisposalQuantity.YearOffsetMeasure;
                        builder.txt(String(TRIYearOffsetMeasure satisfies number));
                      }
                      builder = builder.up();
                    }
                    {
                      const Quantity = TRIOffsiteUICDisposalQuantity.Quantity;
                      if (Quantity.choice === 0) {
                        {
                          const chosen36 = Quantity.value;
                          builder = builder.ele('TRI:TotalQuantity');
                          {
                            const TRITotalQuantity = chosen36.TotalQuantity;
                            builder.txt(formRTotalQuantityToString(TRITotalQuantity));
                          }
                          builder = builder.up();
                          if (chosen36.CalculatorRoundingHintNumber !== null) {
                            builder = builder.ele('TRI:CalculatorRoundingHintNumber');
                            {
                              const TRICalculatorRoundingHintNumber = chosen36.CalculatorRoundingHintNumber;
                              builder.txt(String(TRICalculatorRoundingHintNumber satisfies number));
                            }
                            builder = builder.up();
                          }
                        }
                      }
                      if (Quantity.choice === 'NA') {
                        {
                          const chosen37 = Quantity.value;
                          if (chosen37 !== null) {
                            builder = builder.ele('TRI:TotalQuantityNAIndicator');
                            {
                              const TRITotalQuantityNAIndicator = chosen37;
                              builder.txt(String(TRITotalQuantityNAIndicator satisfies boolean));
                            }
                            builder = builder.up();
                          }
                        }
                      }
                    }
                    if (TRIOffsiteUICDisposalQuantity.ToxicEquivalencyIdentification !== null) {
                      builder = builder.ele('TRI:ToxicEquivalencyIdentification');
                      {
                        const TRIToxicEquivalencyIdentification =
                          TRIOffsiteUICDisposalQuantity.ToxicEquivalencyIdentification;
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency1Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency1Value');
                          {
                            const TRIToxicEquivalency1Value = TRIToxicEquivalencyIdentification.ToxicEquivalency1Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency1Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency2Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency2Value');
                          {
                            const TRIToxicEquivalency2Value = TRIToxicEquivalencyIdentification.ToxicEquivalency2Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency2Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency3Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency3Value');
                          {
                            const TRIToxicEquivalency3Value = TRIToxicEquivalencyIdentification.ToxicEquivalency3Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency3Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency4Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency4Value');
                          {
                            const TRIToxicEquivalency4Value = TRIToxicEquivalencyIdentification.ToxicEquivalency4Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency4Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency5Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency5Value');
                          {
                            const TRIToxicEquivalency5Value = TRIToxicEquivalencyIdentification.ToxicEquivalency5Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency5Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency6Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency6Value');
                          {
                            const TRIToxicEquivalency6Value = TRIToxicEquivalencyIdentification.ToxicEquivalency6Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency6Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency7Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency7Value');
                          {
                            const TRIToxicEquivalency7Value = TRIToxicEquivalencyIdentification.ToxicEquivalency7Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency7Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency8Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency8Value');
                          {
                            const TRIToxicEquivalency8Value = TRIToxicEquivalencyIdentification.ToxicEquivalency8Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency8Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency9Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency9Value');
                          {
                            const TRIToxicEquivalency9Value = TRIToxicEquivalencyIdentification.ToxicEquivalency9Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency9Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency10Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency10Value');
                          {
                            const TRIToxicEquivalency10Value =
                              TRIToxicEquivalencyIdentification.ToxicEquivalency10Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency10Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency11Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency11Value');
                          {
                            const TRIToxicEquivalency11Value =
                              TRIToxicEquivalencyIdentification.ToxicEquivalency11Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency11Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency12Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency12Value');
                          {
                            const TRIToxicEquivalency12Value =
                              TRIToxicEquivalencyIdentification.ToxicEquivalency12Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency12Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency13Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency13Value');
                          {
                            const TRIToxicEquivalency13Value =
                              TRIToxicEquivalencyIdentification.ToxicEquivalency13Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency13Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency14Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency14Value');
                          {
                            const TRIToxicEquivalency14Value =
                              TRIToxicEquivalencyIdentification.ToxicEquivalency14Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency14Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency15Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency15Value');
                          {
                            const TRIToxicEquivalency15Value =
                              TRIToxicEquivalencyIdentification.ToxicEquivalency15Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency15Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency16Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency16Value');
                          {
                            const TRIToxicEquivalency16Value =
                              TRIToxicEquivalencyIdentification.ToxicEquivalency16Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency16Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency17Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency17Value');
                          {
                            const TRIToxicEquivalency17Value =
                              TRIToxicEquivalencyIdentification.ToxicEquivalency17Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency17Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalencyNAIndicator !== null) {
                          builder = builder.ele('TRI:ToxicEquivalencyNAIndicator');
                          {
                            const TRIToxicEquivalencyNAIndicator =
                              TRIToxicEquivalencyIdentification.ToxicEquivalencyNAIndicator;
                            builder.txt(String(TRIToxicEquivalencyNAIndicator satisfies boolean));
                          }
                          builder = builder.up();
                        }
                      }
                      builder = builder.up();
                    }
                  }
                  builder = builder.up();
                }
                for (const TRISourceReductionQuantityItem of TRISourceReductionQuantity.OffsiteOtherDisposalQuantity) {
                  builder = builder.ele('TRI:OffsiteOtherDisposalQuantity');
                  {
                    const TRIOffsiteOtherDisposalQuantity = TRISourceReductionQuantityItem;
                    if (TRIOffsiteOtherDisposalQuantity.YearOffsetMeasure !== null) {
                      builder = builder.ele('TRI:YearOffsetMeasure');
                      {
                        const TRIYearOffsetMeasure = TRIOffsiteOtherDisposalQuantity.YearOffsetMeasure;
                        builder.txt(String(TRIYearOffsetMeasure satisfies number));
                      }
                      builder = builder.up();
                    }
                    {
                      const Quantity = TRIOffsiteOtherDisposalQuantity.Quantity;
                      if (Quantity.choice === 0) {
                        {
                          const chosen38 = Quantity.value;
                          builder = builder.ele('TRI:TotalQuantity');
                          {
                            const TRITotalQuantity = chosen38.TotalQuantity;
                            builder.txt(formRTotalQuantityToString(TRITotalQuantity));
                          }
                          builder = builder.up();
                          if (chosen38.CalculatorRoundingHintNumber !== null) {
                            builder = builder.ele('TRI:CalculatorRoundingHintNumber');
                            {
                              const TRICalculatorRoundingHintNumber = chosen38.CalculatorRoundingHintNumber;
                              builder.txt(String(TRICalculatorRoundingHintNumber satisfies number));
                            }
                            builder = builder.up();
                          }
                        }
                      }
                      if (Quantity.choice === 'NA') {
                        {
                          const chosen39 = Quantity.value;
                          if (chosen39 !== null) {
                            builder = builder.ele('TRI:TotalQuantityNAIndicator');
                            {
                              const TRITotalQuantityNAIndicator = chosen39;
                              builder.txt(String(TRITotalQuantityNAIndicator satisfies boolean));
                            }
                            builder = builder.up();
                          }
                        }
                      }
                    }
                    if (TRIOffsiteOtherDisposalQuantity.ToxicEquivalencyIdentification !== null) {
                      builder = builder.ele('TRI:ToxicEquivalencyIdentification');
                      {
                        const TRIToxicEquivalencyIdentification =
                          TRIOffsiteOtherDisposalQuantity.ToxicEquivalencyIdentification;
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency1Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency1Value');
                          {
                            const TRIToxicEquivalency1Value = TRIToxicEquivalencyIdentification.ToxicEquivalency1Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency1Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency2Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency2Value');
                          {
                            const TRIToxicEquivalency2Value = TRIToxicEquivalencyIdentification.ToxicEquivalency2Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency2Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency3Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency3Value');
                          {
                            const TRIToxicEquivalency3Value = TRIToxicEquivalencyIdentification.ToxicEquivalency3Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency3Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency4Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency4Value');
                          {
                            const TRIToxicEquivalency4Value = TRIToxicEquivalencyIdentification.ToxicEquivalency4Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency4Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency5Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency5Value');
                          {
                            const TRIToxicEquivalency5Value = TRIToxicEquivalencyIdentification.ToxicEquivalency5Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency5Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency6Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency6Value');
                          {
                            const TRIToxicEquivalency6Value = TRIToxicEquivalencyIdentification.ToxicEquivalency6Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency6Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency7Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency7Value');
                          {
                            const TRIToxicEquivalency7Value = TRIToxicEquivalencyIdentification.ToxicEquivalency7Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency7Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency8Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency8Value');
                          {
                            const TRIToxicEquivalency8Value = TRIToxicEquivalencyIdentification.ToxicEquivalency8Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency8Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency9Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency9Value');
                          {
                            const TRIToxicEquivalency9Value = TRIToxicEquivalencyIdentification.ToxicEquivalency9Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency9Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency10Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency10Value');
                          {
                            const TRIToxicEquivalency10Value =
                              TRIToxicEquivalencyIdentification.ToxicEquivalency10Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency10Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency11Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency11Value');
                          {
                            const TRIToxicEquivalency11Value =
                              TRIToxicEquivalencyIdentification.ToxicEquivalency11Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency11Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency12Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency12Value');
                          {
                            const TRIToxicEquivalency12Value =
                              TRIToxicEquivalencyIdentification.ToxicEquivalency12Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency12Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency13Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency13Value');
                          {
                            const TRIToxicEquivalency13Value =
                              TRIToxicEquivalencyIdentification.ToxicEquivalency13Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency13Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency14Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency14Value');
                          {
                            const TRIToxicEquivalency14Value =
                              TRIToxicEquivalencyIdentification.ToxicEquivalency14Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency14Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency15Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency15Value');
                          {
                            const TRIToxicEquivalency15Value =
                              TRIToxicEquivalencyIdentification.ToxicEquivalency15Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency15Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency16Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency16Value');
                          {
                            const TRIToxicEquivalency16Value =
                              TRIToxicEquivalencyIdentification.ToxicEquivalency16Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency16Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency17Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency17Value');
                          {
                            const TRIToxicEquivalency17Value =
                              TRIToxicEquivalencyIdentification.ToxicEquivalency17Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency17Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalencyNAIndicator !== null) {
                          builder = builder.ele('TRI:ToxicEquivalencyNAIndicator');
                          {
                            const TRIToxicEquivalencyNAIndicator =
                              TRIToxicEquivalencyIdentification.ToxicEquivalencyNAIndicator;
                            builder.txt(String(TRIToxicEquivalencyNAIndicator satisfies boolean));
                          }
                          builder = builder.up();
                        }
                      }
                      builder = builder.up();
                    }
                  }
                  builder = builder.up();
                }
                for (const TRISourceReductionQuantityItem of TRISourceReductionQuantity.OnsiteEnergyRecoveryQuantity) {
                  builder = builder.ele('TRI:OnsiteEnergyRecoveryQuantity');
                  {
                    const TRIOnsiteEnergyRecoveryQuantity = TRISourceReductionQuantityItem;
                    if (TRIOnsiteEnergyRecoveryQuantity.YearOffsetMeasure !== null) {
                      builder = builder.ele('TRI:YearOffsetMeasure');
                      {
                        const TRIYearOffsetMeasure = TRIOnsiteEnergyRecoveryQuantity.YearOffsetMeasure;
                        builder.txt(String(TRIYearOffsetMeasure satisfies number));
                      }
                      builder = builder.up();
                    }
                    {
                      const Quantity = TRIOnsiteEnergyRecoveryQuantity.Quantity;
                      if (Quantity.choice === 0) {
                        {
                          const chosen40 = Quantity.value;
                          builder = builder.ele('TRI:TotalQuantity');
                          {
                            const TRITotalQuantity = chosen40.TotalQuantity;
                            builder.txt(formRTotalQuantityToString(TRITotalQuantity));
                          }
                          builder = builder.up();
                          if (chosen40.CalculatorRoundingHintNumber !== null) {
                            builder = builder.ele('TRI:CalculatorRoundingHintNumber');
                            {
                              const TRICalculatorRoundingHintNumber = chosen40.CalculatorRoundingHintNumber;
                              builder.txt(String(TRICalculatorRoundingHintNumber satisfies number));
                            }
                            builder = builder.up();
                          }
                        }
                      }
                      if (Quantity.choice === 'NA') {
                        {
                          const chosen41 = Quantity.value;
                          if (chosen41 !== null) {
                            builder = builder.ele('TRI:TotalQuantityNAIndicator');
                            {
                              const TRITotalQuantityNAIndicator = chosen41;
                              builder.txt(String(TRITotalQuantityNAIndicator satisfies boolean));
                            }
                            builder = builder.up();
                          }
                        }
                      }
                    }
                    if (TRIOnsiteEnergyRecoveryQuantity.ToxicEquivalencyIdentification !== null) {
                      builder = builder.ele('TRI:ToxicEquivalencyIdentification');
                      {
                        const TRIToxicEquivalencyIdentification =
                          TRIOnsiteEnergyRecoveryQuantity.ToxicEquivalencyIdentification;
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency1Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency1Value');
                          {
                            const TRIToxicEquivalency1Value = TRIToxicEquivalencyIdentification.ToxicEquivalency1Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency1Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency2Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency2Value');
                          {
                            const TRIToxicEquivalency2Value = TRIToxicEquivalencyIdentification.ToxicEquivalency2Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency2Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency3Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency3Value');
                          {
                            const TRIToxicEquivalency3Value = TRIToxicEquivalencyIdentification.ToxicEquivalency3Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency3Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency4Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency4Value');
                          {
                            const TRIToxicEquivalency4Value = TRIToxicEquivalencyIdentification.ToxicEquivalency4Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency4Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency5Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency5Value');
                          {
                            const TRIToxicEquivalency5Value = TRIToxicEquivalencyIdentification.ToxicEquivalency5Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency5Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency6Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency6Value');
                          {
                            const TRIToxicEquivalency6Value = TRIToxicEquivalencyIdentification.ToxicEquivalency6Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency6Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency7Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency7Value');
                          {
                            const TRIToxicEquivalency7Value = TRIToxicEquivalencyIdentification.ToxicEquivalency7Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency7Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency8Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency8Value');
                          {
                            const TRIToxicEquivalency8Value = TRIToxicEquivalencyIdentification.ToxicEquivalency8Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency8Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency9Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency9Value');
                          {
                            const TRIToxicEquivalency9Value = TRIToxicEquivalencyIdentification.ToxicEquivalency9Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency9Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency10Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency10Value');
                          {
                            const TRIToxicEquivalency10Value =
                              TRIToxicEquivalencyIdentification.ToxicEquivalency10Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency10Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency11Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency11Value');
                          {
                            const TRIToxicEquivalency11Value =
                              TRIToxicEquivalencyIdentification.ToxicEquivalency11Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency11Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency12Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency12Value');
                          {
                            const TRIToxicEquivalency12Value =
                              TRIToxicEquivalencyIdentification.ToxicEquivalency12Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency12Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency13Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency13Value');
                          {
                            const TRIToxicEquivalency13Value =
                              TRIToxicEquivalencyIdentification.ToxicEquivalency13Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency13Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency14Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency14Value');
                          {
                            const TRIToxicEquivalency14Value =
                              TRIToxicEquivalencyIdentification.ToxicEquivalency14Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency14Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency15Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency15Value');
                          {
                            const TRIToxicEquivalency15Value =
                              TRIToxicEquivalencyIdentification.ToxicEquivalency15Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency15Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency16Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency16Value');
                          {
                            const TRIToxicEquivalency16Value =
                              TRIToxicEquivalencyIdentification.ToxicEquivalency16Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency16Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency17Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency17Value');
                          {
                            const TRIToxicEquivalency17Value =
                              TRIToxicEquivalencyIdentification.ToxicEquivalency17Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency17Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalencyNAIndicator !== null) {
                          builder = builder.ele('TRI:ToxicEquivalencyNAIndicator');
                          {
                            const TRIToxicEquivalencyNAIndicator =
                              TRIToxicEquivalencyIdentification.ToxicEquivalencyNAIndicator;
                            builder.txt(String(TRIToxicEquivalencyNAIndicator satisfies boolean));
                          }
                          builder = builder.up();
                        }
                      }
                      builder = builder.up();
                    }
                  }
                  builder = builder.up();
                }
                for (const TRISourceReductionQuantityItem of TRISourceReductionQuantity.OffsiteEnergyRecoveryQuantity) {
                  builder = builder.ele('TRI:OffsiteEnergyRecoveryQuantity');
                  {
                    const TRIOffsiteEnergyRecoveryQuantity = TRISourceReductionQuantityItem;
                    if (TRIOffsiteEnergyRecoveryQuantity.YearOffsetMeasure !== null) {
                      builder = builder.ele('TRI:YearOffsetMeasure');
                      {
                        const TRIYearOffsetMeasure = TRIOffsiteEnergyRecoveryQuantity.YearOffsetMeasure;
                        builder.txt(String(TRIYearOffsetMeasure satisfies number));
                      }
                      builder = builder.up();
                    }
                    {
                      const Quantity = TRIOffsiteEnergyRecoveryQuantity.Quantity;
                      if (Quantity.choice === 0) {
                        {
                          const chosen42 = Quantity.value;
                          builder = builder.ele('TRI:TotalQuantity');
                          {
                            const TRITotalQuantity = chosen42.TotalQuantity;
                            builder.txt(formRTotalQuantityToString(TRITotalQuantity));
                          }
                          builder = builder.up();
                          if (chosen42.CalculatorRoundingHintNumber !== null) {
                            builder = builder.ele('TRI:CalculatorRoundingHintNumber');
                            {
                              const TRICalculatorRoundingHintNumber = chosen42.CalculatorRoundingHintNumber;
                              builder.txt(String(TRICalculatorRoundingHintNumber satisfies number));
                            }
                            builder = builder.up();
                          }
                        }
                      }
                      if (Quantity.choice === 'NA') {
                        {
                          const chosen43 = Quantity.value;
                          if (chosen43 !== null) {
                            builder = builder.ele('TRI:TotalQuantityNAIndicator');
                            {
                              const TRITotalQuantityNAIndicator = chosen43;
                              builder.txt(String(TRITotalQuantityNAIndicator satisfies boolean));
                            }
                            builder = builder.up();
                          }
                        }
                      }
                    }
                    if (TRIOffsiteEnergyRecoveryQuantity.ToxicEquivalencyIdentification !== null) {
                      builder = builder.ele('TRI:ToxicEquivalencyIdentification');
                      {
                        const TRIToxicEquivalencyIdentification =
                          TRIOffsiteEnergyRecoveryQuantity.ToxicEquivalencyIdentification;
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency1Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency1Value');
                          {
                            const TRIToxicEquivalency1Value = TRIToxicEquivalencyIdentification.ToxicEquivalency1Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency1Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency2Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency2Value');
                          {
                            const TRIToxicEquivalency2Value = TRIToxicEquivalencyIdentification.ToxicEquivalency2Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency2Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency3Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency3Value');
                          {
                            const TRIToxicEquivalency3Value = TRIToxicEquivalencyIdentification.ToxicEquivalency3Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency3Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency4Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency4Value');
                          {
                            const TRIToxicEquivalency4Value = TRIToxicEquivalencyIdentification.ToxicEquivalency4Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency4Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency5Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency5Value');
                          {
                            const TRIToxicEquivalency5Value = TRIToxicEquivalencyIdentification.ToxicEquivalency5Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency5Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency6Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency6Value');
                          {
                            const TRIToxicEquivalency6Value = TRIToxicEquivalencyIdentification.ToxicEquivalency6Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency6Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency7Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency7Value');
                          {
                            const TRIToxicEquivalency7Value = TRIToxicEquivalencyIdentification.ToxicEquivalency7Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency7Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency8Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency8Value');
                          {
                            const TRIToxicEquivalency8Value = TRIToxicEquivalencyIdentification.ToxicEquivalency8Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency8Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency9Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency9Value');
                          {
                            const TRIToxicEquivalency9Value = TRIToxicEquivalencyIdentification.ToxicEquivalency9Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency9Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency10Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency10Value');
                          {
                            const TRIToxicEquivalency10Value =
                              TRIToxicEquivalencyIdentification.ToxicEquivalency10Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency10Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency11Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency11Value');
                          {
                            const TRIToxicEquivalency11Value =
                              TRIToxicEquivalencyIdentification.ToxicEquivalency11Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency11Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency12Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency12Value');
                          {
                            const TRIToxicEquivalency12Value =
                              TRIToxicEquivalencyIdentification.ToxicEquivalency12Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency12Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency13Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency13Value');
                          {
                            const TRIToxicEquivalency13Value =
                              TRIToxicEquivalencyIdentification.ToxicEquivalency13Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency13Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency14Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency14Value');
                          {
                            const TRIToxicEquivalency14Value =
                              TRIToxicEquivalencyIdentification.ToxicEquivalency14Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency14Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency15Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency15Value');
                          {
                            const TRIToxicEquivalency15Value =
                              TRIToxicEquivalencyIdentification.ToxicEquivalency15Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency15Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency16Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency16Value');
                          {
                            const TRIToxicEquivalency16Value =
                              TRIToxicEquivalencyIdentification.ToxicEquivalency16Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency16Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency17Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency17Value');
                          {
                            const TRIToxicEquivalency17Value =
                              TRIToxicEquivalencyIdentification.ToxicEquivalency17Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency17Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalencyNAIndicator !== null) {
                          builder = builder.ele('TRI:ToxicEquivalencyNAIndicator');
                          {
                            const TRIToxicEquivalencyNAIndicator =
                              TRIToxicEquivalencyIdentification.ToxicEquivalencyNAIndicator;
                            builder.txt(String(TRIToxicEquivalencyNAIndicator satisfies boolean));
                          }
                          builder = builder.up();
                        }
                      }
                      builder = builder.up();
                    }
                  }
                  builder = builder.up();
                }
                for (const TRISourceReductionQuantityItem of TRISourceReductionQuantity.OnsiteRecycledQuantity) {
                  builder = builder.ele('TRI:OnsiteRecycledQuantity');
                  {
                    const TRIOnsiteRecycledQuantity = TRISourceReductionQuantityItem;
                    if (TRIOnsiteRecycledQuantity.YearOffsetMeasure !== null) {
                      builder = builder.ele('TRI:YearOffsetMeasure');
                      {
                        const TRIYearOffsetMeasure = TRIOnsiteRecycledQuantity.YearOffsetMeasure;
                        builder.txt(String(TRIYearOffsetMeasure satisfies number));
                      }
                      builder = builder.up();
                    }
                    {
                      const Quantity = TRIOnsiteRecycledQuantity.Quantity;
                      if (Quantity.choice === 0) {
                        {
                          const chosen44 = Quantity.value;
                          builder = builder.ele('TRI:TotalQuantity');
                          {
                            const TRITotalQuantity = chosen44.TotalQuantity;
                            builder.txt(formRTotalQuantityToString(TRITotalQuantity));
                          }
                          builder = builder.up();
                          if (chosen44.CalculatorRoundingHintNumber !== null) {
                            builder = builder.ele('TRI:CalculatorRoundingHintNumber');
                            {
                              const TRICalculatorRoundingHintNumber = chosen44.CalculatorRoundingHintNumber;
                              builder.txt(String(TRICalculatorRoundingHintNumber satisfies number));
                            }
                            builder = builder.up();
                          }
                        }
                      }
                      if (Quantity.choice === 'NA') {
                        {
                          const chosen45 = Quantity.value;
                          if (chosen45 !== null) {
                            builder = builder.ele('TRI:TotalQuantityNAIndicator');
                            {
                              const TRITotalQuantityNAIndicator = chosen45;
                              builder.txt(String(TRITotalQuantityNAIndicator satisfies boolean));
                            }
                            builder = builder.up();
                          }
                        }
                      }
                    }
                    if (TRIOnsiteRecycledQuantity.ToxicEquivalencyIdentification !== null) {
                      builder = builder.ele('TRI:ToxicEquivalencyIdentification');
                      {
                        const TRIToxicEquivalencyIdentification =
                          TRIOnsiteRecycledQuantity.ToxicEquivalencyIdentification;
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency1Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency1Value');
                          {
                            const TRIToxicEquivalency1Value = TRIToxicEquivalencyIdentification.ToxicEquivalency1Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency1Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency2Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency2Value');
                          {
                            const TRIToxicEquivalency2Value = TRIToxicEquivalencyIdentification.ToxicEquivalency2Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency2Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency3Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency3Value');
                          {
                            const TRIToxicEquivalency3Value = TRIToxicEquivalencyIdentification.ToxicEquivalency3Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency3Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency4Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency4Value');
                          {
                            const TRIToxicEquivalency4Value = TRIToxicEquivalencyIdentification.ToxicEquivalency4Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency4Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency5Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency5Value');
                          {
                            const TRIToxicEquivalency5Value = TRIToxicEquivalencyIdentification.ToxicEquivalency5Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency5Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency6Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency6Value');
                          {
                            const TRIToxicEquivalency6Value = TRIToxicEquivalencyIdentification.ToxicEquivalency6Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency6Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency7Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency7Value');
                          {
                            const TRIToxicEquivalency7Value = TRIToxicEquivalencyIdentification.ToxicEquivalency7Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency7Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency8Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency8Value');
                          {
                            const TRIToxicEquivalency8Value = TRIToxicEquivalencyIdentification.ToxicEquivalency8Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency8Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency9Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency9Value');
                          {
                            const TRIToxicEquivalency9Value = TRIToxicEquivalencyIdentification.ToxicEquivalency9Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency9Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency10Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency10Value');
                          {
                            const TRIToxicEquivalency10Value =
                              TRIToxicEquivalencyIdentification.ToxicEquivalency10Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency10Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency11Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency11Value');
                          {
                            const TRIToxicEquivalency11Value =
                              TRIToxicEquivalencyIdentification.ToxicEquivalency11Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency11Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency12Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency12Value');
                          {
                            const TRIToxicEquivalency12Value =
                              TRIToxicEquivalencyIdentification.ToxicEquivalency12Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency12Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency13Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency13Value');
                          {
                            const TRIToxicEquivalency13Value =
                              TRIToxicEquivalencyIdentification.ToxicEquivalency13Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency13Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency14Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency14Value');
                          {
                            const TRIToxicEquivalency14Value =
                              TRIToxicEquivalencyIdentification.ToxicEquivalency14Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency14Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency15Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency15Value');
                          {
                            const TRIToxicEquivalency15Value =
                              TRIToxicEquivalencyIdentification.ToxicEquivalency15Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency15Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency16Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency16Value');
                          {
                            const TRIToxicEquivalency16Value =
                              TRIToxicEquivalencyIdentification.ToxicEquivalency16Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency16Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency17Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency17Value');
                          {
                            const TRIToxicEquivalency17Value =
                              TRIToxicEquivalencyIdentification.ToxicEquivalency17Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency17Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalencyNAIndicator !== null) {
                          builder = builder.ele('TRI:ToxicEquivalencyNAIndicator');
                          {
                            const TRIToxicEquivalencyNAIndicator =
                              TRIToxicEquivalencyIdentification.ToxicEquivalencyNAIndicator;
                            builder.txt(String(TRIToxicEquivalencyNAIndicator satisfies boolean));
                          }
                          builder = builder.up();
                        }
                      }
                      builder = builder.up();
                    }
                  }
                  builder = builder.up();
                }
                for (const TRISourceReductionQuantityItem of TRISourceReductionQuantity.OffsiteRecycledQuantity) {
                  builder = builder.ele('TRI:OffsiteRecycledQuantity');
                  {
                    const TRIOffsiteRecycledQuantity = TRISourceReductionQuantityItem;
                    if (TRIOffsiteRecycledQuantity.YearOffsetMeasure !== null) {
                      builder = builder.ele('TRI:YearOffsetMeasure');
                      {
                        const TRIYearOffsetMeasure = TRIOffsiteRecycledQuantity.YearOffsetMeasure;
                        builder.txt(String(TRIYearOffsetMeasure satisfies number));
                      }
                      builder = builder.up();
                    }
                    {
                      const Quantity = TRIOffsiteRecycledQuantity.Quantity;
                      if (Quantity.choice === 0) {
                        {
                          const chosen46 = Quantity.value;
                          builder = builder.ele('TRI:TotalQuantity');
                          {
                            const TRITotalQuantity = chosen46.TotalQuantity;
                            builder.txt(formRTotalQuantityToString(TRITotalQuantity));
                          }
                          builder = builder.up();
                          if (chosen46.CalculatorRoundingHintNumber !== null) {
                            builder = builder.ele('TRI:CalculatorRoundingHintNumber');
                            {
                              const TRICalculatorRoundingHintNumber = chosen46.CalculatorRoundingHintNumber;
                              builder.txt(String(TRICalculatorRoundingHintNumber satisfies number));
                            }
                            builder = builder.up();
                          }
                        }
                      }
                      if (Quantity.choice === 'NA') {
                        {
                          const chosen47 = Quantity.value;
                          if (chosen47 !== null) {
                            builder = builder.ele('TRI:TotalQuantityNAIndicator');
                            {
                              const TRITotalQuantityNAIndicator = chosen47;
                              builder.txt(String(TRITotalQuantityNAIndicator satisfies boolean));
                            }
                            builder = builder.up();
                          }
                        }
                      }
                    }
                    if (TRIOffsiteRecycledQuantity.ToxicEquivalencyIdentification !== null) {
                      builder = builder.ele('TRI:ToxicEquivalencyIdentification');
                      {
                        const TRIToxicEquivalencyIdentification =
                          TRIOffsiteRecycledQuantity.ToxicEquivalencyIdentification;
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency1Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency1Value');
                          {
                            const TRIToxicEquivalency1Value = TRIToxicEquivalencyIdentification.ToxicEquivalency1Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency1Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency2Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency2Value');
                          {
                            const TRIToxicEquivalency2Value = TRIToxicEquivalencyIdentification.ToxicEquivalency2Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency2Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency3Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency3Value');
                          {
                            const TRIToxicEquivalency3Value = TRIToxicEquivalencyIdentification.ToxicEquivalency3Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency3Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency4Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency4Value');
                          {
                            const TRIToxicEquivalency4Value = TRIToxicEquivalencyIdentification.ToxicEquivalency4Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency4Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency5Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency5Value');
                          {
                            const TRIToxicEquivalency5Value = TRIToxicEquivalencyIdentification.ToxicEquivalency5Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency5Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency6Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency6Value');
                          {
                            const TRIToxicEquivalency6Value = TRIToxicEquivalencyIdentification.ToxicEquivalency6Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency6Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency7Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency7Value');
                          {
                            const TRIToxicEquivalency7Value = TRIToxicEquivalencyIdentification.ToxicEquivalency7Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency7Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency8Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency8Value');
                          {
                            const TRIToxicEquivalency8Value = TRIToxicEquivalencyIdentification.ToxicEquivalency8Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency8Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency9Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency9Value');
                          {
                            const TRIToxicEquivalency9Value = TRIToxicEquivalencyIdentification.ToxicEquivalency9Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency9Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency10Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency10Value');
                          {
                            const TRIToxicEquivalency10Value =
                              TRIToxicEquivalencyIdentification.ToxicEquivalency10Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency10Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency11Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency11Value');
                          {
                            const TRIToxicEquivalency11Value =
                              TRIToxicEquivalencyIdentification.ToxicEquivalency11Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency11Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency12Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency12Value');
                          {
                            const TRIToxicEquivalency12Value =
                              TRIToxicEquivalencyIdentification.ToxicEquivalency12Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency12Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency13Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency13Value');
                          {
                            const TRIToxicEquivalency13Value =
                              TRIToxicEquivalencyIdentification.ToxicEquivalency13Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency13Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency14Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency14Value');
                          {
                            const TRIToxicEquivalency14Value =
                              TRIToxicEquivalencyIdentification.ToxicEquivalency14Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency14Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency15Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency15Value');
                          {
                            const TRIToxicEquivalency15Value =
                              TRIToxicEquivalencyIdentification.ToxicEquivalency15Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency15Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency16Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency16Value');
                          {
                            const TRIToxicEquivalency16Value =
                              TRIToxicEquivalencyIdentification.ToxicEquivalency16Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency16Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency17Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency17Value');
                          {
                            const TRIToxicEquivalency17Value =
                              TRIToxicEquivalencyIdentification.ToxicEquivalency17Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency17Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalencyNAIndicator !== null) {
                          builder = builder.ele('TRI:ToxicEquivalencyNAIndicator');
                          {
                            const TRIToxicEquivalencyNAIndicator =
                              TRIToxicEquivalencyIdentification.ToxicEquivalencyNAIndicator;
                            builder.txt(String(TRIToxicEquivalencyNAIndicator satisfies boolean));
                          }
                          builder = builder.up();
                        }
                      }
                      builder = builder.up();
                    }
                  }
                  builder = builder.up();
                }
                for (const TRISourceReductionQuantityItem of TRISourceReductionQuantity.OnsiteTreatedQuantity) {
                  builder = builder.ele('TRI:OnsiteTreatedQuantity');
                  {
                    const TRIOnsiteTreatedQuantity = TRISourceReductionQuantityItem;
                    if (TRIOnsiteTreatedQuantity.YearOffsetMeasure !== null) {
                      builder = builder.ele('TRI:YearOffsetMeasure');
                      {
                        const TRIYearOffsetMeasure = TRIOnsiteTreatedQuantity.YearOffsetMeasure;
                        builder.txt(String(TRIYearOffsetMeasure satisfies number));
                      }
                      builder = builder.up();
                    }
                    {
                      const Quantity = TRIOnsiteTreatedQuantity.Quantity;
                      if (Quantity.choice === 0) {
                        {
                          const chosen48 = Quantity.value;
                          builder = builder.ele('TRI:TotalQuantity');
                          {
                            const TRITotalQuantity = chosen48.TotalQuantity;
                            builder.txt(formRTotalQuantityToString(TRITotalQuantity));
                          }
                          builder = builder.up();
                          if (chosen48.CalculatorRoundingHintNumber !== null) {
                            builder = builder.ele('TRI:CalculatorRoundingHintNumber');
                            {
                              const TRICalculatorRoundingHintNumber = chosen48.CalculatorRoundingHintNumber;
                              builder.txt(String(TRICalculatorRoundingHintNumber satisfies number));
                            }
                            builder = builder.up();
                          }
                        }
                      }
                      if (Quantity.choice === 'NA') {
                        {
                          const chosen49 = Quantity.value;
                          if (chosen49 !== null) {
                            builder = builder.ele('TRI:TotalQuantityNAIndicator');
                            {
                              const TRITotalQuantityNAIndicator = chosen49;
                              builder.txt(String(TRITotalQuantityNAIndicator satisfies boolean));
                            }
                            builder = builder.up();
                          }
                        }
                      }
                    }
                    if (TRIOnsiteTreatedQuantity.ToxicEquivalencyIdentification !== null) {
                      builder = builder.ele('TRI:ToxicEquivalencyIdentification');
                      {
                        const TRIToxicEquivalencyIdentification =
                          TRIOnsiteTreatedQuantity.ToxicEquivalencyIdentification;
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency1Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency1Value');
                          {
                            const TRIToxicEquivalency1Value = TRIToxicEquivalencyIdentification.ToxicEquivalency1Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency1Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency2Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency2Value');
                          {
                            const TRIToxicEquivalency2Value = TRIToxicEquivalencyIdentification.ToxicEquivalency2Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency2Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency3Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency3Value');
                          {
                            const TRIToxicEquivalency3Value = TRIToxicEquivalencyIdentification.ToxicEquivalency3Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency3Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency4Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency4Value');
                          {
                            const TRIToxicEquivalency4Value = TRIToxicEquivalencyIdentification.ToxicEquivalency4Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency4Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency5Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency5Value');
                          {
                            const TRIToxicEquivalency5Value = TRIToxicEquivalencyIdentification.ToxicEquivalency5Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency5Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency6Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency6Value');
                          {
                            const TRIToxicEquivalency6Value = TRIToxicEquivalencyIdentification.ToxicEquivalency6Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency6Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency7Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency7Value');
                          {
                            const TRIToxicEquivalency7Value = TRIToxicEquivalencyIdentification.ToxicEquivalency7Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency7Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency8Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency8Value');
                          {
                            const TRIToxicEquivalency8Value = TRIToxicEquivalencyIdentification.ToxicEquivalency8Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency8Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency9Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency9Value');
                          {
                            const TRIToxicEquivalency9Value = TRIToxicEquivalencyIdentification.ToxicEquivalency9Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency9Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency10Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency10Value');
                          {
                            const TRIToxicEquivalency10Value =
                              TRIToxicEquivalencyIdentification.ToxicEquivalency10Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency10Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency11Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency11Value');
                          {
                            const TRIToxicEquivalency11Value =
                              TRIToxicEquivalencyIdentification.ToxicEquivalency11Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency11Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency12Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency12Value');
                          {
                            const TRIToxicEquivalency12Value =
                              TRIToxicEquivalencyIdentification.ToxicEquivalency12Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency12Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency13Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency13Value');
                          {
                            const TRIToxicEquivalency13Value =
                              TRIToxicEquivalencyIdentification.ToxicEquivalency13Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency13Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency14Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency14Value');
                          {
                            const TRIToxicEquivalency14Value =
                              TRIToxicEquivalencyIdentification.ToxicEquivalency14Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency14Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency15Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency15Value');
                          {
                            const TRIToxicEquivalency15Value =
                              TRIToxicEquivalencyIdentification.ToxicEquivalency15Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency15Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency16Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency16Value');
                          {
                            const TRIToxicEquivalency16Value =
                              TRIToxicEquivalencyIdentification.ToxicEquivalency16Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency16Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency17Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency17Value');
                          {
                            const TRIToxicEquivalency17Value =
                              TRIToxicEquivalencyIdentification.ToxicEquivalency17Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency17Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalencyNAIndicator !== null) {
                          builder = builder.ele('TRI:ToxicEquivalencyNAIndicator');
                          {
                            const TRIToxicEquivalencyNAIndicator =
                              TRIToxicEquivalencyIdentification.ToxicEquivalencyNAIndicator;
                            builder.txt(String(TRIToxicEquivalencyNAIndicator satisfies boolean));
                          }
                          builder = builder.up();
                        }
                      }
                      builder = builder.up();
                    }
                  }
                  builder = builder.up();
                }
                for (const TRISourceReductionQuantityItem of TRISourceReductionQuantity.OffsiteTreatedQuantity) {
                  builder = builder.ele('TRI:OffsiteTreatedQuantity');
                  {
                    const TRIOffsiteTreatedQuantity = TRISourceReductionQuantityItem;
                    if (TRIOffsiteTreatedQuantity.YearOffsetMeasure !== null) {
                      builder = builder.ele('TRI:YearOffsetMeasure');
                      {
                        const TRIYearOffsetMeasure = TRIOffsiteTreatedQuantity.YearOffsetMeasure;
                        builder.txt(String(TRIYearOffsetMeasure satisfies number));
                      }
                      builder = builder.up();
                    }
                    {
                      const Quantity = TRIOffsiteTreatedQuantity.Quantity;
                      if (Quantity.choice === 0) {
                        {
                          const chosen50 = Quantity.value;
                          builder = builder.ele('TRI:TotalQuantity');
                          {
                            const TRITotalQuantity = chosen50.TotalQuantity;
                            builder.txt(formRTotalQuantityToString(TRITotalQuantity));
                          }
                          builder = builder.up();
                          if (chosen50.CalculatorRoundingHintNumber !== null) {
                            builder = builder.ele('TRI:CalculatorRoundingHintNumber');
                            {
                              const TRICalculatorRoundingHintNumber = chosen50.CalculatorRoundingHintNumber;
                              builder.txt(String(TRICalculatorRoundingHintNumber satisfies number));
                            }
                            builder = builder.up();
                          }
                        }
                      }
                      if (Quantity.choice === 'NA') {
                        {
                          const chosen51 = Quantity.value;
                          if (chosen51 !== null) {
                            builder = builder.ele('TRI:TotalQuantityNAIndicator');
                            {
                              const TRITotalQuantityNAIndicator = chosen51;
                              builder.txt(String(TRITotalQuantityNAIndicator satisfies boolean));
                            }
                            builder = builder.up();
                          }
                        }
                      }
                    }
                    if (TRIOffsiteTreatedQuantity.ToxicEquivalencyIdentification !== null) {
                      builder = builder.ele('TRI:ToxicEquivalencyIdentification');
                      {
                        const TRIToxicEquivalencyIdentification =
                          TRIOffsiteTreatedQuantity.ToxicEquivalencyIdentification;
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency1Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency1Value');
                          {
                            const TRIToxicEquivalency1Value = TRIToxicEquivalencyIdentification.ToxicEquivalency1Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency1Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency2Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency2Value');
                          {
                            const TRIToxicEquivalency2Value = TRIToxicEquivalencyIdentification.ToxicEquivalency2Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency2Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency3Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency3Value');
                          {
                            const TRIToxicEquivalency3Value = TRIToxicEquivalencyIdentification.ToxicEquivalency3Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency3Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency4Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency4Value');
                          {
                            const TRIToxicEquivalency4Value = TRIToxicEquivalencyIdentification.ToxicEquivalency4Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency4Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency5Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency5Value');
                          {
                            const TRIToxicEquivalency5Value = TRIToxicEquivalencyIdentification.ToxicEquivalency5Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency5Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency6Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency6Value');
                          {
                            const TRIToxicEquivalency6Value = TRIToxicEquivalencyIdentification.ToxicEquivalency6Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency6Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency7Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency7Value');
                          {
                            const TRIToxicEquivalency7Value = TRIToxicEquivalencyIdentification.ToxicEquivalency7Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency7Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency8Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency8Value');
                          {
                            const TRIToxicEquivalency8Value = TRIToxicEquivalencyIdentification.ToxicEquivalency8Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency8Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency9Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency9Value');
                          {
                            const TRIToxicEquivalency9Value = TRIToxicEquivalencyIdentification.ToxicEquivalency9Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency9Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency10Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency10Value');
                          {
                            const TRIToxicEquivalency10Value =
                              TRIToxicEquivalencyIdentification.ToxicEquivalency10Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency10Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency11Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency11Value');
                          {
                            const TRIToxicEquivalency11Value =
                              TRIToxicEquivalencyIdentification.ToxicEquivalency11Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency11Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency12Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency12Value');
                          {
                            const TRIToxicEquivalency12Value =
                              TRIToxicEquivalencyIdentification.ToxicEquivalency12Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency12Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency13Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency13Value');
                          {
                            const TRIToxicEquivalency13Value =
                              TRIToxicEquivalencyIdentification.ToxicEquivalency13Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency13Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency14Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency14Value');
                          {
                            const TRIToxicEquivalency14Value =
                              TRIToxicEquivalencyIdentification.ToxicEquivalency14Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency14Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency15Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency15Value');
                          {
                            const TRIToxicEquivalency15Value =
                              TRIToxicEquivalencyIdentification.ToxicEquivalency15Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency15Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency16Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency16Value');
                          {
                            const TRIToxicEquivalency16Value =
                              TRIToxicEquivalencyIdentification.ToxicEquivalency16Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency16Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalency17Value !== null) {
                          builder = builder.ele('TRI:ToxicEquivalency17Value');
                          {
                            const TRIToxicEquivalency17Value =
                              TRIToxicEquivalencyIdentification.ToxicEquivalency17Value;
                            builder.txt(formRDecimalToString(TRIToxicEquivalency17Value));
                          }
                          builder = builder.up();
                        }
                        if (TRIToxicEquivalencyIdentification.ToxicEquivalencyNAIndicator !== null) {
                          builder = builder.ele('TRI:ToxicEquivalencyNAIndicator');
                          {
                            const TRIToxicEquivalencyNAIndicator =
                              TRIToxicEquivalencyIdentification.ToxicEquivalencyNAIndicator;
                            builder.txt(String(TRIToxicEquivalencyNAIndicator satisfies boolean));
                          }
                          builder = builder.up();
                        }
                      }
                      builder = builder.up();
                    }
                  }
                  builder = builder.up();
                }
                {
                  const ReleaseQuantity = TRISourceReductionQuantity.ReleaseQuantity;
                  if (ReleaseQuantity.choice === 0) {
                    {
                      const chosen52 = ReleaseQuantity.value;
                      builder = builder.ele('TRI:OneTimeReleaseQuantity');
                      {
                        const TRIOneTimeReleaseQuantity = chosen52.OneTimeReleaseQuantity;
                        builder.txt(formRDecimalToString(TRIOneTimeReleaseQuantity));
                      }
                      builder = builder.up();
                      if (chosen52.CalculatorRoundingHintNumber !== null) {
                        builder = builder.ele('TRI:CalculatorRoundingHintNumber');
                        {
                          const TRICalculatorRoundingHintNumber = chosen52.CalculatorRoundingHintNumber;
                          builder.txt(String(TRICalculatorRoundingHintNumber satisfies number));
                        }
                        builder = builder.up();
                      }
                      if (chosen52.ToxicEquivalencyIdentification !== null) {
                        builder = builder.ele('TRI:ToxicEquivalencyIdentification');
                        {
                          const TRIToxicEquivalencyIdentification = chosen52.ToxicEquivalencyIdentification;
                          if (TRIToxicEquivalencyIdentification.ToxicEquivalency1Value !== null) {
                            builder = builder.ele('TRI:ToxicEquivalency1Value');
                            {
                              const TRIToxicEquivalency1Value =
                                TRIToxicEquivalencyIdentification.ToxicEquivalency1Value;
                              builder.txt(formRDecimalToString(TRIToxicEquivalency1Value));
                            }
                            builder = builder.up();
                          }
                          if (TRIToxicEquivalencyIdentification.ToxicEquivalency2Value !== null) {
                            builder = builder.ele('TRI:ToxicEquivalency2Value');
                            {
                              const TRIToxicEquivalency2Value =
                                TRIToxicEquivalencyIdentification.ToxicEquivalency2Value;
                              builder.txt(formRDecimalToString(TRIToxicEquivalency2Value));
                            }
                            builder = builder.up();
                          }
                          if (TRIToxicEquivalencyIdentification.ToxicEquivalency3Value !== null) {
                            builder = builder.ele('TRI:ToxicEquivalency3Value');
                            {
                              const TRIToxicEquivalency3Value =
                                TRIToxicEquivalencyIdentification.ToxicEquivalency3Value;
                              builder.txt(formRDecimalToString(TRIToxicEquivalency3Value));
                            }
                            builder = builder.up();
                          }
                          if (TRIToxicEquivalencyIdentification.ToxicEquivalency4Value !== null) {
                            builder = builder.ele('TRI:ToxicEquivalency4Value');
                            {
                              const TRIToxicEquivalency4Value =
                                TRIToxicEquivalencyIdentification.ToxicEquivalency4Value;
                              builder.txt(formRDecimalToString(TRIToxicEquivalency4Value));
                            }
                            builder = builder.up();
                          }
                          if (TRIToxicEquivalencyIdentification.ToxicEquivalency5Value !== null) {
                            builder = builder.ele('TRI:ToxicEquivalency5Value');
                            {
                              const TRIToxicEquivalency5Value =
                                TRIToxicEquivalencyIdentification.ToxicEquivalency5Value;
                              builder.txt(formRDecimalToString(TRIToxicEquivalency5Value));
                            }
                            builder = builder.up();
                          }
                          if (TRIToxicEquivalencyIdentification.ToxicEquivalency6Value !== null) {
                            builder = builder.ele('TRI:ToxicEquivalency6Value');
                            {
                              const TRIToxicEquivalency6Value =
                                TRIToxicEquivalencyIdentification.ToxicEquivalency6Value;
                              builder.txt(formRDecimalToString(TRIToxicEquivalency6Value));
                            }
                            builder = builder.up();
                          }
                          if (TRIToxicEquivalencyIdentification.ToxicEquivalency7Value !== null) {
                            builder = builder.ele('TRI:ToxicEquivalency7Value');
                            {
                              const TRIToxicEquivalency7Value =
                                TRIToxicEquivalencyIdentification.ToxicEquivalency7Value;
                              builder.txt(formRDecimalToString(TRIToxicEquivalency7Value));
                            }
                            builder = builder.up();
                          }
                          if (TRIToxicEquivalencyIdentification.ToxicEquivalency8Value !== null) {
                            builder = builder.ele('TRI:ToxicEquivalency8Value');
                            {
                              const TRIToxicEquivalency8Value =
                                TRIToxicEquivalencyIdentification.ToxicEquivalency8Value;
                              builder.txt(formRDecimalToString(TRIToxicEquivalency8Value));
                            }
                            builder = builder.up();
                          }
                          if (TRIToxicEquivalencyIdentification.ToxicEquivalency9Value !== null) {
                            builder = builder.ele('TRI:ToxicEquivalency9Value');
                            {
                              const TRIToxicEquivalency9Value =
                                TRIToxicEquivalencyIdentification.ToxicEquivalency9Value;
                              builder.txt(formRDecimalToString(TRIToxicEquivalency9Value));
                            }
                            builder = builder.up();
                          }
                          if (TRIToxicEquivalencyIdentification.ToxicEquivalency10Value !== null) {
                            builder = builder.ele('TRI:ToxicEquivalency10Value');
                            {
                              const TRIToxicEquivalency10Value =
                                TRIToxicEquivalencyIdentification.ToxicEquivalency10Value;
                              builder.txt(formRDecimalToString(TRIToxicEquivalency10Value));
                            }
                            builder = builder.up();
                          }
                          if (TRIToxicEquivalencyIdentification.ToxicEquivalency11Value !== null) {
                            builder = builder.ele('TRI:ToxicEquivalency11Value');
                            {
                              const TRIToxicEquivalency11Value =
                                TRIToxicEquivalencyIdentification.ToxicEquivalency11Value;
                              builder.txt(formRDecimalToString(TRIToxicEquivalency11Value));
                            }
                            builder = builder.up();
                          }
                          if (TRIToxicEquivalencyIdentification.ToxicEquivalency12Value !== null) {
                            builder = builder.ele('TRI:ToxicEquivalency12Value');
                            {
                              const TRIToxicEquivalency12Value =
                                TRIToxicEquivalencyIdentification.ToxicEquivalency12Value;
                              builder.txt(formRDecimalToString(TRIToxicEquivalency12Value));
                            }
                            builder = builder.up();
                          }
                          if (TRIToxicEquivalencyIdentification.ToxicEquivalency13Value !== null) {
                            builder = builder.ele('TRI:ToxicEquivalency13Value');
                            {
                              const TRIToxicEquivalency13Value =
                                TRIToxicEquivalencyIdentification.ToxicEquivalency13Value;
                              builder.txt(formRDecimalToString(TRIToxicEquivalency13Value));
                            }
                            builder = builder.up();
                          }
                          if (TRIToxicEquivalencyIdentification.ToxicEquivalency14Value !== null) {
                            builder = builder.ele('TRI:ToxicEquivalency14Value');
                            {
                              const TRIToxicEquivalency14Value =
                                TRIToxicEquivalencyIdentification.ToxicEquivalency14Value;
                              builder.txt(formRDecimalToString(TRIToxicEquivalency14Value));
                            }
                            builder = builder.up();
                          }
                          if (TRIToxicEquivalencyIdentification.ToxicEquivalency15Value !== null) {
                            builder = builder.ele('TRI:ToxicEquivalency15Value');
                            {
                              const TRIToxicEquivalency15Value =
                                TRIToxicEquivalencyIdentification.ToxicEquivalency15Value;
                              builder.txt(formRDecimalToString(TRIToxicEquivalency15Value));
                            }
                            builder = builder.up();
                          }
                          if (TRIToxicEquivalencyIdentification.ToxicEquivalency16Value !== null) {
                            builder = builder.ele('TRI:ToxicEquivalency16Value');
                            {
                              const TRIToxicEquivalency16Value =
                                TRIToxicEquivalencyIdentification.ToxicEquivalency16Value;
                              builder.txt(formRDecimalToString(TRIToxicEquivalency16Value));
                            }
                            builder = builder.up();
                          }
                          if (TRIToxicEquivalencyIdentification.ToxicEquivalency17Value !== null) {
                            builder = builder.ele('TRI:ToxicEquivalency17Value');
                            {
                              const TRIToxicEquivalency17Value =
                                TRIToxicEquivalencyIdentification.ToxicEquivalency17Value;
                              builder.txt(formRDecimalToString(TRIToxicEquivalency17Value));
                            }
                            builder = builder.up();
                          }
                          if (TRIToxicEquivalencyIdentification.ToxicEquivalencyNAIndicator !== null) {
                            builder = builder.ele('TRI:ToxicEquivalencyNAIndicator');
                            {
                              const TRIToxicEquivalencyNAIndicator =
                                TRIToxicEquivalencyIdentification.ToxicEquivalencyNAIndicator;
                              builder.txt(String(TRIToxicEquivalencyNAIndicator satisfies boolean));
                            }
                            builder = builder.up();
                          }
                        }
                        builder = builder.up();
                      }
                    }
                  }
                  if (ReleaseQuantity.choice === 'NA') {
                    {
                      const chosen53 = ReleaseQuantity.value;
                      if (chosen53 !== null) {
                        builder = builder.ele('TRI:OneTimeReleaseNAIndicator');
                        {
                          const TRIOneTimeReleaseNAIndicator = chosen53;
                          builder.txt(String(TRIOneTimeReleaseNAIndicator satisfies boolean));
                        }
                        builder = builder.up();
                      }
                    }
                  }
                }
                {
                  const ProductionRatio = TRISourceReductionQuantity.ProductionRatio;
                  if (ProductionRatio.choice === 'ProductionRatioMeasure') {
                    {
                      const chosen54 = ProductionRatio.value;
                      if (chosen54 !== null) {
                        builder = builder.ele('TRI:ProductionRatioMeasure');
                        {
                          const TRIProductionRatioMeasure = chosen54;
                          builder.txt(formRDecimalToString(TRIProductionRatioMeasure));
                        }
                        builder = builder.up();
                      }
                    }
                  }
                  if (ProductionRatio.choice === 'NA') {
                    {
                      const chosen55 = ProductionRatio.value;
                      if (chosen55 !== null) {
                        builder = builder.ele('TRI:ProductionRatioNAIndicator');
                        {
                          const TRIProductionRatioNAIndicator = chosen55;
                          builder.txt(String(TRIProductionRatioNAIndicator satisfies boolean));
                        }
                        builder = builder.up();
                      }
                    }
                  }
                }
                if (TRISourceReductionQuantity.ProductionRatioType !== null) {
                  builder = builder.ele('TRI:ProductionRatioType');
                  {
                    const TRIProductionRatioType = TRISourceReductionQuantity.ProductionRatioType;
                    builder.txt(TRIProductionRatioType);
                  }
                  builder = builder.up();
                }
              }
              builder = builder.up();
            }
            {
              const SourceReduction = TRIReport.SourceReduction;
              if (SourceReduction !== null) {
                if (SourceReduction.choice === 'NA') {
                  {
                    const chosen56 = SourceReduction.value;
                    if (chosen56 !== null) {
                      builder = builder.ele('TRI:SourceReductionNAIndicator');
                      {
                        const TRISourceReductionNAIndicator = chosen56;
                        builder.txt(String(TRISourceReductionNAIndicator satisfies boolean));
                      }
                      builder = builder.up();
                    }
                  }
                }
                if (SourceReduction.choice === 'SourceReductionActivity') {
                  {
                    const chosen57 = SourceReduction.value;
                    for (const chosen57Item of chosen57) {
                      builder = builder.ele('TRI:SourceReductionActivity');
                      {
                        const TRISourceReductionActivity = chosen57Item;
                        if (TRISourceReductionActivity.SourceReductionSequenceNumber !== null) {
                          builder = builder.ele('TRI:SourceReductionSequenceNumber');
                          {
                            const TRISourceReductionSequenceNumber =
                              TRISourceReductionActivity.SourceReductionSequenceNumber;
                            builder.txt(String(TRISourceReductionSequenceNumber satisfies number));
                          }
                          builder = builder.up();
                        }
                        if (TRISourceReductionActivity.SourceReductionActivityCode !== null) {
                          builder = builder.ele('TRI:SourceReductionActivityCode');
                          {
                            const TRISourceReductionActivityCode =
                              TRISourceReductionActivity.SourceReductionActivityCode;
                            builder.txt(TRISourceReductionActivityCode);
                          }
                          builder = builder.up();
                        }
                        for (const TRISourceReductionActivityItem of TRISourceReductionActivity.SourceReductionMethodCode) {
                          builder = builder.ele('TRI:SourceReductionMethodCode');
                          {
                            const TRISourceReductionMethodCode = TRISourceReductionActivityItem;
                            builder.txt(TRISourceReductionMethodCode);
                          }
                          builder = builder.up();
                        }
                        if (TRISourceReductionActivity.SourceReductionEfficiencyCode !== null) {
                          builder = builder.ele('TRI:SourceReductionEfficiencyCode');
                          {
                            const TRISourceReductionEfficiencyCode =
                              TRISourceReductionActivity.SourceReductionEfficiencyCode;
                            builder.txt(TRISourceReductionEfficiencyCode);
                          }
                          builder = builder.up();
                        }
                      }
                      builder = builder.up();
                    }
                  }
                }
              }
            }
            if (TRIReport.SubmissionAdditionalDataIndicator !== null) {
              builder = builder.ele('TRI:SubmissionAdditionalDataIndicator');
              {
                const TRISubmissionAdditionalDataIndicator = TRIReport.SubmissionAdditionalDataIndicator;
                builder.txt(String(TRISubmissionAdditionalDataIndicator satisfies boolean));
              }
              builder = builder.up();
            }
            if (TRIReport.OptionalInformationText !== null) {
              builder = builder.ele('TRI:OptionalInformationText');
              {
                const TRIOptionalInformationText = TRIReport.OptionalInformationText;
                builder.txt(TRIOptionalInformationText);
              }
              builder = builder.up();
            }
            if (TRIReport.OptionalInformationCategory !== null) {
              builder = builder.ele('TRI:OptionalInformationCategory');
              {
                const TRIOptionalInformationCategory = TRIReport.OptionalInformationCategory;
                builder.txt(TRIOptionalInformationCategory);
              }
              builder = builder.up();
            }
            if (TRIReport.MiscellaneousInformationText !== null) {
              builder = builder.ele('TRI:MiscellaneousInformationText');
              {
                const TRIMiscellaneousInformationText = TRIReport.MiscellaneousInformationText;
                builder.txt(TRIMiscellaneousInformationText);
              }
              builder = builder.up();
            }
            if (TRIReport.MiscellaneousInformationCategory !== null) {
              builder = builder.ele('TRI:MiscellaneousInformationCategory');
              {
                const TRIMiscellaneousInformationCategory = TRIReport.MiscellaneousInformationCategory;
                builder.txt(TRIMiscellaneousInformationCategory);
              }
              builder = builder.up();
            }
            for (const TRIReportItem of TRIReport.TRIComment) {
              builder = builder.ele('TRI:TRIComment');
              {
                const TRITRIComment = TRIReportItem;
                if (TRITRIComment.TRICommentSequence !== null) {
                  builder = builder.ele('TRI:TRICommentSequence');
                  {
                    const TRITRICommentSequence = TRITRIComment.TRICommentSequence;
                    builder.txt(String(TRITRICommentSequence satisfies number));
                  }
                  builder = builder.up();
                }
                if (TRITRIComment.TRICommentSection !== null) {
                  builder = builder.ele('TRI:TRICommentSection');
                  {
                    const TRITRICommentSection = TRITRIComment.TRICommentSection;
                    builder.txt(TRITRICommentSection);
                  }
                  builder = builder.up();
                }
                if (TRITRIComment.TRICommentType !== null) {
                  builder = builder.ele('TRI:TRICommentType');
                  {
                    const TRITRICommentType = TRITRIComment.TRICommentType;
                    builder.txt(TRITRICommentType);
                  }
                  builder = builder.up();
                }
                if (TRITRIComment.TRICommentTypeDescription !== null) {
                  builder = builder.ele('TRI:TRICommentTypeDescription');
                  {
                    const TRITRICommentTypeDescription = TRITRIComment.TRICommentTypeDescription;
                    builder.txt(TRITRICommentTypeDescription);
                  }
                  builder = builder.up();
                }
                if (TRITRIComment.TRICommentText !== null) {
                  builder = builder.ele('TRI:TRICommentText');
                  {
                    const TRITRICommentText = TRITRIComment.TRICommentText;
                    builder.txt(TRITRICommentText);
                  }
                  builder = builder.up();
                }
                if (TRITRIComment.TRICommentP2Classification !== null) {
                  builder = builder.ele('TRI:TRICommentP2Classification');
                  {
                    const TRITRICommentP2Classification = TRITRIComment.TRICommentP2Classification;
                    builder.txt(TRITRICommentP2Classification);
                  }
                  builder = builder.up();
                }
              }
              builder = builder.up();
            }
            for (const TRIReportItem of TRIReport.NonProductionWasteTotal) {
              builder = builder.ele('TRI:NonProductionWasteTotal');
              {
                const TRINonProductionWasteTotal = TRIReportItem;
                builder = builder.ele('TRI:NonProductionWasteTotalCode');
                {
                  const TRINonProductionWasteTotalCode = TRINonProductionWasteTotal.NonProductionWasteTotalCode;
                  builder.txt(TRINonProductionWasteTotalCode);
                }
                builder = builder.up();
                builder = builder.ele('TRI:NonProductionWasteTotalQuantity');
                {
                  const TRINonProductionWasteTotalQuantity = TRINonProductionWasteTotal.NonProductionWasteTotalQuantity;
                  builder.txt(formRDecimalToString(TRINonProductionWasteTotalQuantity));
                }
                builder = builder.up();
              }
              builder = builder.up();
            }
          }
          builder = builder.up();
        }
      }
      builder = builder.up();
    }
  }
  builder = builder.up();
  const doc = builder.doc();
  return doc.toString({ prettyPrint: true });
}
